import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faBuilding,
  faChevronDown,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DarkModeContext } from "../../context/DarkModeContext";
import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import logo from "../../assets/img/logo.png";
import { getToken } from "../../utils/auth";
import { descargarOrdenPagoDesdePayload } from "../../helpers/legacyOrdenPagoPdf";
import { crmPostventaApi } from "./crmPostventaApi";
import ClienteSelectorModal from "./ClienteSelectorModal";
import ZocoCheckPanel from "./ZocoCheckPanel";
import ZocoCheckCampaignsPanel from "./ZocoCheckCampaignsPanel";
import "./crm-postventa.css";

const STORAGE_KEY = "crm_postventa_cliente_id";

const makeToday = () => new Date().toISOString().slice(0, 10);
const decodeTokenPayload = (token) => {
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    return JSON.parse(window.atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
};
const makeMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const makeCurrentMonth = () => makeToday().slice(0, 7);
const makeNowDateTimeLocal = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
};
const toDateTimeLocalInput = (value) => {
  if (!value || value === "-") return "";
  const normalized = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(normalized)) {
    const [datePart, timePart] = normalized.split(" ");
    const [day, month, year] = datePart.split("/");
    return `${year}-${month}-${day}T${timePart}`;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};
const buildMonthDateRange = (monthValue) => {
  const fallback = makeCurrentMonth();
  const [yearText, monthText] = String(monthValue || fallback).split("-");
  const year = Number(yearText);
  const month = Number(monthText);

  if (!year || !month) {
    return {
      mes: fallback,
      desde: `${fallback}-01`,
      hasta: makeToday(),
    };
  }

  const normalizedMonth = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
  const lastDay = new Date(year, month, 0).getDate();

  return {
    mes: normalizedMonth,
    desde: `${normalizedMonth}-01`,
    hasta: `${normalizedMonth}-${String(lastDay).padStart(2, "0")}`,
  };
};
const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const normalizeCuitValue = (value) => normalizeDigits(value).slice(0, 11);
const normalizeEditablePhoneValue = (value) => normalizeDigits(value).slice(0, 20);
const isLegacyPlaceholderValue = (value) => String(value ?? "").trim() === "-";
const sanitizeLegacyTextValue = (value) => {
  const normalized = String(value ?? "").trim();
  return isLegacyPlaceholderValue(normalized) ? "" : normalized;
};
const sanitizeLegacyNumericValue = (value) => normalizeDigits(String(value ?? ""));
const normalizeTerminalStatusText = (value) => String(value || "").trim();
const isPhoneFieldLabel = (label = "") => normalizeText(label).startsWith("telefono");
const formatGestionRuleLabel = (regla = {}) => String(regla?.aliasVista || regla?.nombreAccion || "Sin acción").trim();
const formatMissingFieldsHtmlList = (fields = []) =>
  `<div style="text-align:left;">Faltan completar:<br/><ul style="margin:8px 0 0 18px;padding:0;">${fields
    .map((field) => `<li>${field}</li>`)
    .join("")}</ul></div>`;
const resolveTerminalStatusLabel = (terminal = {}) => {
  const estadoAlta = normalizeTerminalStatusText(terminal.estadoAlta || terminal.estado);
  const estadoOperativo = normalizeTerminalStatusText(terminal.estadoOperativo);

  if (/extraviad/i.test(estadoAlta)) return "Extraviada";
  if (/baja/i.test(estadoAlta)) return "Baja";
  if (estadoOperativo && !/^ok$/i.test(estadoOperativo)) {
    return estadoAlta && /^alta$/i.test(estadoAlta) ? `ALTA / ${estadoOperativo}` : estadoOperativo;
  }
  if (/alta/i.test(estadoAlta) && /^ok$/i.test(estadoOperativo)) return "ALTA / OK";
  if (estadoAlta) return estadoAlta;
  if (estadoOperativo) return estadoOperativo;
  return "Sin datos";
};
const buildAltaMissingFields = (altaWizardData = {}, formularioPayload = null) => {
  const missing = [];
  const addMissing = (label, condition) => {
    if (condition) {
      missing.push(label);
    }
  };

  const alta = altaWizardData.alta || {};
  const legajo = altaWizardData.legajo || {};
  const usuario = altaWizardData.usuario || {};

  addMissing("N° comercio solicitado", !sanitizeLegacyTextValue(alta.numeroComercioSolicitado));
  addMissing("Rubro", !sanitizeLegacyTextValue(alta.rubro));
  addMissing("Código actividad", !sanitizeLegacyNumericValue(alta.codActividad));
  addMissing("Actividad", !sanitizeLegacyTextValue(alta.actividad));
  addMissing("Código actividad rentas", !sanitizeLegacyNumericValue(alta.codActividadRentas));
  addMissing("Actividad rentas", !sanitizeLegacyTextValue(alta.actividadRentas));
  addMissing("Banco", !sanitizeLegacyTextValue(alta.banco));
  addMissing("Tipo de cuenta", !sanitizeLegacyTextValue(alta.tipoCuenta));
  addMissing("Celular / WhatsApp", !normalizeEditablePhoneValue(usuario.celular || legajo.celularWhatsapp));
  addMissing("Puesto / cargo / referencia 1", !sanitizeLegacyTextValue(alta.referenciaTelefono));

  if (String(alta.tipoAlta || "") === "AgregaTerminal" || String(alta.altas || "") === "AGREGA") {
    addMissing("Terminal / comercio base para AGREGA", !altaWizardData.agregaBaseClienteId);
  }

  if (formularioPayload) {
    addMissing("Terminal base", !formularioPayload.TerminalN);
    addMissing("Código postal comercio", !formularioPayload.CodigoPostalComercio);
  }

  return missing.filter((item, index, array) => array.indexOf(item) === index);
};
const getFirstValue = (item, keys = []) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
};
const normalizeLegacyIdentifier = (value) => {
  if (value === undefined || value === null) return "";

  const normalized = String(value).trim();
  if (!normalized) return "";

  return normalized.replace(/\.0+$/g, "");
};
const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
const formatLegacyDisplayDate = (value) => {
  if (!value) return "-";
  if (/^\d{2}-\d{2}-\d{4}$/.test(String(value))) {
    return String(value);
  }

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) {
    return String(value);
  }

  return `${day}-${month}-${year}`;
};
const formatLegacyMonthLabel = (value) => {
  if (!value) return "-";
  const [year, month] = String(value).split("-");
  if (!year || !month) {
    return String(value);
  }

  return `${month}/${String(year).slice(-2)}`;
};
const parseFlexibleAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  normalized = normalized.replace(/\$/g, "").replace(/\s/g, "").replace(/[^0-9,.-]/g, "");
  if (!normalized) {
    return null;
  }

  const dotCount = (normalized.match(/\./g) || []).length;
  const commaCount = (normalized.match(/,/g) || []).length;
  const lastComma = normalized.lastIndexOf(",");
  const lastDot = normalized.lastIndexOf(".");
  const hasBoth = lastComma >= 0 && lastDot >= 0;
  const lastSepIndex = Math.max(lastComma, lastDot);

  if (lastSepIndex >= 0) {
    const digitsAfter = normalized.length - lastSepIndex - 1;
    const hasSingleSep = dotCount + commaCount === 1;
    const shouldTreatAsThousandsOnly = hasSingleSep && digitsAfter === 3 && !hasBoth;

    if (shouldTreatAsThousandsOnly) {
      normalized = normalized.replace(/[.,]/g, "");
    } else {
      const integerPart = normalized.slice(0, lastSepIndex).replace(/[.,]/g, "");
      const decimalPart = normalized.slice(lastSepIndex + 1).replace(/[.,]/g, "");
      normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    }
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};
const parseLegacyDate = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const date = new Date(`${raw.slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const [day, month, year] = raw.split(/[/-]/);
  if (!day || !month || !year) {
    return null;
  }

  const iso = `${year.padStart(4, "20")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatCurrencyCell = (value) => {
  const parsed = parseFlexibleAmount(value);
  if (parsed === null) {
    return "-";
  }

  return parsed.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const normalizeCuponLegacyRow = (item = {}) => {
  const fechaOperacion = getFirstValue(item, ["fechaOperacion", "FechaOperacion", "fecha operacion", "Fecha Operacion"]);
  const fechaDePago = getFirstValue(item, ["fechaDePago", "FechaDePago", "fecha de pago", "Fecha de Pago"]);
  const nroDeCupon = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeCupon", "NroDeCupon", "Nro# de Cupon"]));
  const nroDeComercio = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeComercio", "NroDeComercio", "nroDeTerminal", "NroDeTerminal", "Nro# de Comercio"]));
  const nroDeTarjeta = normalizeLegacyIdentifier(getFirstValue(item, ["nroDeTarjeta", "NroDeTarjeta", "Nro# de Tarjeta"]));
  const brutoRaw = getFirstValue(item, ["totalBruto", "TotalBruto", "bruto", "Bruto", "importe", "Importe", "Total Bruto"]);
  const totalBruto = parseFlexibleAmount(brutoRaw);
  const cuotas = normalizeLegacyIdentifier(getFirstValue(item, ["cuotas", "Cuotas"]));
  const tarjeta = getFirstValue(item, ["tarjeta", "Tarjeta"]);
  const provincia = getFirstValue(item, ["provincia", "Provincia"]);
  const nombreComercio = getFirstValue(item, ["nombreComercio", "NombreComercio", "Nombre Comercio"]);
  const cuit = normalizeDigits(getFirstValue(item, ["cuit", "Cuit", "CUIT"]));

  return {
    fechaOperacion: formatLegacyDisplayDate(fechaOperacion),
    fechaDePago: formatLegacyDisplayDate(fechaDePago),
    fechaOperacionDate: parseLegacyDate(fechaOperacion),
    fechaDePagoDate: parseLegacyDate(fechaDePago),
    nroDeCupon: nroDeCupon || "-",
    nroDeComercio: nroDeComercio || "-",
    nroDeTarjeta: nroDeTarjeta || "-",
    totalBruto,
    brutoRaw,
    cuotas: cuotas || "-",
    tarjeta: tarjeta || "-",
    provincia: provincia || "-",
    nombreComercio: nombreComercio || "-",
    cuit: cuit || "-",
  };
};
const buildCuponDedupeKey = (item = {}) =>
  [
    normalizeLegacyIdentifier(item.nroDeCupon),
    normalizeLegacyIdentifier(item.nroDeComercio),
    formatLegacyDisplayDate(item.fechaOperacion),
    item.totalBruto === null ? "" : Number(item.totalBruto).toFixed(4),
    normalizeText(item.tarjeta),
  ].join("|");
const buildCuponSummary = (rows = []) => {
  const fechasOperacion = rows.map((item) => item.fechaOperacionDate).filter(Boolean).sort((a, b) => a - b);
  const fechasPago = rows.map((item) => item.fechaDePagoDate).filter(Boolean).sort((a, b) => a - b);
  const totalBruto = rows.reduce((acc, item) => acc + (Number.isFinite(item.totalBruto) ? item.totalBruto : 0), 0);

  const formatDate = (value) => (value ? value.toLocaleDateString("es-AR") : "-");

  return {
    cantidad: rows.length,
    totalBruto,
    fechaOperacionMin: formatDate(fechasOperacion[0]),
    fechaOperacionMax: formatDate(fechasOperacion[fechasOperacion.length - 1]),
    fechaPagoMin: formatDate(fechasPago[0]),
    fechaPagoMax: formatDate(fechasPago[fechasPago.length - 1]),
  };
};
const sanitizeCuponesMessage = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }

  return normalized.toLowerCase().includes("lista de sas")
    ? "No hay cupones para este cliente/terminal."
    : normalized;
};
const formatCurrencyDetailed = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const formatPhoneLine = (phone, reference) => {
  const normalizedPhone = String(phone || "").trim();
  const normalizedReference = String(reference || "").trim();
  if (!normalizedPhone && !normalizedReference) {
    return "-";
  }

  return [normalizedPhone, normalizedReference ? `(${normalizedReference})` : ""].filter(Boolean).join(" ");
};
const resolvePreferredWhatsappPhone = (item = {}) =>
  normalizeWhatsappPhone(item?.telefono) || normalizeWhatsappPhone(item?.telefonoAlternativo) || "";
const buildFacturacionPhoneLines = (item = {}) => {
  const primary = formatPhoneLine(item?.telefono, item?.referenciaTelefono);
  const secondary = formatPhoneLine(item?.telefonoAlternativo, item?.referenciaTelefonoAlternativo);
  return [primary, secondary].filter((value, index, array) => value !== "-" && array.indexOf(value) === index);
};
const buildFichaValueMapByLabel = (ficha = {}) =>
  [ficha?.datosGenerales, ficha?.contacto, ficha?.fiscal, ficha?.operativo]
    .flat()
    .filter(Boolean)
    .reduce((acc, item) => {
      const key = normalizeText(item?.label);
      if (key && !(key in acc)) {
        acc[key] = item?.value ?? "";
      }
      return acc;
    }, {});

const ALTA_LEGAJO_DOC_TYPES = [
  "AFIP",
  "FACTURANTE",
  "IIBB",
  "CONTRATO",
  "CUIT",
  "CREDENCIAL",
  "CONSTANCIA_CBU",
  "OTRO",
];

const LEGACY_ALTA_TIPO_CUENTA_OPTIONS = [
  { value: "Caja de Ahorros", label: "Caja de Ahorros" },
  { value: "Cuenta Corriente", label: "Cuenta Corriente" },
  { value: "Billetera Virtual", label: "Billetera Virtual" },
];

const LEGACY_DEFAULT_ALTAS_BY_TIPO = {
  NuevoAliado: "ALTA NUEVA",
  AgregaTerminal: "AGREGA",
};

const findProvinciaAlicuota = (provincias = [], provinciaValue = "") =>
  provincias.find((item) => item.value === String(provinciaValue || ""))?.alicuota || "";

const mapCondicionAfipToEstado = (condicionAfip = "") =>
  String(condicionAfip || "").toLowerCase() === "monotributista"
    ? "MONOTRIBUTISTA"
    : "RESPONSABLE INSCRIPTO";

const mapEstadoAfipToCondicion = (estadoAfip = "") =>
  String(estadoAfip || "").toUpperCase() === "MONOTRIBUTISTA"
    ? "Monotributista"
    : "Responsable Inscripto";

const mapExentoToLegacyFlags = (exento = "") => {
  const normalized = String(exento || "").toUpperCase();
  return {
    afipExentoGanancia: normalized === "EXENTO GANANCIA",
    afipExentoIva: normalized === "EXENTO IVA",
  };
};

const mapEstadoRentasToLegacyFlags = (estadoRentas = "") => {
  const normalized = String(estadoRentas || "").toUpperCase();
  return {
    rentasIncripto: normalized === "INSCRIPTO",
    rentasConvenioMultilateral: normalized === "CM",
    rentasExento: normalized === "EXENTO",
  };
};

const emptyAltaWizardData = () => ({
  usuarioId: null,
  legajoId: null,
  formularioAltaId: null,
  agregaBaseClienteId: null,
  usuario: {
    cuit: "",
    nombre: "",
    email: "",
    tipoUsuario: 0,
  },
  legajo: {
    nroLegajo: "",
    terminal: "",
    nombreDeFantasia: "",
    provincia: "",
    tipoPersona: "fisica",
    razonSocial: "",
    calle: "",
    numero: "",
    pisoDepto: "",
    localidad: "",
    codigoPostal: "",
    emailContacto: "",
    celularWhatsapp: "",
    asesor: "",
  },
  alta: {
    condicionAFIP: "Monotributista",
    actividadLaboralProfesional: "",
    pais: "Argentina",
    nacionalidad: "Argentina",
    fechaNacimiento: "",
    celular: "",
    telFijo: "",
    referenciaTelefono: "",
    telefonoAlternativo: "",
    referenciaTelefonoAlternativo: "",
    correo: "",
    apoderado: "",
    localidadTitular: "",
    codigoPostalTitular: "",
    domicilioComercio: "",
    localidadComercio: "",
    codigoPostalComercio: "",
    afipExentoGanancia: false,
    afipExentoIva: false,
    rentasIncripto: false,
    rentasConvenioMultilateral: false,
    rentasExento: false,
    banco: "",
    tipoCuenta: "Caja de Ahorros",
    cbucvu: "",
    alias: "",
    cuitbancario: "",
    titularRazonSocial: "",
    numeroComericoActual: "",
    numeroComercioSolicitado: "",
    tipoAlta: "NuevoAliado",
    estado: "ALTA",
    linkDePago: "NO",
    provincia: "",
    provincia3: "",
    nombreDelAsesor: "",
    estadoAfip: "MONOTRIBUTISTA",
    exento: "NO",
    estadoRentas: "",
    altas: "ALTA NUEVA",
    baja: "",
    alicuota: "",
    rubro: "",
    rubroSecundario: "",
    nroLocal: "",
    codActividad: "",
    actividad: "",
    actividad2: "",
    codActividadRentas: "",
    actividadRentas: "",
    observaciones: "",
  },
});

const emptyBajaDraft = () => ({
  terminal: "",
  motivo: "BAJA",
  observacion: "",
  fechaBaja: makeToday().replace(/-/g, "/"),
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getFileExtension = (file) => {
  const name = String(file?.name || "");
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === name.length - 1) {
    return "";
  }

  return name.slice(dotIndex + 1).trim().toLowerCase();
};

const boolToLegacyFlag = (value) => (value ? "true" : "");

const nextLegajoSuggestion = (legajos = []) => {
  const maxValue = legajos.reduce((acc, item) => {
    const raw = Number(item?.nroLegajo ?? item?.NroLegajo ?? item?.nroLegajo1 ?? 0);
    return Number.isFinite(raw) && raw > acc ? raw : acc;
  }, 0);

  return String(maxValue + 1 || 1);
};

const buildAltaWizardFormPayload = (wizardData, token) => {
  const { usuario, legajo, alta } = wizardData;
  const razonSocial = sanitizeLegacyTextValue(legajo.razonSocial || usuario.nombre);
  const domicilioTitular = sanitizeLegacyTextValue(alta.localidadTitular || legajo.localidad);
  const domicilioComercio = sanitizeLegacyTextValue(alta.localidadComercio || legajo.localidad);
  const cuitTitular = normalizeCuitValue(usuario.cuit);
  const cuitBancario = normalizeCuitValue(alta.cuitbancario || cuitTitular);
  const provinciaAlta = sanitizeLegacyTextValue(alta.provincia || legajo.provincia);
  const provinciaFiscal = sanitizeLegacyTextValue(alta.provincia3 || provinciaAlta);
  const estadoAfip = alta.estadoAfip || mapCondicionAfipToEstado(alta.condicionAFIP);
  const exentoFlags = mapExentoToLegacyFlags(alta.exento);
  const rentasFlags = mapEstadoRentasToLegacyFlags(alta.estadoRentas);
  const terminal = sanitizeLegacyNumericValue(legajo.terminal);
  const codigoPostalTitular = sanitizeLegacyNumericValue(alta.codigoPostalTitular || legajo.codigoPostal);
  const celular = sanitizeLegacyNumericValue(alta.celular || legajo.celularWhatsapp);
  const telFijo = sanitizeLegacyNumericValue(alta.telefonoAlternativo || alta.telFijo || legajo.celularWhatsapp);
  const codigoPostalComercio = sanitizeLegacyNumericValue(alta.codigoPostalComercio || legajo.codigoPostal);

  return {
    token,
    condicionAFIP: mapEstadoAfipToCondicion(estadoAfip),
    TipoPersona: sanitizeLegacyTextValue(legajo.tipoPersona) || "fisica",
    TerminalN: terminal,
    ApellidoYnombre: sanitizeLegacyTextValue(usuario.nombre),
    Cuittitular: cuitTitular,
    ActividadLaboralProfesional: sanitizeLegacyTextValue(alta.actividadLaboralProfesional),
    Calle: sanitizeLegacyTextValue(legajo.calle),
    NumeroCalle: sanitizeLegacyTextValue(legajo.numero),
    LocalidadTitular: domicilioTitular,
    Provincia: provinciaAlta,
    Pais: sanitizeLegacyTextValue(alta.pais) || "Argentina",
    CodigoPostalTitular: codigoPostalTitular,
    Nacionalidad: sanitizeLegacyTextValue(alta.nacionalidad) || "Argentina",
    FechaNacimiento: sanitizeLegacyTextValue(alta.fechaNacimiento),
    Celular: celular,
    TelFijo: telFijo,
    Correo: sanitizeLegacyTextValue(alta.correo || legajo.emailContacto || usuario.email),
    Apoderado: sanitizeLegacyTextValue(alta.referenciaTelefono || alta.apoderado),
    NombreFantasia: sanitizeLegacyTextValue(legajo.nombreDeFantasia),
    Domicilio: sanitizeLegacyTextValue(alta.domicilioComercio || [legajo.calle, legajo.numero].filter(Boolean).join(" ")),
    LocalidadComercio: domicilioComercio,
    CodigoPostalComercio: codigoPostalComercio,
    AfipExentoGanancia: boolToLegacyFlag(exentoFlags.afipExentoGanancia),
    AfipExentoIva: boolToLegacyFlag(exentoFlags.afipExentoIva),
    RentasIncripto: boolToLegacyFlag(rentasFlags.rentasIncripto),
    RentasConvenioMultilateral: boolToLegacyFlag(rentasFlags.rentasConvenioMultilateral),
    RentasExento: boolToLegacyFlag(rentasFlags.rentasExento),
    Banco: sanitizeLegacyTextValue(alta.banco),
    TipoCuenta: sanitizeLegacyTextValue(alta.tipoCuenta),
    Cbucvu: sanitizeLegacyNumericValue(alta.cbucvu),
    Alias: sanitizeLegacyTextValue(alta.alias),
    Cuitbancario: cuitBancario,
    TitularRazonSocial: sanitizeLegacyTextValue(alta.titularRazonSocial) || razonSocial,
    NumeroComericoActual: sanitizeLegacyTextValue(alta.numeroComericoActual),
    NumeroComercioSolicitado: sanitizeLegacyTextValue(alta.numeroComercioSolicitado),
    TipoAlta: sanitizeLegacyTextValue(alta.tipoAlta),
    Estado: sanitizeLegacyTextValue(alta.estado) || "ALTA",
    LinkDePago: sanitizeLegacyTextValue(alta.linkDePago) || "NO",
    NombreDelAsesor: sanitizeLegacyTextValue(alta.nombreDelAsesor || legajo.asesor),
    EstadoAfip: estadoAfip,
    Exento: sanitizeLegacyTextValue(alta.exento) || "NO",
    EstadoRentas: sanitizeLegacyTextValue(alta.estadoRentas),
    Provincia3: provinciaFiscal,
    Altas: sanitizeLegacyTextValue(alta.altas) || LEGACY_DEFAULT_ALTAS_BY_TIPO[alta.tipoAlta] || "ALTA NUEVA",
    Baja: sanitizeLegacyTextValue(alta.baja),
    NuevoPanel: "true",
  };
};

const currency = (value) =>
  typeof value === "number"
    ? value.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 })
    : "Sin datos";

const percent = (value) =>
  typeof value === "number"
    ? `${(value * 100).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
    : "Sin datos";

const GESTION_STATES = ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"];
const GESTION_EDIT_STATES = ["Pendiente", "Contactado", "Derivado", "Cerrado", "Sin respuesta", "Descartado", "Otro"];
const GESTION_CLOSE_REASONS = ["Resuelto", "Cliente contactado", "Derivado", "Sin respuesta", "Descartado", "Otro"];
const GESTION_SCOPE_LABELS = {
  ipc: "Gestion IPC",
  operativa: "Alerta operativa",
  proactiva: "Proactiva",
};
const GESTION_SCOPE_ACTION_LABELS = {
  ipc: "Nueva gestion",
  operativa: "Nueva gestion de alerta operativa",
  proactiva: "Nueva gestion proactiva",
};
const GESTION_SCOPE_EMPTY_MESSAGES = {
  ipc: "No hay gestiones IPC registradas para este cliente.",
  operativa: "No hay alertas operativas registradas para este cliente.",
  proactiva: "No hay gestiones proactivas registradas para este cliente.",
};
const GESTION_SCOPE_STATES = {
  ipc: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
  operativa: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
  proactiva: ["Pendiente", "Contactado", "Derivado", "Cerrado", "Descartado", "Otro"],
};
const CONTACT_CHANNEL_OPTIONS = ["WhatsApp", "Llamada", "Mail"];
const CALL_RESULT_OPTIONS = ["No atiende", "Ocupado", "Numero incorrecto", "Contesto", "Otro"];
const GESTION_IPC_TYPES = ["Modificacion", "Reclamo", "Consulta"];
const GESTION_IPC_REASON_OPTIONS = {
  Modificacion: ["Datos bancarios", "Datos fiscales", "Titularidad", "Domicilio", "Datos de terminal", "Otros"],
  Reclamo: ["Liquidacion", "Servicio tecnico", "Configuracion terminal", "Plazos de acreditacion", "Panel central"],
  Consulta: ["Tasas", "Plazos de acreditacion", "Operativa", "Simuladores", "Ordenes de pago", "Consultas especificas", "Rollos"],
};
const GESTION_SCOPE_RULE_CODES = {
  operativa: new Set([
    "MAN_ALERTA_OPERATIVA",
    "MAN_ZOCO_CHECK",
    "MAN_TERMINAL_EXTRAVIADA",
    "ONBOARDING_24H",
    "ONBOARDING_SIN_VENTAS_3D",
    "ONBOARDING_SIN_VENTAS_7D",
    "ONBOARDING_RIESGO_15D",
    "ONBOARDING_PRIMERA_ACREDITACION",
  ]),
  proactiva: new Set([
    "MAN_COORDINAR_RETIRO_TERMINAL",
    "MAN_RECUPERACION_COMERCIAL",
    "MAN_VISITA_COMERCIAL",
    "MAN_OTRO",
  ]),
};
const INACTIVOS_PROACTIVA_RULE_CODES = new Set([
  "MAN_RECUPERACION_COMERCIAL",
  "MAN_COORDINAR_RETIRO_TERMINAL",
  "MAN_VISITA_COMERCIAL",
  "MAN_OTRO",
]);
const SECTION_PATHS = {
  alertas: "/administracion/crm-postventa/alertas",
  ficha: "/administracion/crm-postventa/ficha",
  facturacion: "/administracion/crm-postventa/facturacion",
  "ordenes-pago": "/administracion/crm-postventa/ordenes-pago",
  cupones: "/administracion/crm-postventa/cupones",
  herramientas: "/administracion/crm-postventa/herramientas",
  "altas-bajas": "/administracion/crm-postventa/altas-bajas",
  "campanias-zoco-check": "/administracion/crm-postventa/campanias-zoco-check",
  gestion: "/administracion/crm-postventa/gestion",
  seguimiento: "/administracion/crm-postventa/seguimiento",
};

const toneForGestionState = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("resuelto") || normalized.includes("cerrado")) return "success";
  if (normalized.includes("escalado")) return "danger";
  if (normalized.includes("contactado") || normalized.includes("proceso")) return "warning";
  if (normalized.includes("pendiente")) return "default";
  return "default";
};

const toneForPriority = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("cr")) return "danger";
  if (normalized.includes("alta")) return "warning";
  if (normalized.includes("media")) return "default";
  return "success";
};

const toneForSegment = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "a") return "danger";
  if (normalized === "b") return "warning";
  if (normalized === "c") return "default";
  return "success";
};

const toneForPattern = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("diario")) return "danger";
  if (normalized.includes("fin_de_semana")) return "warning";
  if (normalized.includes("intermitente")) return "default";
  return "success";
};

const getTimelineTipoDetalle = (item) => {
  const raw = String(item?.tipo || "");
  const [, specific] = raw.split(":");
  return specific || item?.titulo || "-";
};

const PostventaBadge = ({ value, tone = "default" }) => (
  <span className={`crm-postventa-badge tone-${tone}`}>{value || "-"}</span>
);

const DataList = ({ items = [] }) => (
  <dl className="crm-postventa-ficha-list">
    {items.map((item) => (
      <div className="crm-postventa-ficha-item" key={`${item.label}-${item.value}`}>
        <dt>{item.label}</dt>
        <dd>
          {item.isBadge ? (
            <PostventaBadge value={item.value} tone={item.tone} />
          ) : (
            <>
              <span>{item.value || "-"}</span>
              {(() => {
                const whatsappPhone = isPhoneFieldLabel(item.label) ? normalizeWhatsappPhone(item.value) : "";
                return whatsappPhone ? (
                  <a
                    className="btn btn-outline-success btn-sm ms-2"
                    href={`https://wa.me/${whatsappPhone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp
                  </a>
                ) : null;
              })()}
            </>
          )}
        </dd>
      </div>
    ))}
  </dl>
);

const buildClienteGroupKey = (cliente = {}) => {
  const cuit = String(cliente.cuit || "").trim();
  const nombre = String(cliente.nombreDeFantasia || cliente.razonSocial || "")
    .trim()
    .toLowerCase();
  return `${cuit}::${nombre}`;
};

const normalizeTerminalOptions = (terminales = []) =>
  terminales
    .filter((item) => item?.clienteId)
    .sort((a, b) => {
      const left = Number(String(a.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      const right = Number(String(b.terminal || "").replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;
      return left - right || String(a.comercio || "").localeCompare(String(b.comercio || ""));
    })
    .map((item, index) => ({
      clienteId: item.clienteId,
      label: item.label || `Terminal ${index + 1}`,
      terminal: item.terminal || item.nDeTerminal || "",
      comercio: item.comercio || item.nDeComercio || "",
      estado: item.estado || "",
      estadoAlta: item.estadoAlta || item.estado || "",
      estadoOperativo: item.estadoOperativo || "",
      legajo: item.legajo || "",
      resumen:
        item.resumen ||
        [item.terminal ? `Terminal ${item.terminal}` : "", item.comercio ? `Comercio ${item.comercio}` : ""]
          .filter(Boolean)
          .join(" | "),
    }));

const mergeTerminalOptions = (...sources) => {
  const merged = new Map();

  sources
    .flat()
    .forEach((item) => {
      if (!item?.clienteId) {
        return;
      }

      const current = merged.get(item.clienteId) || {};
      merged.set(item.clienteId, {
        ...current,
        ...item,
        label: item.label || current.label,
        resumen: item.resumen || current.resumen,
      });
    });

  return normalizeTerminalOptions(Array.from(merged.values()));
};

const normalizeBajaLookupResults = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((item, index) => ({
      key: [
        item?.clienteId ?? item?.ClienteId ?? `sin-${index}`,
        normalizeDigits(item?.nDeTerminal || item?.NDeTerminal || item?.terminal),
        normalizeDigits(item?.nDeComercio || item?.NDeComercio || item?.comercio),
      ].join("|"),
      terminal: String(item?.nDeTerminal ?? item?.NDeTerminal ?? item?.terminal ?? "").trim(),
      comercio: String(item?.nDeComercio ?? item?.NDeComercio ?? item?.comercio ?? "").trim(),
      nombre: String(item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? item?.razonSocial ?? item?.RazonSocial ?? "").trim(),
      cuit: String(item?.cuit ?? item?.Cuit ?? "").trim(),
      estado: String(item?.estado ?? item?.Estado ?? "").trim(),
    }))
    .filter((item) => item.terminal || item.comercio || item.nombre)
    .sort((left, right) => {
      const byTerminal = String(left.terminal).localeCompare(String(right.terminal));
      if (byTerminal !== 0) {
        return byTerminal;
      }

      return String(left.nombre).localeCompare(String(right.nombre));
    });

const buildTerminalesFromFacturacion = (filas = []) =>
  normalizeTerminalOptions(
    Array.from(
      filas.reduce((acc, row) => {
        if (!row?.clienteId) {
          return acc;
        }

        if (!acc.has(row.clienteId)) {
          acc.set(row.clienteId, {
            clienteId: row.clienteId,
            terminal: row.terminal,
            comercio: row.comercio,
            estado: row.estadoAlerta,
            estadoOperativo: row.estadoAlerta,
            resumen: [row.terminal ? `Terminal ${row.terminal}` : "", row.comercio ? `Comercio ${row.comercio}` : ""]
              .filter(Boolean)
              .join(" | "),
          });
        }

        return acc;
      }, new Map()).values()
    )
  );

const agruparClientesPorGrupo = (clientes = []) =>
  Array.from(
    clientes.reduce((acc, cliente) => {
      const groupKey = buildClienteGroupKey(cliente);
      if (!groupKey || groupKey === "::") {
        return acc;
      }

      if (!acc.has(groupKey)) {
        acc.set(groupKey, []);
      }

      acc.get(groupKey).push(cliente);
      return acc;
    }, new Map()).entries()
  )
    .map(([groupKey, items]) => {
      const representative =
        items.find((item) => String(item.estado || "").toUpperCase() === "ALTA") ||
        items[0];
      const terminales = normalizeTerminalOptions(
        items.map((item) => ({
          clienteId: item.clienteId,
          terminal: item.nDeTerminal,
          comercio: item.nDeComercio,
          estado: item.estado,
          legajo: item.legajo,
        }))
      );
      const comerciosUnicos = new Set(items.map((item) => item.nDeComercio).filter(Boolean)).size;

      return {
        ...representative,
        groupKey,
        terminales,
        cantidadTerminales: terminales.length,
        cantidadComercios: comerciosUnicos,
        resumen: [
          representative.razonSocial || representative.nombreDeFantasia || "",
          representative.cuit ? `CUIT ${representative.cuit}` : "",
          terminales.length ? `${terminales.length} terminal(es)` : "",
          comerciosUnicos ? `${comerciosUnicos} comercio(s)` : "",
        ]
          .filter(Boolean)
          .join(" | "),
      };
    })
    .sort((a, b) => String(a.nombreDeFantasia || a.razonSocial).localeCompare(String(b.nombreDeFantasia || b.razonSocial)));

const buildFichaEditDraft = (campos = []) =>
  campos.reduce((acc, item) => {
    if (item?.key) {
      acc[item.key] = item.value || "";
    }
    return acc;
  }, {});

const FICHA_SECTION_ORDER = [
  "Datos generales",
  "Contacto",
  "Operativo",
  "Bancario",
  "Fiscal / rentas",
];

const FICHA_FIXED_OPTIONS = {
  estado: [
    { value: "ALTA", label: "Alta" },
    { value: "BAJA", label: "Baja" },
    { value: "SERVICIO.TECNICO", label: "Servicio técnico" },
    { value: "DISPONIBLE", label: "Disponible" },
    { value: "EXTRAVIADA", label: "Extraviada" },
  ],
  siNo: [
    { value: "SI", label: "SI" },
    { value: "NO", label: "NO" },
  ],
  tipoCuenta: [
    { value: "CAJA AHORRO", label: "CAJA AHORRO" },
    { value: "CUENTA CORRIENTE", label: "CUENTA CORRIENTE" },
    { value: "BILLETERA VIRTUAL", label: "BILLETERA VIRTUAL" },
  ],
  estadoAfip: [
    { value: "EXENTO", label: "EXENTO" },
    { value: "INHABILITADO", label: "INHABILITADO" },
    { value: "MONOTRIBUTISTA", label: "MONOTRIBUTISTA" },
    { value: "RESPONSABLE INSCRIPTO", label: "RESPONSABLE INSCRIPTO" },
  ],
  exento: [
    { value: "NO", label: "NO" },
    { value: "EXENTO GANANCIA", label: "EXENTO GANANCIA" },
    { value: "EXENTO IVA", label: "EXENTO IVA" },
  ],
  estadoRentas: [
    { value: "ALTA EN PROCESO", label: "ALTA EN PROCESO" },
    { value: "CM", label: "CM" },
    { value: "EXENTO", label: "EXENTO" },
    { value: "INSCRIPTO", label: "INSCRIPTO" },
    { value: "NO INSCRIPTO", label: "NO INSCRIPTO" },
  ],
  altas: [
    { value: "AGREGA", label: "AGREGA" },
    { value: "AGREGA CT", label: "AGREGA CT" },
    { value: "ALTA CCTT", label: "ALTA CCTT" },
    { value: "ALTA NUEVA", label: "ALTA NUEVA" },
    { value: "ALTA SSTT", label: "ALTA SSTT" },
  ],
  bajas: [
    { value: "BAJA", label: "BAJA" },
    { value: "BAJA POR FACTURACION", label: "BAJA POR FACTURACION" },
    { value: "BAJA POR POSNET", label: "BAJA POR POSNET" },
    { value: "BAJA SSTT", label: "BAJA SSTT" },
    { value: "VERIFONE REUTILIZABLE", label: "VERIFONE REUTILIZABLE" },
    { value: "BAJA POR CAMBIO DE TERMINAL", label: "BAJA POR CAMBIO DE TERMINAL" },
  ],
};

const FICHA_SELECT_KEY_TO_SOURCE = {
  estado: "estado",
  nDeComercio: "comercios",
  legajo: "legajos",
  rubro: "rubros",
  rubroSecundario: "rubros",
  altaAhora12: "siNo",
  provincia: "provincias",
  nombreDelAsesor: "asesores",
  altas: "altas",
  baja: "bajas",
  linkDePago: "siNo",
  banco: "bancos",
  tipoDeCuenta: "tipoCuenta",
  estadoAfip: "estadoAfip",
  exento: "exento",
  estadoRentas: "estadoRentas",
  provincia3: "provincias",
};

const FICHA_READONLY_KEYS = new Set(["sasMutual", "estado", "nDeTerminal", "cbuCvu", "letraCbu", "largoCbu", "alicuota"]);

const normalizeOption = (value, label = value, extra = {}) => ({
  value: value == null ? "" : String(value),
  label: label == null ? String(value ?? "") : String(label),
  ...extra,
});

const dedupeOptions = (options = []) => {
  const seen = new Set();

  return options.filter((option) => {
    const key = String(option?.value ?? "");
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const mapFichaDraftToPayload = (draft = {}) => ({
  SasMutual: draft.sasMutual || "",
  Estado: draft.estado || "",
  NDeTerminal: draft.nDeTerminal || "",
  NDeComercio: draft.nDeComercio || "",
  NroLocal: draft.nroLocal || "",
  Legajo: draft.legajo || "",
  NombreDeFantasia: draft.nombreDeFantasia || "",
  Domicilio: draft.domicilio || "",
  Localidad: draft.localidad || "",
  CodigoPostal: draft.codigoPostal || "",
  Telefono: normalizeEditablePhoneValue(draft.telefono),
  ReferenciaTelefono: sanitizeLegacyTextValue(draft.referenciaTelefono),
  TelefonoAlternativo: normalizeEditablePhoneValue(draft.telefonoAlternativo),
  LinkDePago: draft.linkDePago || "",
  Rubro: draft.rubro || "",
  RubroSecundario: draft.rubroSecundario || "",
  AltaAhora12: draft.altaAhora12 || "",
  FechaDeAlta: draft.fechaDeAlta || "",
  FechaDeBaja: draft.fechaDeBaja || "",
  TipoDeBaja: draft.tipoDeBaja || "",
  Provincia: draft.provincia || "",
  NombreDelAsesor: draft.nombreDelAsesor || "",
  Altas: draft.altas || "",
  Baja: draft.baja || "",
  Mail: draft.mail || "",
  Titular: draft.titular || "",
  CuitCuil: draft.cuitCuil || "",
  Banco: draft.banco || "",
  TipoDeCuenta: draft.tipoDeCuenta || "",
  CbuCvu: draft.cbuCvu || "",
  NDeCuenta: draft.nDeCuenta || "",
  LetraCbu: draft.letraCbu || "",
  LargoCbu: draft.largoCbu || "",
  Alias: draft.alias || "",
  RazonSocial: draft.razonSocial || "",
  CuitRazonSocial: draft.cuitRazonSocial || "",
  EstadoAfip: draft.estadoAfip || "",
  CodActividad: draft.codActividad || "",
  Actividad: draft.actividad || "",
  Exento: draft.exento || "",
  EstadoRentas: draft.estadoRentas || "",
  Actividad2: draft.actividad2 || "",
  DomicilioFiscal: draft.domicilioFiscal || "",
  Provincia3: draft.provincia3 || "",
  Alicuota: draft.alicuota || "",
  CodActividadRentas: draft.codActividadRentas || "",
  ActividadRentas: draft.actividadRentas || "",
  AutorizadoParaCambiosAbm: draft.autorizadoParaCambiosAbm || "",
  ReferenciaTelefonoAlternativo: sanitizeLegacyTextValue(draft.referenciaTelefonoAlternativo || draft.contactoAlternativo),
  ContactoAlternativo: sanitizeLegacyTextValue(draft.referenciaTelefonoAlternativo || draft.contactoAlternativo),
});

const groupFichaFieldsBySection = (campos = []) =>
  campos.reduce((acc, item) => {
    const section = item?.section || "General";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(item);
    return acc;
  }, {});

const sortFichaSections = (grouped = {}) =>
  FICHA_SECTION_ORDER.reduce((acc, section) => {
    if (grouped[section]?.length) {
      acc[section] = grouped[section];
    }
    return acc;
  }, {});

const buildFichaCatalogOptions = (catalogos = {}, field) => {
  const source = field?.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field?.key];
  if (!source) {
    return [];
  }

  if (FICHA_FIXED_OPTIONS[source]) {
    return FICHA_FIXED_OPTIONS[source];
  }

  return catalogos[source] || [];
};

const hasLetters = (value = "") => /[a-zA-Z]/.test(String(value));

const applyFichaFieldSideEffects = (prevDraft, fieldKey, nextValue, catalogos = {}) => {
  const nextDraft = {
    ...prevDraft,
    [fieldKey]: nextValue,
  };

  if (fieldKey === "tipoDeCuenta") {
    nextDraft.cbuCvu = nextValue === "BILLETERA VIRTUAL" ? "CVU" : nextValue ? "CBU" : "";
  }

  if (fieldKey === "nDeCuenta") {
    const rawValue = String(nextValue || "");
    nextDraft.letraCbu = rawValue ? (hasLetters(rawValue) ? "CORRECTO" : "INCORRECTO") : "";
    nextDraft.largoCbu = rawValue ? String(rawValue.length) : "";
  }

  if (fieldKey === "provincia3") {
    const provincias = catalogos.provincias || [];
    const selected = provincias.find((item) => item.value === nextValue);
    nextDraft.alicuota = selected?.alicuota || "";
  }

  return nextDraft;
};

async function fetchFichaCatalogos() {
  const [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes] = await Promise.all([
    fetch("/api/Banco/lista"),
    fetch("/api/Rubro/lista"),
    fetch("/api/Provincia/listaprovincia"),
    fetch("/api/Legajo/lista"),
    fetch("/api/Comercio/lista"),
    fetch("/api/Usuarios/obtenergestoresyasesores"),
  ]);

  const responses = [bancosRes, rubrosRes, provinciasRes, legajosRes, comerciosRes, asesoresRes];
  const failed = responses.find((response) => !response.ok);
  if (failed) {
    throw new Error("No se pudieron cargar los catálogos de edición.");
  }

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all(
    responses.map((response) => response.json())
  );

  return {
    bancos: (Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    ),
    rubros: (Array.isArray(rubrosData) ? rubrosData : []).map((item) => normalizeOption(item, item)),
    provincias: (Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    ),
    legajos: (Array.isArray(legajosData) ? legajosData : []).map((item) =>
      normalizeOption(item?.nroLegajo ?? item?.NroLegajo ?? "", item?.nroLegajo ?? item?.NroLegajo ?? "")
    ),
    comercios: (Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(item?.num ?? item?.Num ?? "", item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? "")
    ),
    asesores: (Array.isArray(asesoresData) ? asesoresData : []).map((item) => normalizeOption(item, item)),
  };
}

async function fetchFichaCatalogosV2() {
  const fetchJsonWithFallback = async (urls = []) => {
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          lastError = new Error(`Error cargando ${url}: ${response.status}`);
          continue;
        }

        return response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error("No se pudieron cargar los catalogos de edicion.");
  };

  const [bancosData, rubrosData, provinciasData, legajosData, comerciosData, asesoresData] = await Promise.all([
    fetchJsonWithFallback(["/api/Banco/lista"]),
    fetchJsonWithFallback(["/api/Rubro/lista"]),
    fetchJsonWithFallback(["/api/Provincia/listaprovincia"]),
    fetchJsonWithFallback(["/api/Legajo/lista", "/api/Legajo/listacompleto"]),
    fetchJsonWithFallback(["/api/Comercio/lista"]),
    fetchJsonWithFallback(["/api/Usuarios/obtenergestoresyasesores"]),
  ]);

  return {
    bancos: dedupeOptions((Array.isArray(bancosData) ? bancosData : []).map((item) =>
      normalizeOption(item?.nomBanco ?? item?.NomBanco ?? "", item?.nomBanco ?? item?.NomBanco ?? "")
    )),
    rubros: dedupeOptions((Array.isArray(rubrosData) ? rubrosData : []).map((item) =>
      normalizeOption(item?.nomRubro ?? item?.NomRubro ?? item, item?.nomRubro ?? item?.NomRubro ?? item)
    )),
    provincias: dedupeOptions((Array.isArray(provinciasData) ? provinciasData : []).map((item) =>
      normalizeOption(item?.provincia ?? item?.Provincia ?? "", item?.provincia ?? item?.Provincia ?? "", {
        alicuota: item?.alicuota ?? item?.Alicuota ?? "",
      })
    )),
    legajos: dedupeOptions((Array.isArray(legajosData) ? legajosData : []).map((item) => {
      const legajoValue = item?.nroLegajo ?? item?.NroLegajo ?? "";
      const nombreFantasia = item?.nombreDeFantasia ?? item?.NombreDeFantasia ?? "";
      const label = nombreFantasia ? `${legajoValue} - ${nombreFantasia}` : legajoValue;
      return normalizeOption(legajoValue, label);
    })),
    comercios: dedupeOptions((Array.isArray(comerciosData) ? comerciosData : []).map((item) =>
      normalizeOption(
        item?.num ?? item?.Num ?? item?.numComercio ?? item?.NumComercio ?? "",
        item?.numComercio ?? item?.NumComercio ?? item?.num ?? item?.Num ?? ""
      )
    )),
    asesores: dedupeOptions((Array.isArray(asesoresData) ? asesoresData : []).map((item) =>
      normalizeOption(item?.nombre ?? item?.Nombre ?? item, item?.nombre ?? item?.Nombre ?? item)
    )),
  };
}

const resolveMotivoCodigoFromRegla = (reglas = [], reglaId) => {
  const regla = reglas.find((item) => Number(item?.id) === Number(reglaId));
  return regla?.nombreAccion || "";
};

const resolveRuleIdByMotivoCodigo = (reglas = [], motivoCodigo = "") => {
  const normalized = String(motivoCodigo || "").trim().toUpperCase();
  const regla = (Array.isArray(reglas) ? reglas : []).find(
    (item) => String(item?.nombreAccion || "").trim().toUpperCase() === normalized
  );
  return regla?.id ? String(regla.id) : "";
};

const resolveMotivoCodigoFromAlerta = (alerta = {}) => {
  const motivoCodigo = String(alerta?.motivoCodigo || "").trim();
  if (motivoCodigo) return motivoCodigo;
  const tipo = String(alerta?.tipoAlerta || "").toLowerCase();
  if (tipo.includes("zoco check")) return "MAN_ZOCO_CHECK";
  if (tipo.includes("extraviad")) return "MAN_TERMINAL_EXTRAVIADA";
  if (tipo.includes("onboarding")) return "MAN_ONBOARDING";
  if (tipo.includes("alerta operativa")) return "MAN_ALERTA_OPERATIVA";
  if (tipo.includes("roll")) return "FALTA_ROLLOS";
  if (tipo.includes("contacto vencido")) return "PROXIMO_CONTACTO_VENCIDO";
  if (tipo.includes("caida")) return "CAIDA_FACTURACION";
  if (tipo.includes("factur") || tipo.includes("caída") || tipo.includes("caida") || tipo.includes("actividad")) {
    return "FALTA_FACTURACION";
  }
  return "";
};

const filterManualRules = (reglas = []) =>
  (Array.isArray(reglas) ? reglas : []).filter((regla) =>
    String(regla?.nombreAccion || "").toUpperCase().startsWith("MAN_")
    && String(regla?.nombreAccion || "").toUpperCase() !== "MAN_ONBOARDING"
  );

const resolveGestionScopeByReason = (motivoCodigo = "") => {
  const normalized = String(motivoCodigo || "").trim().toUpperCase();
  if (GESTION_SCOPE_RULE_CODES.operativa.has(normalized) || normalized === "FALTA_FACTURACION" || normalized === "CAIDA_FACTURACION") {
    return "operativa";
  }

  if (GESTION_SCOPE_RULE_CODES.proactiva.has(normalized)) {
    return "proactiva";
  }

  return "ipc";
};

const resolveGestionScope = (item = {}) =>
  resolveGestionScopeByReason(item?.motivoCodigo || item?.MotivoCodigo || item?.nombreAccion);

const isOperationalProtocolRule = (motivoCodigo = "") =>
  String(motivoCodigo || "").trim().toUpperCase() === "MAN_ALERTA_OPERATIVA";

const filterGestionRulesByScope = (reglas = [], scope = "ipc") =>
  filterManualRules(reglas).filter((regla) => {
    const reason = String(regla?.nombreAccion || "").trim().toUpperCase();
    if (scope === "operativa") {
      return GESTION_SCOPE_RULE_CODES.operativa.has(reason);
    }

    if (scope === "proactiva") {
      return GESTION_SCOPE_RULE_CODES.proactiva.has(reason);
    }

    return !GESTION_SCOPE_RULE_CODES.operativa.has(reason) && !GESTION_SCOPE_RULE_CODES.proactiva.has(reason);
  });

const canUseOperationalProtocol = (scope, estadoGestion, motivoCodigo) =>
  scope === "operativa" && estadoGestion === "Contactado" && isOperationalProtocolRule(motivoCodigo);

const normalizeWhatsappPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return "";
  return digits.startsWith("54") ? digits : `54${digits}`;
};

const buildSuggestedObservation = (item, estadoGestion) => {
  const lines = [
    `Motivo: ${item?.tipoAlerta || item?.motivo || "-"}`,
    `Accion sugerida: ${item?.accionSugerida || "-"}`,
    item?.mensajeSugerido ? `Mensaje sugerido: ${item.mensajeSugerido}` : "",
    item?.resumen ? `Contexto: ${item.resumen}` : "",
    estadoGestion ? `Resultado: ${estadoGestion}` : "",
  ].filter(Boolean);

  return lines.join("\n");
};

const truncateText = (value, max = 120) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
};

const getGestionTypeLabel = (item = {}) => (item?.esAutomatica ? "Automatica" : (item?.tipo || "Manual"));
const buildIpcMotivoCodigo = (tipoIpc, motivoIpc) => {
  const tipoSlug = normalizeText(tipoIpc).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const motivoSlug = normalizeText(motivoIpc).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  if (!tipoSlug || !motivoSlug) {
    return "MAN_IPC";
  }

  return `MAN_IPC_${tipoSlug.toUpperCase()}_${motivoSlug.toUpperCase()}`;
};

const buildIpcObservation = (tipoIpc, motivoIpc, observacion) => {
  const detail = String(observacion || "").trim();
  const lines = [
    tipoIpc ? `Tipo IPC: ${tipoIpc}` : "",
    motivoIpc ? `Motivo IPC: ${motivoIpc}` : "",
    detail,
  ].filter(Boolean);

  return lines.join("\n");
};

const CrmPostventa = () => {
  const { darkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const apiUrlToken = process.env.REACT_APP_API_TOKEN;

  const [cargandoSesion, setCargandoSesion] = useState(true);
  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteResults, setClienteResults] = useState([]);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [clienteSeleccionadoId, setClienteSeleccionadoId] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });
  const [clientePendiente, setClientePendiente] = useState(null);
  const [terminalesGrupo, setTerminalesGrupo] = useState([]);
  const [ficha, setFicha] = useState(null);
  const [fichaEditOpen, setFichaEditOpen] = useState(false);
  const [fichaEditDraft, setFichaEditDraft] = useState({});
  const [fichaCatalogos, setFichaCatalogos] = useState({
    bancos: [],
    rubros: [],
    provincias: [],
    legajos: [],
    comercios: [],
    asesores: [],
  });
  const [fichaCatalogosLoading, setFichaCatalogosLoading] = useState(false);
  const [facturacion, setFacturacion] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [inactivos, setInactivos] = useState(null);
  const [gestiones, setGestiones] = useState(null);
  const [seguimiento, setSeguimiento] = useState(null);
  const [ordenesPago, setOrdenesPago] = useState(null);
  const [cupones, setCupones] = useState(null);
  const [rolloModalOpen, setRolloModalOpen] = useState(false);
  const [altasBajasMode, setAltasBajasMode] = useState("alta");
  const [altaWizardStep, setAltaWizardStep] = useState(1);
  const [altaWizardData, setAltaWizardData] = useState(() => emptyAltaWizardData());
  const [altaWizardLegajos, setAltaWizardLegajos] = useState([]);
  const [altaAgregaSearch, setAltaAgregaSearch] = useState("");
  const [altaAgregaResults, setAltaAgregaResults] = useState([]);
  const [altaAgregaLoading, setAltaAgregaLoading] = useState(false);
  const [altaLegajoDocs, setAltaLegajoDocs] = useState([]);
  const [altaLegajoDocDraft, setAltaLegajoDocDraft] = useState({
    nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
    file: null,
  });
  const [altaWizardStatus, setAltaWizardStatus] = useState({
    blanqueo: "",
    legajo: "",
    alta: "",
  });
  const [bajaDraft, setBajaDraft] = useState(() => emptyBajaDraft());
  const [bajaLookupSearch, setBajaLookupSearch] = useState("");
  const [bajaLookupResults, setBajaLookupResults] = useState([]);
  const [bajaLookupLoading, setBajaLookupLoading] = useState(false);
  const [errores, setErrores] = useState({});
  const [cargas, setCargas] = useState({
    ficha: false,
    facturacion: false,
    alertas: false,
    inactivos: false,
    ordenesPago: false,
    cupones: false,
    gestiones: false,
    seguimiento: false,
    guardandoFicha: false,
    guardandoGestion: false,
    guardandoAlertaGestion: false,
    guardandoRollo: false,
    altaBajaCatalogos: false,
    guardandoBlanqueo: false,
    guardandoLegajo: false,
    guardandoDocumentoLegajo: false,
    guardandoAltaWorkflow: false,
    guardandoBajaWorkflow: false,
  });
  const [analyticsRange, setAnalyticsRange] = useState({
    desde: makeMonthStart(),
    hasta: makeToday(),
  });
  const [gestionComposerScope, setGestionComposerScope] = useState("ipc");
  const [gestionDraft, setGestionDraft] = useState({
    reglaId: "",
    estadoGestion: "Pendiente",
    tipoIpc: "",
    ipcMotivo: "",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [rolloDraft, setRolloDraft] = useState({
    fechaEntrega: makeToday(),
    cantidad: "",
    observacion: "",
  });
  const [alertasFiltros, setAlertasFiltros] = useState({
    fecha: makeToday(),
    estado: "",
    prioridad: "",
    tipo: "",
    provincia: "",
    responsable: "",
    search: "",
  });
  const [alertasSearchDraft, setAlertasSearchDraft] = useState("");
  const [alertasOperativasSearch, setAlertasOperativasSearch] = useState("");
  const [alertasFacturacionSearch, setAlertasFacturacionSearch] = useState("");
  const [alertasZocoCheckSearch, setAlertasZocoCheckSearch] = useState("");
  const [alertasTerminalExtraviadaSearch, setAlertasTerminalExtraviadaSearch] = useState("");
  const [inactivosFiltros, setInactivosFiltros] = useState({
    fecha: makeToday(),
    provincia: "",
    responsable: "",
    diasSinOperar: "",
    search: "",
  });
  const [inactivosSearchDraft, setInactivosSearchDraft] = useState("");
  const [ordenesPagoFiltros, setOrdenesPagoFiltros] = useState({
    fecha: makeToday(),
    search: "",
  });
  const [cuponesFiltros, setCuponesFiltros] = useState({
    mes: makeCurrentMonth(),
    search: "",
  });
  const [facturacionFiltroComercio, setFacturacionFiltroComercio] = useState("");
  const [facturacionDetalle, setFacturacionDetalle] = useState(null);
  const [facturacionAccionItem, setFacturacionAccionItem] = useState(null);
  const [facturacionGestionItem, setFacturacionGestionItem] = useState(null);
  const [facturacionGestionDraft, setFacturacionGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [timelineDetalle, setTimelineDetalle] = useState(null);
  const [gestionDetalleItem, setGestionDetalleItem] = useState(null);
  const [gestionEditItem, setGestionEditItem] = useState(null);
  const [gestionEditDraft, setGestionEditDraft] = useState({
    estadoResultante: "Pendiente",
    fechaProximoContacto: "",
    prioridad: "Media",
    observacion: "",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    resultadoZocoCheck: "",
  });
  const [gestionFinalizarItem, setGestionFinalizarItem] = useState(null);
  const [gestionFinalizarDraft, setGestionFinalizarDraft] = useState({
    estadoResultante: "Cerrado",
    motivoCierre: "Resuelto",
    observacionFinal: "",
    resultadoZocoCheck: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
  });
  const [gestionesRollosExpanded, setGestionesRollosExpanded] = useState(false);
  const [alertaGestionDraft, setAlertaGestionDraft] = useState({
    clienteId: 0,
    reglaId: "",
    estadoGestion: "Pendiente",
    canalContacto: "",
    resultadoLlamada: "",
    evidenciaContacto: "",
    observacion: "",
    fechaGestion: makeNowDateTimeLocal(),
    fechaProximoContacto: "",
  });
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
  const [alertaGestionMode, setAlertaGestionMode] = useState("operativa");
  const [alertaAccionItem, setAlertaAccionItem] = useState(null);

  const section = useMemo(() => {
    if (location.pathname.includes("/facturacion")) return "facturacion";
    if (location.pathname.includes("/alertas")) return "alertas";
    if (location.pathname.includes("/ordenes-pago")) return "ordenes-pago";
    if (location.pathname.includes("/cupones")) return "cupones";
    if (location.pathname.includes("/ficha")) return "ficha";
    if (location.pathname.includes("/herramientas")) return "herramientas";
    if (location.pathname.includes("/altas-bajas")) return "altas-bajas";
    if (location.pathname.includes("/campanias-zoco-check")) return "campanias-zoco-check";
    if (location.pathname.includes("/gestion")) return "gestion";
    if (location.pathname.includes("/seguimiento")) return "seguimiento";
    return "alertas";
  }, [location.pathname]);
  const sessionPayload = useMemo(() => decodeTokenPayload(getToken()), []);
  const canManageZocoCheck = useMemo(() => {
    const rawRole = sessionPayload?.rol ?? sessionPayload?.role ?? sessionPayload?.Role ?? "";
    const roleNumber = Number(rawRole);
    const roleText = String(rawRole || "").toLowerCase();

    return roleNumber === 8
      || roleText.includes("admin")
      || roleText.includes("comercial")
      || roleText.includes("marketing");
  }, [sessionPayload]);

  const clienteGrupos = useMemo(() => agruparClientesPorGrupo(clienteResults), [clienteResults]);
  const currentGroupKey = useMemo(
    () => buildClienteGroupKey(clientePendiente || ficha?.header || {}),
    [clientePendiente, ficha?.header]
  );
  const clienteSeleccionadoData = useMemo(() => clientePendiente || ficha?.header || {}, [clientePendiente, ficha?.header]);
  const clienteSeleccionadoCuit = useMemo(
    () => normalizeDigits(clienteSeleccionadoData?.cuit || clienteSeleccionadoData?.Cuit),
    [clienteSeleccionadoData]
  );
  const manualAlertaRules = useMemo(() => filterGestionRulesByScope(alertas?.reglas || [], "operativa"), [alertas?.reglas]);
  const inactivosProactivaRules = useMemo(
    () =>
      filterGestionRulesByScope(inactivos?.reglas || alertas?.reglas || gestiones?.reglas || [], "proactiva").filter((item) =>
        INACTIVOS_PROACTIVA_RULE_CODES.has(String(item?.nombreAccion || "").trim().toUpperCase())
      ),
    [alertas?.reglas, gestiones?.reglas, inactivos?.reglas]
  );
  const gestionIpcRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "ipc"), [gestiones?.reglas]);
  const gestionOperativaRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "operativa"), [gestiones?.reglas]);
  const gestionProactivaRules = useMemo(() => filterGestionRulesByScope(gestiones?.reglas || [], "proactiva"), [gestiones?.reglas]);
  const gestionScopeRules = useMemo(() => ({
    ipc: gestionIpcRules,
    operativa: gestionOperativaRules,
    proactiva: gestionProactivaRules,
  }), [gestionIpcRules, gestionOperativaRules, gestionProactivaRules]);
  const currentGestionRules = useMemo(
    () => gestionScopeRules[gestionComposerScope] || [],
    [gestionComposerScope, gestionScopeRules]
  );
  const currentGestionStates = useMemo(
    () => GESTION_SCOPE_STATES[gestionComposerScope] || GESTION_STATES,
    [gestionComposerScope]
  );
  const currentIpcMotivoOptions = useMemo(
    () => GESTION_IPC_REASON_OPTIONS[gestionDraft.tipoIpc] || [],
    [gestionDraft.tipoIpc]
  );
  const gestionDraftMotivoCodigo = useMemo(
    () =>
      gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId),
    [gestionComposerScope, gestionDraft.ipcMotivo, gestionDraft.reglaId, gestionDraft.tipoIpc, gestiones?.reglas]
  );
  const protocoloContacto = gestiones?.protocoloContacto || null;
  const usaProtocoloOperativo = useMemo(
    () => canUseOperationalProtocol(gestionComposerScope, gestionDraft.estadoGestion, gestionDraftMotivoCodigo),
    [gestionComposerScope, gestionDraft.estadoGestion, gestionDraftMotivoCodigo]
  );
  const canalesProtocoloHabilitados = useMemo(
    () => (usaProtocoloOperativo ? (protocoloContacto?.canalesHabilitados || []) : []),
    [protocoloContacto?.canalesHabilitados, usaProtocoloOperativo]
  );
  const gestionesPorScope = useMemo(() => {
    const historial = Array.isArray(gestiones?.historial) ? gestiones.historial : [];
    return {
      ipc: historial.filter((item) => resolveGestionScope(item) === "ipc"),
      operativa: historial.filter((item) => resolveGestionScope(item) === "operativa"),
      proactiva: historial.filter((item) => resolveGestionScope(item) === "proactiva"),
    };
  }, [gestiones?.historial]);

  useEffect(() => {
    setGestionDraft((prev) => ({
      ...prev,
      reglaId:
        currentGestionRules?.[0]?.id && !currentGestionRules.some((item) => String(item.id) === String(prev.reglaId))
          ? String(currentGestionRules[0].id)
          : prev.reglaId || (currentGestionRules?.[0]?.id ? String(currentGestionRules[0].id) : ""),
      estadoGestion: currentGestionStates.includes(prev.estadoGestion) ? prev.estadoGestion : (currentGestionStates[0] || "Pendiente"),
    }));
  }, [currentGestionRules, currentGestionStates]);

  useEffect(() => {
    if (!usaProtocoloOperativo) {
      return;
    }

    setGestionDraft((prev) => {
      const canalActual = String(prev.canalContacto || "");
      if (!canalActual || canalesProtocoloHabilitados.includes(canalActual)) {
        return prev;
      }

      return {
        ...prev,
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
      };
    });
  }, [canalesProtocoloHabilitados, usaProtocoloOperativo]);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem("token");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const copiarMensajeSugerido = useCallback(async (item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    if (!mensaje) {
      Swal.fire(title, "No hay mensaje sugerido para copiar.", "info");
      return;
    }

    try {
      const reglaIdToSave = gestionComposerScope === "ipc"
        ? Number(currentGestionRules?.[0]?.id || gestionDraft.reglaId)
        : Number(gestionDraft.reglaId);
      const observacionToSave = gestionComposerScope === "ipc"
        ? buildIpcObservation(gestionDraft.tipoIpc, gestionDraft.ipcMotivo, gestionDraft.observacion)
        : gestionDraft.observacion;
      const motivoCodigoToSave = gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId);

      if (!reglaIdToSave) {
        Swal.fire("Gestion", "No hay una regla IPC disponible para guardar la gestion.", "info");
        return;
      }
      await navigator.clipboard.writeText(mensaje);
      Swal.fire({
        icon: "success",
        title: "Mensaje copiado",
        text: "El mensaje sugerido quedo en el portapapeles.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(title, "No se pudo copiar el mensaje sugerido.", "error");
    }
  }, []);

  const abrirWhatsappSugerido = useCallback((item, title = "CRM Postventa") => {
    const mensaje = String(item?.mensajeSugerido || "").trim();
    const telefono = resolvePreferredWhatsappPhone(item);

    if (!telefono) {
      Swal.fire(title, "El cliente no tiene telefono cargado.", "info");
      return;
    }

    const url = `https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const descargarOrdenPago = useCallback(async (item) => {
    if (!item?.documentoKey || !item?.fechaSubida) {
      Swal.fire("Órdenes de pago", "La orden seleccionada no tiene los datos necesarios para descargar el PDF.", "info");
      return;
    }

    try {
      const payload = await crmPostventaApi.obtenerOrdenPagoPdf(clienteSeleccionadoId, item.fechaSubida, item.documentoKey);
      await descargarOrdenPagoDesdePayload({ fechaPago: item.fechaSubida, payload });
    } catch (error) {
      console.error("Error descargando orden de pago:", error);
      Swal.fire("Órdenes de pago", error.message || "No se pudo descargar el PDF.", "error");
    }
  }, [clienteSeleccionadoId]);

  const mostrarAccesoDenegado = useCallback(
    (title, text) => {
      Swal.fire({
        title,
        text,
        imageUrl: logo,
        imageWidth: 100,
        imageHeight: 30,
        imageAlt: "Logo",
        confirmButtonText: "Ok",
        timer: 5000,
        allowOutsideClick: true,
        customClass: {
          container: darkMode ? "swal2-container--dark" : "swal2-container--light",
          confirmButton: "my-swal-button",
        },
      }).then(() => clearSession());
    },
    [clearSession, darkMode]
  );

  const validarSesion = useCallback(async () => {
    const token = getToken();
    if (!token) {
      mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
      return;
    }

    try {
      const response = await fetch(apiUrlToken, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Token: token }),
      });

      if (response.status === 401 || response.status === 403) {
        mostrarAccesoDenegado("Sesión expirada o token inválido.", "Inicie sesión nuevamente.");
        return;
      }

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const raw = await response.text();
      let rol = Number(raw);
      if (!Number.isFinite(rol)) {
        rol = Number(JSON.parse(raw));
      }

      if (rol !== 8) {
        Swal.fire({
          title: "Acceso denegado.",
          text: "No tiene permisos para este panel.",
          imageUrl: logo,
          imageWidth: 100,
          imageHeight: 30,
          imageAlt: "Logo",
          confirmButtonText: "Ok",
          timer: 5000,
          allowOutsideClick: true,
          customClass: {
            container: darkMode ? "swal2-container--dark" : "swal2-container--light",
            confirmButton: "my-swal-button",
          },
        }).then(() => navigate("/"));
        return;
      }
    } catch (error) {
      console.error("Error validando sesión CRM Postventa:", error);
      Swal.fire("Error", "No se pudo validar la sesión en este momento.", "error");
      return;
    }

    setCargandoSesion(false);
  }, [apiUrlToken, darkMode, mostrarAccesoDenegado, navigate]);

  const cargarClientes = useCallback(async (searchValue = "") => {
    setClienteLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(searchValue);
      setClienteResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error buscando clientes:", error);
      setClienteResults([]);
      Swal.fire("Error", error.message || "No se pudieron buscar clientes.", "error");
    } finally {
      setClienteLoading(false);
    }
  }, []);

  const cargarAlertas = useCallback(async (filtrosActuales) => {
    setCargas((prev) => ({ ...prev, alertas: true }));
    try {
      const data = await crmPostventaApi.obtenerAlertas(filtrosActuales);
      setAlertas(data);
      setErrores((prev) => ({ ...prev, alertas: undefined }));
    } catch (error) {
      console.error("Error cargando alertas:", error);
      setAlertas(null);
      setErrores((prev) => ({ ...prev, alertas: error.message || "No se pudieron cargar las alertas." }));
    } finally {
      setCargas((prev) => ({ ...prev, alertas: false }));
    }
  }, []);

  const cargarInactivos = useCallback(async (filtrosActuales) => {
    setCargas((prev) => ({ ...prev, inactivos: true }));
    try {
      console.log("[CRM Postventa][Inactivos] Request", filtrosActuales);
      const data = await crmPostventaApi.obtenerInactivos(filtrosActuales);
      console.log("[CRM Postventa][Inactivos] Response", data);
      setInactivos(data);
      setErrores((prev) => ({ ...prev, inactivos: undefined }));
    } catch (error) {
      console.error("Error cargando comercios inactivos:", error);
      setInactivos(null);
      setErrores((prev) => ({ ...prev, inactivos: error.message || "No se pudieron cargar los comercios inactivos." }));
    } finally {
      setCargas((prev) => ({ ...prev, inactivos: false }));
    }
  }, []);

  const cargarOrdenesPago = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, ordenesPago: true }));
    try {
      const fechaConsultada = filtrosActuales?.fecha || makeToday();
      const search = String(filtrosActuales?.search || "").trim();
      const response = await crmPostventaApi.obtenerOrdenesPago(clienteId, fechaConsultada, search);
      const filas = Array.isArray(response?.filas) ? response.filas : Array.isArray(response?.Filas) ? response.Filas : [];

      setOrdenesPago({
        ...response,
        filas,
      });
      setErrores((prev) => ({ ...prev, ordenesPago: undefined }));
    } catch (error) {
      console.error("Error cargando ordenes de pago:", error);
      setOrdenesPago(null);
      setErrores((prev) => ({ ...prev, ordenesPago: error.message || "No se pudieron cargar las ordenes de pago." }));
    } finally {
      setCargas((prev) => ({ ...prev, ordenesPago: false }));
    }
  }, []);

  const cargarCupones = useCallback(async (clienteId, filtrosActuales) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({ ...prev, cupones: true }));
    try {
      const { mes, desde, hasta } = buildMonthDateRange(filtrosActuales?.mes);
      const search = String(filtrosActuales?.search || "").trim();
      const response = await crmPostventaApi.obtenerCupones(clienteId, desde, hasta, search);
      const rows = Array.from(
        (Array.isArray(response?.filas) ? response.filas : Array.isArray(response?.Filas) ? response.Filas : []).reduce((acc, item) => {
          const normalized = normalizeCuponLegacyRow(item);
          acc.set(buildCuponDedupeKey(normalized), normalized);
          return acc;
        }, new Map()).values()
      );
      const resumen = buildCuponSummary(rows);
      const backendMessage = sanitizeCuponesMessage(response?.message ?? response?.Message);

      setCupones({
        tieneDatosReales: Boolean(response?.tieneDatosReales ?? response?.TieneDatosReales ?? rows.length > 0),
        message: rows.length > 0 ? "" : backendMessage || "No hay cupones para este cliente/terminal.",
        mesConsultado: formatLegacyMonthLabel(mes),
        criterioFiltrado: (response?.cuitFiltrado ?? response?.CuitFiltrado ?? clienteSeleccionadoCuit) || "-",
        fechaDesde: response?.fechaDesde ?? response?.FechaDesde ?? desde,
        fechaHasta: response?.fechaHasta ?? response?.FechaHasta ?? hasta,
        resumen,
        filas: rows,
      });
      setErrores((prev) => ({ ...prev, cupones: undefined }));
    } catch (error) {
      console.error("Error cargando cupones:", error);
      setCupones(null);
      setErrores((prev) => ({
        ...prev,
        cupones: sanitizeCuponesMessage(error.message) || "No se pudieron cargar los cupones.",
      }));
    } finally {
      setCargas((prev) => ({ ...prev, cupones: false }));
    }
  }, [clienteSeleccionadoCuit]);

  const cargarModulo = useCallback(async (clienteId, range) => {
    if (!clienteId) {
      return;
    }

    setCargas((prev) => ({
      ...prev,
      ficha: true,
      facturacion: true,
      gestiones: true,
      seguimiento: true,
      guardandoGestion: false,
    }));
    setErrores({});

    const results = await Promise.allSettled([
      crmPostventaApi.obtenerFicha(clienteId),
      crmPostventaApi.obtenerFacturacion(clienteId, range.desde, range.hasta),
      crmPostventaApi.obtenerGestiones(clienteId),
      crmPostventaApi.obtenerSeguimiento(clienteId, range.desde, range.hasta),
    ]);

    const [fichaRes, facturacionRes, gestionesRes, seguimientoRes] = results;

    if (fichaRes.status === "fulfilled") {
      setFicha(fichaRes.value);
    } else {
      setFicha(null);
      setErrores((prev) => ({ ...prev, ficha: fichaRes.reason?.message || "No se pudo cargar la ficha." }));
    }

    if (facturacionRes.status === "fulfilled") {
      setFacturacion(facturacionRes.value);
    } else {
      setFacturacion(null);
      setErrores((prev) => ({ ...prev, facturacion: facturacionRes.reason?.message || "No se pudo cargar facturación." }));
    }

    if (gestionesRes.status === "fulfilled") {
      let gestionesValue = gestionesRes.value;
      const clienteKey =
        gestionesRes.value?.rollos?.clienteKey ||
        fichaRes.value?.header?.clienteKey ||
        crmPostventaApi.buildClienteKey(fichaRes.value?.header || {});

      if (clienteKey) {
        try {
          const rollos = await crmPostventaApi.obtenerRollos(clienteKey, clienteId);
          gestionesValue = {
            ...gestionesValue,
            rollos,
          };
        } catch (error) {
          console.error("Error cargando rollos:", error);
        }
      }

      setGestiones(gestionesValue);
      setGestionDraft((prev) => ({
        ...prev,
        reglaId: filterManualRules(gestionesValue?.reglas || [])?.[0]?.id ? String(filterManualRules(gestionesValue?.reglas || [])[0].id) : "",
      }));
    } else {
      setGestiones(null);
      setErrores((prev) => ({ ...prev, gestiones: gestionesRes.reason?.message || "No se pudieron cargar las gestiones." }));
    }

    if (seguimientoRes.status === "fulfilled") {
      setSeguimiento(seguimientoRes.value);
    } else {
      setSeguimiento(null);
      setErrores((prev) => ({ ...prev, seguimiento: seguimientoRes.reason?.message || "No se pudo cargar el seguimiento." }));
    }

    const terminalesDesdeFacturacion =
      facturacionRes.status === "fulfilled"
        ? buildTerminalesFromFacturacion(facturacionRes.value?.filas || [])
        : [];
    const terminalesDesdeFicha =
      fichaRes.status === "fulfilled"
        ? normalizeTerminalOptions(fichaRes.value?.header?.terminalesGrupo || [])
        : [];

    setTerminalesGrupo(mergeTerminalOptions(terminalesDesdeFicha, terminalesDesdeFacturacion));

    setCargas((prev) => ({
      ...prev,
      ficha: false,
      facturacion: false,
      gestiones: false,
      seguimiento: false,
    }));
  }, []);

  const seleccionarCliente = useCallback(
    (cliente) => {
      if (!cliente?.clienteId) {
        return;
      }

      localStorage.setItem(STORAGE_KEY, String(cliente.clienteId));
      setClienteSeleccionadoId(cliente.clienteId);
      setClientePendiente(cliente);
      const terminales = normalizeTerminalOptions(cliente.terminales || []);
      if (terminales.length) {
        setTerminalesGrupo(terminales);
      }
      setClienteModalOpen(false);
      setFacturacionFiltroComercio("");
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
      setOrdenesPago(null);
      setCupones(null);
      navigate(SECTION_PATHS[section] || SECTION_PATHS.alertas);
    },
    [navigate, section]
  );

  const activateAltaMode = useCallback((mode = "alta") => {
    const isAgregaMode = mode === "agrega";
    const baseData = emptyAltaWizardData();
    setAltasBajasMode(mode);
    setAltaWizardStep(isAgregaMode ? 3 : 1);
    setAltaWizardData({
      ...baseData,
      alta: {
        ...baseData.alta,
        tipoAlta: isAgregaMode ? "AgregaTerminal" : "NuevoAliado",
        altas: isAgregaMode ? "AGREGA" : "ALTA NUEVA",
      },
    });
    setAltaWizardLegajos([]);
    setAltaAgregaSearch("");
    setAltaAgregaResults([]);
    setAltaAgregaLoading(false);
    setAltaLegajoDocs([]);
    setAltaLegajoDocDraft({
      nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
      file: null,
    });
    setAltaWizardStatus({
      blanqueo: "",
      legajo: "",
      alta: "",
    });
  }, []);

  const resetAltasBajas = useCallback(() => {
    setAltasBajasMode("alta");
    setAltaWizardStep(1);
    setAltaWizardData(emptyAltaWizardData());
    setAltaWizardLegajos([]);
    setAltaAgregaSearch("");
    setAltaAgregaResults([]);
    setAltaAgregaLoading(false);
    setAltaLegajoDocs([]);
    setAltaLegajoDocDraft({
      nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
      file: null,
    });
    setAltaWizardStatus({
      blanqueo: "",
      legajo: "",
      alta: "",
    });
    setGestionComposerScope("ipc");
    setBajaDraft(emptyBajaDraft());
    setBajaLookupSearch("");
    setBajaLookupResults([]);
  }, []);

  const aplicarLegajoAlWizard = useCallback((legajoItem) => {
    if (!legajoItem) {
      return;
    }

    setAltaWizardData((prev) => ({
      ...prev,
      legajoId: legajoItem.id ?? legajoItem.Id ?? prev.legajoId,
      legajo: {
        ...prev.legajo,
        nroLegajo: String(legajoItem.nroLegajo ?? legajoItem.NroLegajo ?? prev.legajo.nroLegajo ?? ""),
        terminal: String(legajoItem.terminal ?? legajoItem.Terminal ?? prev.legajo.terminal ?? ""),
        nombreDeFantasia: legajoItem.nombreDeFantasia ?? legajoItem.NombreDeFantasia ?? prev.legajo.nombreDeFantasia,
        provincia: legajoItem.provincia ?? legajoItem.Provincia ?? prev.legajo.provincia,
        tipoPersona: String(legajoItem.tipoPersona ?? legajoItem.TipoPersona ?? prev.legajo.tipoPersona ?? "fisica").toLowerCase(),
        razonSocial: legajoItem.razonSocial ?? legajoItem.RazonSocial ?? prev.legajo.razonSocial,
        calle: legajoItem.calle ?? legajoItem.Calle ?? prev.legajo.calle,
        numero: legajoItem.numero ?? legajoItem.Numero ?? prev.legajo.numero,
        pisoDepto: legajoItem.pisoDepto ?? legajoItem.PisoDepto ?? prev.legajo.pisoDepto,
        localidad: legajoItem.localidad ?? legajoItem.Localidad ?? prev.legajo.localidad,
        codigoPostal: legajoItem.codigoPostal ?? legajoItem.CodigoPostal ?? prev.legajo.codigoPostal,
        emailContacto: legajoItem.emailContacto ?? legajoItem.EmailContacto ?? prev.legajo.emailContacto,
        celularWhatsapp: legajoItem.celularWhatsapp ?? legajoItem.CelularWhatsapp ?? prev.legajo.celularWhatsapp,
        asesor: legajoItem.asesor ?? legajoItem.Asesor ?? prev.legajo.asesor,
      },
      alta: {
        ...prev.alta,
        correo: prev.alta.correo || legajoItem.emailContacto || legajoItem.EmailContacto || prev.usuario.email,
        celular: prev.alta.celular || legajoItem.celularWhatsapp || legajoItem.CelularWhatsapp,
        localidadTitular: prev.alta.localidadTitular || legajoItem.localidad || legajoItem.Localidad || "",
        codigoPostalTitular: prev.alta.codigoPostalTitular || legajoItem.codigoPostal || legajoItem.CodigoPostal || "",
        provincia: prev.alta.provincia || legajoItem.provincia || legajoItem.Provincia || "",
        provincia3: prev.alta.provincia3 || legajoItem.provincia || legajoItem.Provincia || "",
        nombreDelAsesor: prev.alta.nombreDelAsesor || legajoItem.asesor || legajoItem.Asesor || "",
        domicilioComercio:
          prev.alta.domicilioComercio ||
          [legajoItem.calle ?? legajoItem.Calle, legajoItem.numero ?? legajoItem.Numero]
            .filter(Boolean)
            .join(" "),
        localidadComercio: prev.alta.localidadComercio || legajoItem.localidad || legajoItem.Localidad || "",
        codigoPostalComercio: prev.alta.codigoPostalComercio || legajoItem.codigoPostal || legajoItem.CodigoPostal || "",
        titularRazonSocial: prev.alta.titularRazonSocial || legajoItem.razonSocial || legajoItem.RazonSocial || prev.usuario.nombre,
        alicuota:
          prev.alta.alicuota ||
          findProvinciaAlicuota(
            fichaCatalogos.provincias,
            prev.alta.provincia3 || legajoItem.provincia || legajoItem.Provincia || ""
          ),
      },
    }));
  }, [fichaCatalogos.provincias]);

  const aplicarBaseAgregaDesdeFicha = useCallback((clienteBase, fichaBase) => {
    if (!clienteBase || !fichaBase) {
      return;
    }

    const valores = buildFichaValueMapByLabel(fichaBase);
    setAltaWizardData((prev) => ({
      ...prev,
      agregaBaseClienteId: Number(clienteBase?.clienteId || clienteBase?.ClienteId || fichaBase?.header?.clienteId || 0) || null,
      usuario: {
        ...prev.usuario,
        cuit: normalizeCuitValue(
          fichaBase?.header?.cuit ||
          clienteBase?.cuit ||
          valores["cuit/cuil"] ||
          valores.cuit ||
          prev.usuario.cuit
        ),
        nombre:
          sanitizeLegacyTextValue(
            fichaBase?.header?.razonSocial ||
            fichaBase?.header?.nombreDeFantasia ||
            clienteBase?.razonSocial ||
            clienteBase?.nombreDeFantasia ||
            valores["razon social"] ||
            valores["nombre de fantasia"]
          ) || prev.usuario.nombre,
        email:
          sanitizeLegacyTextValue(
            fichaBase?.header?.mail ||
            clienteBase?.mail ||
            valores.mail
          ) || prev.usuario.email,
      },
      legajo: {
        ...prev.legajo,
        nroLegajo:
          sanitizeLegacyNumericValue(
            fichaBase?.header?.nroLegajo ||
            clienteBase?.nroLegajo ||
            valores.legajo ||
            valores["nro legajo"] ||
            valores["n° legajo"]
          ) || prev.legajo.nroLegajo,
        nombreDeFantasia:
          sanitizeLegacyTextValue(fichaBase?.header?.nombreDeFantasia) ||
          sanitizeLegacyTextValue(valores["nombre de fantasia"]) ||
          prev.legajo.nombreDeFantasia,
        razonSocial:
          sanitizeLegacyTextValue(fichaBase?.header?.razonSocial) ||
          sanitizeLegacyTextValue(valores["razon social"]) ||
          prev.legajo.razonSocial,
        provincia: sanitizeLegacyTextValue(valores.provincia) || prev.legajo.provincia,
        calle: sanitizeLegacyTextValue(valores.domicilio) || prev.legajo.calle,
        localidad: sanitizeLegacyTextValue(valores.localidad) || prev.legajo.localidad,
        codigoPostal: sanitizeLegacyNumericValue(valores["codigo postal"]) || prev.legajo.codigoPostal,
        emailContacto: sanitizeLegacyTextValue(valores.mail) || prev.legajo.emailContacto,
        celularWhatsapp: normalizeEditablePhoneValue(valores["telefono 1"] || prev.legajo.celularWhatsapp),
        asesor: sanitizeLegacyTextValue(valores["nombre del asesor"]) || prev.legajo.asesor,
      },
      alta: {
        ...prev.alta,
        celular: normalizeEditablePhoneValue(valores["telefono 1"] || prev.alta.celular || prev.legajo.celularWhatsapp),
        referenciaTelefono: sanitizeLegacyTextValue(valores["puesto / cargo / referencia 1"] || prev.alta.referenciaTelefono),
        telefonoAlternativo: normalizeEditablePhoneValue(valores["telefono 2"] || prev.alta.telefonoAlternativo || prev.alta.telFijo),
        telFijo: normalizeEditablePhoneValue(valores["telefono 2"] || prev.alta.telFijo),
        referenciaTelefonoAlternativo: sanitizeLegacyTextValue(
          valores["puesto / cargo / referencia 2"] || prev.alta.referenciaTelefonoAlternativo
        ),
        correo: sanitizeLegacyTextValue(valores.mail) || prev.alta.correo || prev.legajo.emailContacto,
        apoderado: sanitizeLegacyTextValue(valores["puesto / cargo / referencia 1"] || prev.alta.apoderado),
        domicilioComercio: sanitizeLegacyTextValue(valores.domicilio) || prev.alta.domicilioComercio,
        localidadComercio: sanitizeLegacyTextValue(valores.localidad) || prev.alta.localidadComercio,
        codigoPostalComercio: sanitizeLegacyNumericValue(valores["codigo postal"]) || prev.alta.codigoPostalComercio,
        provincia: sanitizeLegacyTextValue(valores.provincia) || prev.alta.provincia,
        provincia3: sanitizeLegacyTextValue(valores["provincia fiscal"] || valores.provincia) || prev.alta.provincia3,
        linkDePago: sanitizeLegacyTextValue(valores["link de pagos"] || prev.alta.linkDePago),
        banco: sanitizeLegacyTextValue(valores.banco) || prev.alta.banco,
        tipoCuenta: sanitizeLegacyTextValue(valores["tipo de cuenta"] || prev.alta.tipoCuenta),
        cbucvu: sanitizeLegacyTextValue(valores["cbu/cvu"] || prev.alta.cbucvu),
        alias: sanitizeLegacyTextValue(valores.alias) || prev.alta.alias,
        cuitbancario:
          normalizeCuitValue(valores["cuit razon social"] || prev.alta.cuitbancario || prev.usuario.cuit) || prev.alta.cuitbancario,
        titularRazonSocial: sanitizeLegacyTextValue(valores["razon social"] || prev.alta.titularRazonSocial),
        numeroComericoActual:
          sanitizeLegacyTextValue(fichaBase?.header?.nDeComercio || clienteBase?.nDeComercio || prev.alta.numeroComericoActual),
        numeroComercioSolicitado:
          sanitizeLegacyTextValue(fichaBase?.header?.nDeComercio || clienteBase?.nDeComercio || prev.alta.numeroComercioSolicitado),
        rubro: sanitizeLegacyTextValue(valores.rubro) || prev.alta.rubro,
        rubroSecundario: sanitizeLegacyTextValue(valores["rubro secundario"] || prev.alta.rubroSecundario),
        codActividad: sanitizeLegacyNumericValue(valores["cod. actividad"] || prev.alta.codActividad),
        actividad: sanitizeLegacyTextValue(valores.actividad) || prev.alta.actividad,
        actividad2: sanitizeLegacyTextValue(valores["actividad 2"] || prev.alta.actividad2),
        estadoAfip: sanitizeLegacyTextValue(valores["estado afip"] || prev.alta.estadoAfip),
        exento: sanitizeLegacyTextValue(valores.exento) || prev.alta.exento,
        estadoRentas: sanitizeLegacyTextValue(valores["estado rentas"] || prev.alta.estadoRentas),
        codActividadRentas: sanitizeLegacyNumericValue(valores["cod actividad rentas"] || prev.alta.codActividadRentas),
        actividadRentas: sanitizeLegacyTextValue(valores["actividad rentas"] || prev.alta.actividadRentas),
        nombreDelAsesor: sanitizeLegacyTextValue(valores["nombre del asesor"] || prev.alta.nombreDelAsesor),
        alicuota:
          sanitizeLegacyTextValue(valores.alicuota) ||
          findProvinciaAlicuota(fichaCatalogos.provincias, sanitizeLegacyTextValue(valores["provincia fiscal"] || valores.provincia)),
      },
    }));
  }, [fichaCatalogos.provincias]);

  const cargarDocumentosLegajo = useCallback(async (legajoId) => {
    if (!legajoId) {
      setAltaLegajoDocs([]);
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      const data = await crmPostventaApi.listarDocumentosLegajo(legajoId);
      setAltaLegajoDocs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando documentos de legajo:", error);
      setAltaLegajoDocs([]);
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, []);

  const cargarContextoAlta = useCallback(async (usuarioId) => {
    setCargas((prev) => ({ ...prev, altaBajaCatalogos: true }));

    try {
      const todosLegajos = await crmPostventaApi.obtenerLegajos();
      const legajosUsuario = usuarioId
        ? await crmPostventaApi.obtenerLegajosPorUsuario(usuarioId).catch(() => [])
        : [];

      const legajosUsuarioLista = Array.isArray(legajosUsuario) ? legajosUsuario : [];
      setAltaWizardLegajos(legajosUsuarioLista);

      if (legajosUsuarioLista.length > 0) {
        const legajoPrioritario = [...legajosUsuarioLista].sort(
          (left, right) =>
            Number(right?.nroLegajo ?? right?.NroLegajo ?? 0) -
            Number(left?.nroLegajo ?? left?.NroLegajo ?? 0)
        )[0];
        aplicarLegajoAlWizard(legajoPrioritario);
        await cargarDocumentosLegajo(legajoPrioritario?.id ?? legajoPrioritario?.Id ?? 0);
      } else {
        setAltaWizardData((prev) => ({
          ...prev,
          legajoId: null,
          legajo: {
            ...prev.legajo,
            nroLegajo: nextLegajoSuggestion(Array.isArray(todosLegajos) ? todosLegajos : []),
          },
        }));
      }
    } catch (error) {
      console.error("Error cargando contexto de alta:", error);
      Swal.fire("Altas / Bajas", error.message || "No se pudo preparar el contexto del legajo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, altaBajaCatalogos: false }));
    }
  }, [aplicarLegajoAlWizard, cargarDocumentosLegajo]);

  const handleAltaWizardUserChange = useCallback((field, value) => {
    setAltaWizardData((prev) => ({
      ...prev,
      usuario: {
        ...prev.usuario,
        [field]: value,
      },
    }));
  }, []);

  const handleAltaWizardLegajoChange = useCallback((field, value) => {
    setAltaWizardData((prev) => ({
      ...prev,
      legajo: {
        ...prev.legajo,
        [field]: value,
      },
      alta: field === "emailContacto"
        ? { ...prev.alta, correo: prev.alta.correo || value }
        : field === "celularWhatsapp"
          ? { ...prev.alta, celular: prev.alta.celular || value }
          : field === "provincia"
            ? {
                ...prev.alta,
                provincia: prev.alta.provincia || value,
                provincia3: prev.alta.provincia3 || value,
                alicuota: prev.alta.alicuota || findProvinciaAlicuota(fichaCatalogos.provincias, value),
              }
            : field === "asesor"
              ? { ...prev.alta, nombreDelAsesor: prev.alta.nombreDelAsesor || value }
          : prev.alta,
    }));
  }, [fichaCatalogos.provincias]);

  const handleAltaWizardAltaChange = useCallback((field, value) => {
    setAltaWizardData((prev) => {
      const nextAlta = {
        ...prev.alta,
        [field]: value,
      };

      if (field === "tipoCuenta") {
        nextAlta.cbucvu = value === "Billetera Virtual" ? "CVU" : value ? "CBU" : "";
      }

      if (field === "condicionAFIP") {
        nextAlta.estadoAfip = mapCondicionAfipToEstado(value);
        if (String(value || "").toLowerCase() === "monotributista") {
          nextAlta.exento = "NO";
        }
      }

      if (field === "estadoAfip") {
        nextAlta.condicionAFIP = mapEstadoAfipToCondicion(value);
        if (String(value || "").toUpperCase() === "MONOTRIBUTISTA") {
          nextAlta.exento = "NO";
        }
      }

      if (field === "tipoAlta") {
        nextAlta.altas = LEGACY_DEFAULT_ALTAS_BY_TIPO[value] || nextAlta.altas || "ALTA NUEVA";
      }

      if (field === "provincia") {
        nextAlta.provincia3 = nextAlta.provincia3 || value;
      }

      if (field === "provincia" || field === "provincia3") {
        nextAlta.alicuota = findProvinciaAlicuota(
          fichaCatalogos.provincias,
          field === "provincia3" ? value : nextAlta.provincia3 || value
        );
      }

      return {
        ...prev,
        agregaBaseClienteId:
          field === "tipoAlta" && value !== "AgregaTerminal"
            ? null
            : prev.agregaBaseClienteId,
        alta: nextAlta,
      };
    });
  }, [fichaCatalogos.provincias]);

  const buscarBaseAgrega = useCallback(async () => {
    const needle = String(altaAgregaSearch || "").trim();
    if (!needle) {
      setAltaAgregaResults([]);
      return;
    }

    setAltaAgregaLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(needle);
      setAltaAgregaResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error buscando base para AGREGA:", error);
      setAltaAgregaResults([]);
      Swal.fire("Paso 3", error.message || "No se pudo buscar el comercio o terminal base.", "error");
    } finally {
      setAltaAgregaLoading(false);
    }
  }, [altaAgregaSearch]);

  const seleccionarBaseAgrega = useCallback(async (clienteBase) => {
    if (!clienteBase?.clienteId) {
      return;
    }

    setAltaAgregaLoading(true);
    try {
      const fichaBase = await crmPostventaApi.obtenerFicha(clienteBase.clienteId);
      aplicarBaseAgregaDesdeFicha(clienteBase, fichaBase);
      setAltaAgregaSearch(
        [clienteBase.nombreDeFantasia || clienteBase.razonSocial, clienteBase.nDeComercio && `Comercio ${clienteBase.nDeComercio}`]
          .filter(Boolean)
          .join(" | ")
      );
      setAltaAgregaResults([]);
      Swal.fire("Paso 3", "Se copiaron los datos fijos del comercio/terminal seleccionado.", "success");
    } catch (error) {
      console.error("Error copiando base AGREGA:", error);
      Swal.fire("Paso 3", error.message || "No se pudieron copiar los datos del comercio base.", "error");
    } finally {
      setAltaAgregaLoading(false);
    }
  }, [aplicarBaseAgregaDesdeFicha]);

  const handleAltaBlanqueoSubmit = useCallback(async (event) => {
    event.preventDefault();

    const cuit = normalizeCuitValue(altaWizardData.usuario.cuit);
    if (!cuit || !altaWizardData.usuario.nombre.trim() || !altaWizardData.usuario.email.trim()) {
      Swal.fire("Paso 1", "Completá CUIT, nombre y email para continuar.", "info");
      return;
    }

    if (cuit.length !== 11) {
      Swal.fire("Paso 1", "El CUIT/CUIL debe tener 11 dÃ­gitos. Se aceptan formatos con guiones y se normalizan automÃ¡ticamente.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoBlanqueo: true }));
    try {
      let usuarioPayload = null;

      try {
        usuarioPayload = await crmPostventaApi.buscarUsuarioPorCuit(cuit);
      } catch {
        usuarioPayload = null;
      }

      if (usuarioPayload?.id) {
        const actualizado = await crmPostventaApi.editarUsuarioAliado({
          Id: usuarioPayload.id,
          Usuario: cuit,
          Nombre: altaWizardData.usuario.nombre.trim(),
          Correo: altaWizardData.usuario.email.trim(),
          TipoUsuario: 0,
        });

        setAltaWizardData((prev) => ({
          ...prev,
          usuarioId: actualizado?.id ?? usuarioPayload.id,
          usuario: {
            ...prev.usuario,
            cuit,
            nombre: altaWizardData.usuario.nombre.trim(),
            email: altaWizardData.usuario.email.trim(),
            tipoUsuario: 0,
          },
        }));
        setAltaWizardStatus((prev) => ({
          ...prev,
          blanqueo: "Usuario existente actualizado y reutilizado.",
        }));
        await cargarContextoAlta(actualizado?.id ?? usuarioPayload.id);
      } else {
        const creado = await crmPostventaApi.crearUsuarioAliado({
          Usuario: cuit,
          Nombre: altaWizardData.usuario.nombre.trim(),
          Correo: altaWizardData.usuario.email.trim(),
          TipoUsuario: 0,
        });

        setAltaWizardData((prev) => ({
          ...prev,
          usuarioId: creado?.id ?? null,
          usuario: {
            ...prev.usuario,
            cuit,
            nombre: altaWizardData.usuario.nombre.trim(),
            email: altaWizardData.usuario.email.trim(),
            tipoUsuario: 0,
          },
        }));
        setAltaWizardStatus((prev) => ({
          ...prev,
          blanqueo: "Usuario creado correctamente desde el paso de blanqueo.",
        }));
        await cargarContextoAlta(creado?.id ?? null);
      }

      setAltaWizardStep(2);
    } catch (error) {
      console.error("Error guardando blanqueo:", error);
      Swal.fire("Paso 1", error.message || "No se pudo guardar el blanqueo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoBlanqueo: false }));
    }
  }, [altaWizardData.usuario, cargarContextoAlta]);

  const handleAltaLegajoSelect = useCallback((event) => {
    const selectedId = Number(event.target.value || 0);
    if (!selectedId) {
      setAltaWizardData((prev) => ({
        ...prev,
        legajoId: null,
        legajo: {
          ...prev.legajo,
          nroLegajo: prev.legajo.nroLegajo || nextLegajoSuggestion(altaWizardLegajos),
        },
      }));
      setAltaLegajoDocs([]);
      return;
    }

    const selected = altaWizardLegajos.find((item) => Number(item?.id ?? item?.Id ?? 0) === selectedId);
    if (selected) {
      aplicarLegajoAlWizard(selected);
      cargarDocumentosLegajo(selectedId);
    }
  }, [altaWizardLegajos, aplicarLegajoAlWizard, cargarDocumentosLegajo]);

  const handleAltaLegajoSubmit = useCallback(async (event) => {
    event.preventDefault();

    const { legajo, usuarioId } = altaWizardData;
    if (!usuarioId) {
      Swal.fire("Paso 2", "Primero completá el blanqueo del usuario.", "info");
      return;
    }

    if (!legajo.nroLegajo || !legajo.terminal || !legajo.nombreDeFantasia || !legajo.provincia) {
      Swal.fire("Paso 2", "Completá al menos número de legajo, terminal, nombre de fantasía y provincia.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoLegajo: true }));
    try {
      const payload = {
        Id: altaWizardData.legajoId || undefined,
        NroLegajo: Number(legajo.nroLegajo),
        Terminal: legajo.terminal,
        NombreDeFantasia: legajo.nombreDeFantasia,
        Provincia: legajo.provincia,
        TipoPersona: legajo.tipoPersona,
        CuitTitularOSociedad: altaWizardData.usuario.cuit,
        RazonSocial: legajo.razonSocial,
        Calle: legajo.calle,
        Numero: legajo.numero,
        PisoDepto: legajo.pisoDepto,
        Localidad: legajo.localidad,
        CodigoPostal: legajo.codigoPostal,
        EmailContacto: legajo.emailContacto || altaWizardData.usuario.email,
        CelularWhatsapp: legajo.celularWhatsapp,
        Asesor: legajo.asesor,
        Iduser: usuarioId,
      };

      const response = altaWizardData.legajoId
        ? await crmPostventaApi.editarLegajo(payload)
        : await crmPostventaApi.crearLegajo(payload);

      const legajoId = response?.idLegajo ?? response?.id ?? response?.Id ?? altaWizardData.legajoId;
      setAltaWizardData((prev) => ({
        ...prev,
        legajoId,
      }));
      setAltaWizardStatus((prev) => ({
        ...prev,
        legajo: altaWizardData.legajoId
          ? "Legajo actualizado correctamente."
          : "Legajo creado correctamente. Ya podés cargar documentos o continuar.",
      }));

      await cargarContextoAlta(usuarioId);
      await cargarDocumentosLegajo(legajoId);
      setAltaWizardStep(3);
    } catch (error) {
      console.error("Error guardando legajo:", error);
      Swal.fire("Paso 2", error.message || "No se pudo guardar el legajo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoLegajo: false }));
    }
  }, [altaWizardData, cargarContextoAlta, cargarDocumentosLegajo]);

  const handleAltaLegajoDocumento = useCallback(async (event) => {
    event.preventDefault();

    if (!altaWizardData.legajoId) {
      Swal.fire("Documentos", "Guardá primero el legajo para subir archivos.", "info");
      return;
    }

    if (!altaLegajoDocDraft.file) {
      Swal.fire("Documentos", "Seleccioná un archivo para subir.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      const archivoBase64 = await fileToDataUrl(altaLegajoDocDraft.file);
      await crmPostventaApi.subirDocumentoLegajo({
        IdLegajo: altaWizardData.legajoId,
        NombreDocumento: altaLegajoDocDraft.nombreDocumento,
        DocumentoTipo: getFileExtension(altaLegajoDocDraft.file) || null,
        ArchivoBase64: archivoBase64,
      });

      setAltaLegajoDocDraft({
        nombreDocumento: ALTA_LEGAJO_DOC_TYPES[0],
        file: null,
      });
      await cargarDocumentosLegajo(altaWizardData.legajoId);
      Swal.fire("Documentos", "Archivo cargado correctamente.", "success");
    } catch (error) {
      console.error("Error subiendo documento:", error);
      Swal.fire("Documentos", error.message || "No se pudo subir el archivo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, [altaLegajoDocDraft, altaWizardData.legajoId, cargarDocumentosLegajo]);

  const handleEliminarDocumentoLegajo = useCallback(async (idDocumento) => {
    if (!altaWizardData.legajoId || !idDocumento) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: true }));
    try {
      await crmPostventaApi.eliminarDocumentoLegajo(idDocumento);
      await cargarDocumentosLegajo(altaWizardData.legajoId);
    } catch (error) {
      console.error("Error eliminando documento:", error);
      Swal.fire("Documentos", error.message || "No se pudo eliminar el archivo.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoDocumentoLegajo: false }));
    }
  }, [altaWizardData.legajoId, cargarDocumentosLegajo]);

  const asegurarTerminalBaseDisponible = useCallback(async (terminal) => {
    const terminalNormalizada = normalizeDigits(terminal);
    if (!terminalNormalizada) {
      throw new Error("El número de terminal es obligatorio.");
    }

    const candidatos = await crmPostventaApi.buscarClientes(terminalNormalizada);
    const existeTerminal = Array.isArray(candidatos) && candidatos.some(
      (item) => normalizeDigits(item?.nDeTerminal || item?.NDeTerminal) === terminalNormalizada
    );

    if (!existeTerminal) {
      await crmPostventaApi.crearTerminalBaseAlta({
        NumeroTerminalAlta: terminalNormalizada,
        Observacion: "Registro base creado automáticamente desde CRM Postventa > Altas / Bajas.",
      });
    }
  }, []);

  const handleAltaWorkflowSubmit = useCallback(async (event) => {
    event.preventDefault();

    const token = getToken();
    if (!token) {
      Swal.fire("Alta CRM", "No hay sesión activa.", "error");
      return;
    }

    if (!(String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" || String(altaWizardData.alta.altas || "") === "AGREGA") && !altaWizardData.legajoId) {
      Swal.fire("Alta CRM", "Primero guardá el legajo.", "info");
      return;
    }

    const isAgregaMode =
      String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" ||
      String(altaWizardData.alta.altas || "") === "AGREGA";

    if (false && !isAgregaMode && !altaWizardData.legajoId) {
      Swal.fire("Alta CRM", "Primero guardÃ¡ el legajo.", "info");
      return;
    }

    if (isAgregaMode && !altaWizardData.agregaBaseClienteId) {
      Swal.fire("Alta CRM", "SeleccionÃ¡ primero el comercio o terminal base para copiar los datos fijos.", "info");
      return;
    }

    if (isAgregaMode && !sanitizeLegacyNumericValue(altaWizardData.legajo.nroLegajo)) {
      Swal.fire("Alta CRM", "No se pudo recuperar el nÃºmero de legajo base del comercio seleccionado.", "info");
      return;
    }

    const cuitTitular = normalizeCuitValue(altaWizardData.usuario.cuit);
    const cuitBancario = normalizeCuitValue(altaWizardData.alta.cuitbancario || cuitTitular);
    if (cuitTitular.length !== 11) {
      Swal.fire("Paso 3", "El CUIT/CUIL del titular debe tener 11 dÃ­gitos. Se aceptan formatos con guiones y se normalizan antes de enviar.", "info");
      return;
    }

    if (cuitBancario && cuitBancario.length !== 11) {
      Swal.fire("Paso 3", "El CUIT bancario debe tener 11 dÃ­gitos.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoAltaWorkflow: true }));
    try {
      await asegurarTerminalBaseDisponible(altaWizardData.legajo.terminal);

      const formularioPayload = buildAltaWizardFormPayload(altaWizardData, token);
      const missingFields = buildAltaMissingFields(altaWizardData, formularioPayload);
      if (missingFields.length) {
        await Swal.fire({
          title: "Paso 3",
          html: formatMissingFieldsHtmlList(missingFields),
          icon: "info",
        });
        return;
      }

      const formularioCreado = await crmPostventaApi.crearFormularioAlta(formularioPayload);
      const formularioId = Number(formularioCreado?.id || 0);

      if (!formularioId) {
        throw new Error("No se pudo obtener el identificador del formulario de alta.");
      }

      await crmPostventaApi.crearAltaCrm({
        id: formularioId,
        token,
        NroLocal: sanitizeLegacyNumericValue(altaWizardData.alta.nroLocal) || "1",
        Legajo: sanitizeLegacyNumericValue(altaWizardData.legajo.nroLegajo),
        CodActividad: sanitizeLegacyNumericValue(altaWizardData.alta.codActividad),
        Rubro: sanitizeLegacyTextValue(altaWizardData.alta.rubro),
        RubroSecundario: sanitizeLegacyTextValue(altaWizardData.alta.rubroSecundario),
        Actividad: sanitizeLegacyTextValue(altaWizardData.alta.actividad),
        Actividad2: sanitizeLegacyTextValue(altaWizardData.alta.actividad2),
        CodActividadRentas: sanitizeLegacyNumericValue(altaWizardData.alta.codActividadRentas),
        ActividadRentas: sanitizeLegacyTextValue(altaWizardData.alta.actividadRentas),
        Observaciones: sanitizeLegacyTextValue(altaWizardData.alta.observaciones),
        Estado: sanitizeLegacyTextValue(altaWizardData.alta.estado) || "ALTA",
        LinkDePago: sanitizeLegacyTextValue(altaWizardData.alta.linkDePago) || "NO",
        NombreDelAsesor: sanitizeLegacyTextValue(altaWizardData.alta.nombreDelAsesor || altaWizardData.legajo.asesor),
        EstadoAfip: sanitizeLegacyTextValue(
          altaWizardData.alta.estadoAfip || mapCondicionAfipToEstado(altaWizardData.alta.condicionAFIP)
        ),
        Exento: sanitizeLegacyTextValue(altaWizardData.alta.exento) || "NO",
        EstadoRentas: sanitizeLegacyTextValue(altaWizardData.alta.estadoRentas),
        Provincia3: sanitizeLegacyTextValue(
          altaWizardData.alta.provincia3 || altaWizardData.alta.provincia || altaWizardData.legajo.provincia
        ),
        Altas:
          sanitizeLegacyTextValue(altaWizardData.alta.altas) ||
          LEGACY_DEFAULT_ALTAS_BY_TIPO[altaWizardData.alta.tipoAlta] ||
          "ALTA NUEVA",
        Baja: sanitizeLegacyTextValue(altaWizardData.alta.baja),
      });

      try {
        const candidatosAlta = await crmPostventaApi.buscarClientes(altaWizardData.legajo.terminal);
        const altaCreada = (Array.isArray(candidatosAlta) ? candidatosAlta : []).find(
          (item) =>
            normalizeDigits(item?.nDeTerminal || item?.NDeTerminal) === normalizeDigits(altaWizardData.legajo.terminal) &&
            String(item?.estado || item?.Estado || "").toUpperCase() === "ALTA"
        );

        if (altaCreada?.clienteId) {
          await crmPostventaApi.actualizarFicha(altaCreada.clienteId, {
            Telefono: normalizeEditablePhoneValue(altaWizardData.alta.celular || altaWizardData.legajo.celularWhatsapp),
            ReferenciaTelefono: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefono || altaWizardData.alta.apoderado),
            TelefonoAlternativo: normalizeEditablePhoneValue(altaWizardData.alta.telefonoAlternativo || altaWizardData.alta.telFijo),
            ReferenciaTelefonoAlternativo: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefonoAlternativo),
            ContactoAlternativo: sanitizeLegacyTextValue(altaWizardData.alta.referenciaTelefonoAlternativo),
          });
        }
      } catch (errorContacto) {
        console.error("Error sincronizando contactos post alta:", errorContacto);
      }

      setAltaWizardData((prev) => ({
        ...prev,
        formularioAltaId: formularioId,
      }));
      setAltaWizardStatus((prev) => ({
        ...prev,
        alta: "Alta CRM generada correctamente reutilizando formulario alta + alta final legacy.",
      }));
      Swal.fire("Alta CRM", "El alta se generó correctamente dentro del flujo guiado.", "success");
      setAltaWizardStep(3);
    } catch (error) {
      console.error("Error generando alta CRM:", error);
      Swal.fire("Alta CRM", error.message || "No se pudo completar el alta.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoAltaWorkflow: false }));
    }
  }, [altaWizardData, asegurarTerminalBaseDisponible]);

  const buscarOpcionesBaja = useCallback(async (searchValue) => {
    const normalizedSearch = String(searchValue || "").trim();
    if (!normalizedSearch) {
      setBajaLookupResults([]);
      return;
    }

    setBajaLookupLoading(true);
    try {
      const data = await crmPostventaApi.buscarClientes(normalizedSearch);
      setBajaLookupResults(normalizeBajaLookupResults(data));
    } catch (error) {
      console.error("Error buscando terminal/comercio para baja:", error);
      setBajaLookupResults([]);
      Swal.fire("Baja por terminal", error.message || "No se pudo buscar la terminal o el comercio.", "error");
    } finally {
      setBajaLookupLoading(false);
    }
  }, []);

  const handleSeleccionBajaResultado = useCallback((event) => {
    const selected = bajaLookupResults.find((item) => item.key === event.target.value);
    if (!selected) {
      return;
    }

    setBajaDraft((prev) => ({
      ...prev,
      terminal: selected.terminal || prev.terminal,
    }));
  }, [bajaLookupResults]);

  const handleBajaWorkflowSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!bajaDraft.terminal || !bajaDraft.observacion.trim()) {
      Swal.fire("Baja por terminal", "Indicá terminal y observación para continuar.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: true }));
    try {
      await crmPostventaApi.ejecutarBajaTerminal({
        NumeroTerminalBaja: normalizeDigits(bajaDraft.terminal),
        Motivo: bajaDraft.motivo,
        Observacion: bajaDraft.observacion.trim(),
        FechaBaja: bajaDraft.fechaBaja,
      });

      Swal.fire("Baja por terminal", "La baja se registró correctamente usando la lógica legacy.", "success");
      setBajaDraft(emptyBajaDraft());
      setBajaLookupSearch("");
      setBajaLookupResults([]);
    } catch (error) {
      console.error("Error ejecutando baja:", error);
      Swal.fire("Baja por terminal", error.message || "No se pudo registrar la baja.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoBajaWorkflow: false }));
    }
  }, [bajaDraft]);

  useEffect(() => {
    validarSesion();
  }, [validarSesion]);

  useEffect(() => {
    let isMounted = true;

    const loadFichaCatalogos = async () => {
      setFichaCatalogosLoading(true);
      try {
        const data = await fetchFichaCatalogosV2();
        if (isMounted) {
          setFichaCatalogos(data);
        }
      } catch (error) {
        console.error("Error cargando catálogos de edición:", error);
      } finally {
        if (isMounted) {
          setFichaCatalogosLoading(false);
        }
      }
    };

    loadFichaCatalogos();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!clienteModalOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cargarClientes(clienteSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [clienteModalOpen, clienteSearch, cargarClientes]);

  useEffect(() => {
    if (!clienteModalOpen || clientePendiente?.groupKey || !clienteGrupos.length || !currentGroupKey) {
      return;
    }

    const grupoActual = clienteGrupos.find((item) => item.groupKey === currentGroupKey);
    if (grupoActual) {
      setClientePendiente(grupoActual);
    }
  }, [clienteGrupos, clienteModalOpen, clientePendiente?.groupKey, currentGroupKey]);

  useEffect(() => {
    if (!cargandoSesion && clienteSeleccionadoId) {
      cargarModulo(clienteSeleccionadoId, analyticsRange);
    }
  }, [analyticsRange, cargarModulo, cargandoSesion, clienteSeleccionadoId]);

  useEffect(() => {
    if (!clienteSeleccionadoCuit) {
      return;
    }

    setOrdenesPagoFiltros((prev) =>
      prev.search === clienteSeleccionadoCuit
        ? prev
        : {
            ...prev,
            search: clienteSeleccionadoCuit,
          }
    );
  }, [clienteSeleccionadoCuit, clienteSeleccionadoId]);

  useEffect(() => {
    setInactivosFiltros((prev) =>
      prev.fecha === alertasFiltros.fecha
        ? prev
        : {
            ...prev,
            fecha: alertasFiltros.fecha,
          }
    );
  }, [alertasFiltros.fecha]);

  useEffect(() => {
    if (cargandoSesion || section !== "alertas") {
      return;
    }

    cargarAlertas(alertasFiltros);
  }, [
    alertasFiltros.estado,
    alertasFiltros.fecha,
    alertasFiltros.prioridad,
    alertasFiltros.provincia,
    alertasFiltros.responsable,
    alertasFiltros.tipo,
    cargarAlertas,
    cargandoSesion,
    section,
  ]);

  useEffect(() => {
    if (cargandoSesion || section !== "alertas") {
      return;
    }

    cargarInactivos(inactivosFiltros);
  }, [
    cargarInactivos,
    cargandoSesion,
    inactivosFiltros.diasSinOperar,
    inactivosFiltros.fecha,
    inactivosFiltros.provincia,
    inactivosFiltros.responsable,
    section,
  ]);

  useEffect(() => {
    if (cargandoSesion || section !== "ordenes-pago" || !clienteSeleccionadoId) {
      return;
    }

    cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
  }, [cargandoSesion, cargarOrdenesPago, clienteSeleccionadoId, section]);

  useEffect(() => {
    if (cargandoSesion || section !== "cupones" || !clienteSeleccionadoId) {
      return;
    }

    cargarCupones(clienteSeleccionadoId, cuponesFiltros);
  }, [cargandoSesion, cargarCupones, clienteSeleccionadoId, section]);

  const clienteHeader = ficha?.header || {
    clienteKey: clientePendiente?.clienteKey || "",
    nombreDeFantasia: clientePendiente?.nombreDeFantasia || "Sin cliente seleccionado",
    razonSocial: clientePendiente?.razonSocial || "",
    cuit: clientePendiente?.cuit || "",
    estado: clientePendiente?.estado || "",
    producto: clientePendiente?.producto || "",
    nDeTerminal: clientePendiente?.nDeTerminal || "",
    nDeComercio: clientePendiente?.nDeComercio || "",
    legajo: clientePendiente?.legajo || "",
  };

  const handleCambiarTerminal = useCallback((terminal) => {
    if (!terminal?.clienteId || terminal.clienteId === clienteSeleccionadoId) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(terminal.clienteId));
    setClienteSeleccionadoId(terminal.clienteId);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    setOrdenesPago(null);
    setCupones(null);
  }, [clienteSeleccionadoId]);

  const filasFacturacion = useMemo(() => {
    const allRows = Array.isArray(facturacion?.filas) ? facturacion.filas : [];
    if (!facturacionFiltroComercio) {
      return allRows;
    }

    return allRows.filter(
      (row) =>
        row.terminal === facturacionFiltroComercio ||
        row.comercio === facturacionFiltroComercio
    );
  }, [facturacion?.filas, facturacionFiltroComercio]);

  const filterAlertRowsBySearch = useCallback((rows, searchValue) => {
    const filas = Array.isArray(rows) ? rows : [];
    const needle = String(searchValue || "").trim().toLowerCase();

    if (!needle) {
      return filas;
    }

    return filas.filter((item) =>
      [
        item?.comercio,
        item?.cuit,
        item?.terminal,
        item?.provincia,
        item?.tipoAlerta,
        item?.responsable,
        item?.estado,
        item?.resumen,
        item?.telefono,
        item?.ultimaGestion,
        item?.proximoContacto,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, []);

  const alertasFacturacionFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasFacturacion, alertasFacturacionSearch),
    [alertas?.alertasFacturacion, alertasFacturacionSearch, filterAlertRowsBySearch]
  );

  const alertasZocoCheckFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasZocoCheck, alertasZocoCheckSearch),
    [alertas?.alertasZocoCheck, alertasZocoCheckSearch, filterAlertRowsBySearch]
  );

  const alertasTerminalExtraviadaFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.alertasTerminalExtraviada, alertasTerminalExtraviadaSearch),
    [alertas?.alertasTerminalExtraviada, alertasTerminalExtraviadaSearch, filterAlertRowsBySearch]
  );

  const alertasOperativasFiltradas = useMemo(
    () => filterAlertRowsBySearch(alertas?.filas, alertasOperativasSearch),
    [alertas?.filas, alertasOperativasSearch, filterAlertRowsBySearch]
  );

  const inactivosFiltrados = useMemo(() => {
    const filas = Array.isArray(inactivos?.filas) ? inactivos.filas : [];
    const needle = String(inactivosSearchDraft || "").trim().toLowerCase();

    if (!needle) {
      return filas;
    }

    return filas.filter((item) =>
      [
        item?.estado,
        item?.cuit,
        item?.comercio,
        item?.razonSocial,
        item?.terminal,
        item?.provincia,
        item?.telefono,
        item?.ultimaFechaFacturacion,
        item?.usuarioResponsable,
        item?.ultimaGestion,
        item?.proximoContacto,
        item?.accionSugerida,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [inactivos?.filas, inactivosSearchDraft]);

  const inactivosProvinciaOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(inactivos?.filas) ? inactivos.filas : [])
            .map((item) => String(item?.provincia || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "es")),
    [inactivos?.filas]
  );

  const fichaCamposEdicion = useMemo(
    () => Array.isArray(ficha?.edicion?.campos) ? ficha.edicion.campos : [],
    [ficha?.edicion?.campos]
  );

  const altaWizardComercioOptions = useMemo(
    () =>
      dedupeOptions(
        fichaCatalogos.comercios.map((item) =>
          normalizeOption(item.label || item.value, item.label || item.value, {
            numero: item.value,
          })
        )
      ),
    [fichaCatalogos.comercios]
  );
  const isAltaAgregaMode = useMemo(
    () =>
      String(altaWizardData.alta.tipoAlta || "") === "AgregaTerminal" ||
      String(altaWizardData.alta.altas || "") === "AGREGA",
    [altaWizardData.alta.altas, altaWizardData.alta.tipoAlta]
  );

  const fichaCamposAgrupados = useMemo(
    () => sortFichaSections(groupFichaFieldsBySection(fichaCamposEdicion)),
    [fichaCamposEdicion]
  );

  const abrirEdicionFicha = useCallback(() => {
    setFichaEditDraft(buildFichaEditDraft(fichaCamposEdicion));
    setFichaEditOpen(true);
  }, [fichaCamposEdicion]);

  const guardarEdicionFicha = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoFicha: true }));
    try {
      const resultado = await crmPostventaApi.actualizarFicha(
        clienteSeleccionadoId,
        mapFichaDraftToPayload(fichaEditDraft)
      );

      setFichaEditOpen(false);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      const warningText = Array.isArray(resultado?.warnings) && resultado.warnings.length
        ? `\n\n${resultado.warnings.join("\n")}`
        : "";

      Swal.fire("Ficha", `${resultado?.message || "Datos actualizados correctamente."}${warningText}`, "success");
    } catch (error) {
      console.error("Error guardando ficha:", error);
      Swal.fire("Error", error.message || "No se pudieron actualizar los datos del cliente.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoFicha: false }));
    }
  };

  const handleFichaFieldChange = useCallback((field, nextValue) => {
    setFichaEditDraft((prev) => applyFichaFieldSideEffects(prev, field.key, nextValue, fichaCatalogos));
  }, [fichaCatalogos]);

  const renderFichaEditField = useCallback((field) => {
    const value = fichaEditDraft[field.key] ?? "";
    const baseOptions = buildFichaCatalogOptions(fichaCatalogos, field);
    const options = value && !baseOptions.some((option) => option.value === value)
      ? [...baseOptions, normalizeOption(value, value)]
      : baseOptions;
    const isReadOnly = !field.editable || FICHA_READONLY_KEYS.has(field.key);
    const source = field.dataSource || FICHA_SELECT_KEY_TO_SOURCE[field.key];

    if (field.inputType === "select") {
      return (
        <div className={`crm-postventa-select-control ${isReadOnly ? "is-readonly" : ""}`}>
          <Form.Select
            className={`crm-postventa-input ${isReadOnly ? "is-readonly" : ""}`}
            value={value}
            disabled={isReadOnly}
            onChange={(event) => handleFichaFieldChange(field, event.target.value)}
          >
            <option value="">{source === "estado" && !field.editable ? "Sin valor" : "Seleccionar"}</option>
            {options.map((option) => (
              <option key={`${field.key}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <span className="crm-postventa-select-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </div>
      );
    }

    const renderAlertaOperativaTable = (title, rows, total, searchValue, onSearchChange, emptyMessage) => (
      <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
        <div className="crm-postventa-panel-title">
          <h3>{title}</h3>
          <span className="crm-postventa-card-label">Mostrando {rows.length} de {total} alerta(s)</span>
        </div>
        <div className="crm-postventa-alert-queue-filters">
          <Form.Control
            type="search"
            className="crm-postventa-filter-control"
            placeholder={`Buscar en ${title.toLowerCase()} por comercio, CUIT, terminal, responsable o teléfono`}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <div className="crm-postventa-alert-queue-scroll">
          <div className="crm-postventa-alert-queue-list">
            <Table className="crm-postventa-table crm-postventa-table-alertas mb-0" hover>
              <thead className="table-light">
                <tr>
                  <th>Prioridad / estado</th>
                  <th>Comercio</th>
                  <th>Alerta</th>
                  <th>Seguimiento</th>
                  <th className="crm-postventa-action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((item, index) => (
                  <tr key={`${title}-${item.cuit}-${item.terminal}-${item.tipoAlerta}-${index}`}>
                    <td data-label="Prioridad / estado">
                      <div className="crm-postventa-alert-table-stack">
                        <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                        <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                        <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                      </div>
                    </td>
                    <td className="crm-postventa-alert-table-commerce" data-label="Comercio">
                      <strong className="crm-postventa-alert-table-title">{item.comercio || "Sin comercio"}</strong>
                      <div className="crm-postventa-inline-badges">
                        <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                        <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                      </div>
                      <div className="crm-postventa-alert-table-subline">CUIT: {item.cuit || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Terminal: {item.terminal || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Provincia: {item.provincia || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Teléfono: {item.telefono || "-"}</div>
                    </td>
                    <td className="crm-postventa-alert-table-detail" data-label="Alerta">
                      <strong className="crm-postventa-alert-table-title">{item.tipoAlerta || "-"}</strong>
                      <div className="crm-postventa-alert-table-text">{item.resumen || "Sin descripción disponible."}</div>
                      <div className="crm-postventa-alert-table-subline">Motivo código: {item.motivoCodigo || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Acción sugerida: {item.accionSugerida || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Mensaje sugerido: {item.mensajeSugerido || "-"}</div>
                      {(item.motivoCodigo === "FALTA_FACTURACION" || item.motivoCodigo === "CAIDA_FACTURACION") ? (
                        <>
                          <div className="crm-postventa-alert-table-subline">Hoy: {currency(item.montoHoy)}</div>
                          <div className="crm-postventa-alert-table-subline">Prom. 7 días: {currency(item.promedio7Dias)}</div>
                          <div className="crm-postventa-alert-table-subline">Variación: {percent(item.variacion)}</div>
                        </>
                      ) : null}
                    </td>
                    <td data-label="Seguimiento">
                      <div className="crm-postventa-alert-table-subline">Responsable: {item.responsable || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Último contacto: {item.ultimaGestion || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Próximo contacto: {item.proximoContacto || "-"}</div>
                    </td>
                    <td className="crm-postventa-action-cell" data-label="Acciones">
                      <Button size="sm" variant="outline-secondary" className="crm-postventa-action-button" onClick={() => setAlertaAccionItem(item)}>
                        Acciones
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">{emptyMessage}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </section>
    );

    const renderAlertaOperativaTableInline = (title, rows, total, searchValue, onSearchChange, emptyMessage) => (
      <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
        <div className="crm-postventa-panel-title">
          <h3>{title}</h3>
          <span className="crm-postventa-card-label">Mostrando {rows.length} de {total} alerta(s)</span>
        </div>
        <div className="crm-postventa-alert-queue-filters">
          <Form.Control
            type="search"
            className="crm-postventa-filter-control"
            placeholder={`Buscar en ${title.toLowerCase()} por comercio, CUIT, terminal, responsable o teléfono`}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
        <div className="crm-postventa-alert-queue-scroll">
          <div className="crm-postventa-alert-queue-list">
            <Table className="crm-postventa-table crm-postventa-table-alertas mb-0" hover>
              <thead className="table-light">
                <tr>
                  <th>Prioridad / estado</th>
                  <th>Comercio</th>
                  <th>Alerta</th>
                  <th>Seguimiento</th>
                  <th className="crm-postventa-action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((item, index) => (
                  <tr key={`${title}-${item.cuit}-${item.terminal}-${item.tipoAlerta}-${index}`}>
                    <td data-label="Prioridad / estado">
                      <div className="crm-postventa-alert-table-stack">
                        <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                        <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                        <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                      </div>
                    </td>
                    <td className="crm-postventa-alert-table-commerce" data-label="Comercio">
                      <strong className="crm-postventa-alert-table-title">{item.comercio || "Sin comercio"}</strong>
                      <div className="crm-postventa-inline-badges">
                        <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                        <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                      </div>
                      <div className="crm-postventa-alert-table-subline">CUIT: {item.cuit || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Terminal: {item.terminal || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Provincia: {item.provincia || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Teléfono: {item.telefono || "-"}</div>
                    </td>
                    <td className="crm-postventa-alert-table-detail" data-label="Alerta">
                      <strong className="crm-postventa-alert-table-title">{item.tipoAlerta || "-"}</strong>
                      <div className="crm-postventa-alert-table-text">{item.resumen || "Sin descripción disponible."}</div>
                      <div className="crm-postventa-alert-table-subline">Motivo código: {item.motivoCodigo || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Acción sugerida: {item.accionSugerida || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Mensaje sugerido: {item.mensajeSugerido || "-"}</div>
                      {(item.motivoCodigo === "FALTA_FACTURACION" || item.motivoCodigo === "CAIDA_FACTURACION") ? (
                        <>
                          <div className="crm-postventa-alert-table-subline">Hoy: {currency(item.montoHoy)}</div>
                          <div className="crm-postventa-alert-table-subline">Prom. 7 días: {currency(item.promedio7Dias)}</div>
                          <div className="crm-postventa-alert-table-subline">Variación: {percent(item.variacion)}</div>
                        </>
                      ) : null}
                    </td>
                    <td data-label="Seguimiento">
                      <div className="crm-postventa-alert-table-subline">Responsable: {item.responsable || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Último contacto: {item.ultimaGestion || "-"}</div>
                      <div className="crm-postventa-alert-table-subline">Próximo contacto: {item.proximoContacto || "-"}</div>
                    </td>
                    <td className="crm-postventa-action-cell" data-label="Acciones">
                      <Button size="sm" variant="outline-secondary" className="crm-postventa-action-button" onClick={() => setAlertaAccionItem(item)}>
                        Acciones
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">{emptyMessage}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </section>
    );

    return (
      <Form.Control
        className={`crm-postventa-input ${isReadOnly ? "is-readonly" : ""}`}
        as={field.inputType === "textarea" ? "textarea" : undefined}
        rows={field.inputType === "textarea" ? 4 : undefined}
        type={field.inputType === "textarea" ? undefined : field.inputType || "text"}
        value={value}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        onChange={(event) => handleFichaFieldChange(field, event.target.value)}
      />
    );
  }, [fichaCatalogos, fichaEditDraft, handleFichaFieldChange]);

  const handleCrearGestion = async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId) {
      return;
    }

    if (!gestionDraft.reglaId) {
      Swal.fire("Gestión", "Seleccione un motivo/acción para guardar la gestión.", "info");
      return;
    }

    if (!gestionDraft.estadoGestion) {
      Swal.fire("Gestión", "Seleccione un estado para la gestión.", "info");
      return;
    }

    if (gestionComposerScope === "ipc" && !gestionDraft.tipoIpc) {
      Swal.fire("Gestion", "Seleccione un Tipo IPC para guardar la gestion.", "info");
      return;
    }

    if (gestionComposerScope === "ipc" && !gestionDraft.ipcMotivo) {
      Swal.fire("Gestion", "Seleccione un motivo IPC para guardar la gestion.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      const reglaIdToSave = gestionComposerScope === "ipc"
        ? Number(currentGestionRules?.[0]?.id || gestionDraft.reglaId)
        : Number(gestionDraft.reglaId);
      const observacionToSave = gestionComposerScope === "ipc"
        ? buildIpcObservation(gestionDraft.tipoIpc, gestionDraft.ipcMotivo, gestionDraft.observacion)
        : gestionDraft.observacion;
      const motivoCodigoToSave = gestionComposerScope === "ipc"
        ? buildIpcMotivoCodigo(gestionDraft.tipoIpc, gestionDraft.ipcMotivo)
        : resolveMotivoCodigoFromRegla(gestiones?.reglas || [], gestionDraft.reglaId);

      if (!reglaIdToSave) {
        Swal.fire("Gestion", "No hay una regla IPC disponible para guardar la gestion.", "info");
        return;
      }

      if (usaProtocoloOperativo && !gestionDraft.canalContacto) {
        Swal.fire("Gestion", "Debe seleccionar un canal de contacto para guardar una gestion contactada.", "info");
        return;
      }

      if (usaProtocoloOperativo && gestionDraft.canalContacto === "Llamada") {
        if (!gestionDraft.resultadoLlamada) {
          Swal.fire("Gestion", "Debe indicar el resultado de la llamada.", "info");
          return;
        }

        if (!String(observacionToSave || "").trim()) {
          Swal.fire("Gestion", "Debe completar una observacion para registrar la llamada.", "info");
          return;
        }

        if (!String(gestionDraft.evidenciaContacto || "").trim()) {
          Swal.fire("Gestion", "Debe completar la evidencia de la llamada.", "info");
          return;
        }
      }

      await crmPostventaApi.crearGestion(
        clienteSeleccionadoId,
        reglaIdToSave,
        gestionDraft.estadoGestion,
        observacionToSave,
        gestionDraft.fechaGestion,
        gestionDraft.fechaProximoContacto,
        motivoCodigoToSave,
        gestionDraft.canalContacto,
        gestionDraft.resultadoLlamada,
        gestionDraft.evidenciaContacto
      );

      Swal.fire("Ok", "La gestión se guardó correctamente.", "success");
      setGestionDraft((prev) => ({
        ...prev,
        tipoIpc: "",
        ipcMotivo: "",
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
        observacion: "",
        fechaProximoContacto: "",
      }));
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      navigate("/administracion/crm-postventa/gestion");
    } catch (error) {
      console.error("Error guardando gestión:", error);
      Swal.fire("Error", error.message || "No se pudo guardar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const abrirEditarGestion = useCallback((item) => {
    setGestionEditItem(item);
    setGestionEditDraft({
      estadoResultante: item?.estado || "Pendiente",
      fechaProximoContacto: toDateTimeLocalInput(item?.proximoContacto),
      prioridad: item?.prioridad || "Media",
      observacion: item?.observacion || item?.descripcion || "",
      canalContacto: item?.canalContacto || "",
      resultadoLlamada: item?.resultadoLlamada || "",
      evidenciaContacto: item?.evidenciaContacto || "",
      resultadoZocoCheck: "",
    });
  }, []);

  const abrirFinalizarGestion = useCallback((item) => {
    setGestionFinalizarItem(item);
    setGestionFinalizarDraft({
      estadoResultante: "Cerrado",
      motivoCierre: "Resuelto",
      observacionFinal: item?.observacion || "",
      resultadoZocoCheck: "",
      resultadoLlamada: item?.resultadoLlamada || "",
      evidenciaContacto: item?.evidenciaContacto || "",
    });
  }, []);

  const handleGuardarEdicionGestion = useCallback(async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId || !gestionEditItem?.id) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.actualizarGestion({
        ClienteId: clienteSeleccionadoId,
        Id: gestionEditItem.id,
        EstadoResultante: gestionEditDraft.estadoResultante,
        Observacion: gestionEditDraft.observacion,
        FechaProximoContacto: gestionEditDraft.fechaProximoContacto || null,
        Prioridad: gestionEditDraft.prioridad,
        CanalContacto: gestionEditDraft.canalContacto,
        ResultadoLlamada: gestionEditDraft.resultadoLlamada,
        EvidenciaContacto: gestionEditDraft.evidenciaContacto,
        ResultadoZocoCheck: gestionEditDraft.resultadoZocoCheck,
      });

      setGestionEditItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      Swal.fire("Gestión", "La gestión se actualizó correctamente.", "success");
    } catch (error) {
      console.error("Error actualizando gestión:", error);
      Swal.fire("Gestión", error.message || "No se pudo actualizar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  }, [analyticsRange, cargarModulo, clienteSeleccionadoId, gestionEditDraft, gestionEditItem]);

  const handleFinalizarGestionExistente = useCallback(async (event) => {
    event.preventDefault();
    if (!clienteSeleccionadoId || !gestionFinalizarItem?.id) {
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.finalizarGestion({
        ClienteId: clienteSeleccionadoId,
        Id: gestionFinalizarItem.id,
        EstadoResultante: gestionFinalizarDraft.motivoCierre === "Sin respuesta"
          ? "Sin respuesta"
          : gestionFinalizarDraft.motivoCierre === "Descartado"
            ? "Descartado"
            : gestionFinalizarDraft.estadoResultante,
        ObservacionFinal: gestionFinalizarDraft.observacionFinal,
        MotivoCierre: gestionFinalizarDraft.motivoCierre,
        ResultadoZocoCheck: gestionFinalizarDraft.resultadoZocoCheck,
        ResultadoLlamada: gestionFinalizarDraft.resultadoLlamada,
        EvidenciaContacto: gestionFinalizarDraft.evidenciaContacto,
      });

      setGestionFinalizarItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
      Swal.fire("Gestión", "La gestión se finalizó correctamente.", "success");
    } catch (error) {
      console.error("Error finalizando gestión:", error);
      Swal.fire("Gestión", error.message || "No se pudo finalizar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  }, [analyticsRange, cargarModulo, clienteSeleccionadoId, gestionFinalizarDraft, gestionFinalizarItem]);

  const handleRegistrarRollo = () => {
    if (!clienteSeleccionadoId) {
      return;
    }

    setRolloDraft({
      fechaEntrega: makeToday(),
      cantidad: "",
      observacion: "",
    });
    setRolloModalOpen(true);
  };

  const handleGuardarRollo = async (event) => {
    event.preventDefault();

    if (!clienteSeleccionadoId) {
      return;
    }

    const cantidad = Number(rolloDraft.cantidad);
    if (!rolloDraft.fechaEntrega) {
      Swal.fire("Rollos", "La fecha de entrega es obligatoria.", "info");
      return;
    }

    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      Swal.fire("Rollos", "La cantidad es obligatoria y debe ser mayor a 0.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoRollo: true }));
    try {
      await crmPostventaApi.registrarRollo(
        {
          clienteId: clienteSeleccionadoId,
          clienteKey: gestiones?.rollos?.clienteKey || crmPostventaApi.buildClienteKey(clienteHeader),
          cuit: clienteHeader.cuit,
          nDeComercio: clienteHeader.nDeComercio,
        },
        rolloDraft.fechaEntrega,
        cantidad,
        rolloDraft.observacion
      );

      setRolloModalOpen(false);
      setRolloDraft({
        fechaEntrega: makeToday(),
        cantidad: "",
        observacion: "",
      });
      await cargarModulo(clienteSeleccionadoId, analyticsRange);

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Entrega registrada correctamente.",
        showConfirmButton: false,
        timer: 2400,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Error", error.message || "No se pudo registrar la entrega.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoRollo: false }));
    }
  };

  const showFacturacionDetail = (row) => {
    setFacturacionDetalle(row);
  };

  const aplicarFiltrosAlertas = useCallback(() => {
    const nextFilters = {
      ...alertasFiltros,
      search: alertasSearchDraft,
    };

    setAlertasFiltros(nextFilters);
    cargarAlertas(nextFilters);
  }, [alertasFiltros, alertasSearchDraft, cargarAlertas]);

  const aplicarFiltrosInactivos = useCallback(() => {
    const nextFilters = {
      ...inactivosFiltros,
      fecha: alertasFiltros.fecha,
      search: inactivosSearchDraft,
    };

    setInactivosFiltros(nextFilters);
    cargarInactivos(nextFilters);
  }, [alertasFiltros.fecha, cargarInactivos, inactivosFiltros, inactivosSearchDraft]);

  const handleAlertasSearchKeyDown = useCallback((event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    aplicarFiltrosAlertas();
  }, [aplicarFiltrosAlertas]);

  const handleInactivosSearchKeyDown = useCallback((event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    aplicarFiltrosInactivos();
  }, [aplicarFiltrosInactivos]);

  const abrirGestionFacturacion = useCallback((row, defaults = {}) => {
    if (!row?.clienteId) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene un cliente asociado para registrar la gestion.", "info");
      return;
    }

    setFacturacionAccionItem(null);
    setFacturacionGestionItem(row);
    setFacturacionGestionDraft({
      clienteId: row.clienteId,
      reglaId: manualAlertaRules?.[0]?.id ? String(manualAlertaRules[0].id) : "",
      estadoGestion: defaults.estadoGestion || "Pendiente",
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion:
        defaults.observacion ||
        `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [manualAlertaRules]);

  const copiarCuitFacturacion = useCallback(async (row) => {
    const cuit = String(row?.cuit || "").trim();
    if (!cuit) {
      Swal.fire("Facturacion", "La fila seleccionada no tiene CUIT para copiar.", "info");
      return;
    }

    try {
      await navigator.clipboard.writeText(cuit);
      setFacturacionAccionItem(null);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "CUIT copiado al portapapeles.",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Facturacion", "No se pudo copiar el CUIT.", "error");
    }
  }, []);

  const ejecutarAccionFacturacion = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    switch (accion) {
      case "detalle":
        setFacturacionAccionItem(null);
        showFacturacionDetail(row);
        return;
      case "filtrar":
        setFacturacionAccionItem(null);
        setFacturacionFiltroComercio(row.comercio || row.terminal || "");
        return;
      case "copiarCuit":
        copiarCuitFacturacion(row);
        return;
      case "registrarGestion":
        abrirGestionFacturacion(row, {
          observacion: `Gestion desde Facturacion. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · Estado ${row.estadoAlerta || "-"}.`,
        });
        return;
      case "seguimiento":
        abrirGestionFacturacion(row, {
          estadoGestion: "Pendiente",
          observacion: `Seguimiento pendiente desde Facturacion para comercio ${row.comercio || "-"} y terminal ${row.terminal || "-"}.`,
        });
        return;
      default:
        return;
    }
  }, [abrirGestionFacturacion, copiarCuitFacturacion]);

  const guardarGestionDesdeFacturacion = async (event) => {
    event.preventDefault();

    if (!facturacionGestionDraft.clienteId || !facturacionGestionDraft.reglaId || !facturacionGestionDraft.estadoGestion) {
      Swal.fire("Facturacion", "Complete regla y estado antes de guardar la gestion.", "info");
      return;
    }

    if (facturacionGestionDraft.estadoGestion === "Contactado" && !facturacionGestionDraft.canalContacto) {
      Swal.fire("Facturacion", "Debe seleccionar un canal de contacto.", "info");
      return;
    }

    if (facturacionGestionDraft.estadoGestion === "Contactado" && facturacionGestionDraft.canalContacto === "Llamada") {
      if (!facturacionGestionDraft.resultadoLlamada || !String(facturacionGestionDraft.observacion || "").trim() || !String(facturacionGestionDraft.evidenciaContacto || "").trim()) {
        Swal.fire("Facturacion", "Para llamada debe completar resultado, observacion y evidencia.", "info");
        return;
      }
    }

    setCargas((prev) => ({ ...prev, guardandoGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        facturacionGestionDraft.clienteId,
        Number(facturacionGestionDraft.reglaId),
        facturacionGestionDraft.estadoGestion,
        facturacionGestionDraft.observacion,
        facturacionGestionDraft.fechaGestion,
        facturacionGestionDraft.fechaProximoContacto,
        "FALTA_FACTURACION",
        facturacionGestionDraft.canalContacto,
        facturacionGestionDraft.resultadoLlamada,
        facturacionGestionDraft.evidenciaContacto
      );

      Swal.fire("Ok", "La gestion se registro correctamente.", "success");
      setFacturacionGestionItem(null);
      await cargarModulo(clienteSeleccionadoId, analyticsRange);
    } catch (error) {
      console.error("Error guardando gestion desde facturacion:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestion.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoGestion: false }));
    }
  };

  const abrirClienteDesdeAlerta = useCallback((row) => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para abrir la ficha.", "info");
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(row.clienteId));
    setClienteSeleccionadoId(row.clienteId);
    setClientePendiente(null);
    setFacturacionDetalle(null);
    setTimelineDetalle(null);
    navigate("/administracion/crm-postventa/ficha");
  }, [navigate]);

  const goToGestion = (row, observacion) => {
    if (row?.clienteId) {
      localStorage.setItem(STORAGE_KEY, String(row.clienteId));
      setClienteSeleccionadoId(row.clienteId);
      setClientePendiente(null);
      setFacturacionDetalle(null);
      setTimelineDetalle(null);
    }

    const motivoCodigo = resolveMotivoCodigoFromAlerta(row);
    const targetScope = String(motivoCodigo || "").toUpperCase().startsWith("MAN_")
      ? resolveGestionScopeByReason(motivoCodigo)
      : gestionComposerScope;
    const reglasTargetScope = gestionScopeRules[targetScope] || [];

    setGestionComposerScope(targetScope);
    setGestionDraft((prev) => ({
      ...prev,
      reglaId: resolveRuleIdByMotivoCodigo(reglasTargetScope, motivoCodigo) || prev.reglaId,
      estadoGestion: prev.estadoGestion || "Pendiente",
      observacion,
    }));
    navigate("/administracion/crm-postventa/gestion");
  };

  const abrirAccionAlerta = useCallback((row, estadoGestion, observacionBase = "") => {
    if (!row?.clienteId) {
      Swal.fire("Alertas", "La alerta no tiene un cliente asociado para registrar una gestión.", "info");
      return;
    }

    const motivoCodigo = resolveMotivoCodigoFromAlerta(row);
    setAlertaGestionMode("operativa");
    setAlertaSeleccionada(row);
    setAlertaGestionDraft({
      clienteId: row.clienteId,
      reglaId: resolveRuleIdByMotivoCodigo(manualAlertaRules, motivoCodigo) || (manualAlertaRules?.[0]?.id ? String(manualAlertaRules[0].id) : ""),
      estadoGestion,
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion: observacionBase || buildSuggestedObservation(row, estadoGestion),
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [manualAlertaRules]);

  const abrirGestionInactiva = useCallback((row) => {
    if (!row?.clienteId) {
      Swal.fire("Terminales inactivas", "La fila seleccionada no tiene un cliente asociado para registrar una gestion.", "info");
      return;
    }

    setAlertaGestionMode("proactiva");
    setAlertaSeleccionada(row);
    setAlertaGestionDraft({
      clienteId: row.clienteId,
      reglaId: inactivosProactivaRules?.[0]?.id ? String(inactivosProactivaRules[0].id) : "",
      estadoGestion: "Pendiente",
      canalContacto: "",
      resultadoLlamada: "",
      evidenciaContacto: "",
      observacion: `Gestion proactiva por inactividad >90 dias. Comercio ${row.comercio || "-"} · Terminal ${row.terminal || "-"} · ${row.diasSinOperar || 0} dias sin operar.`,
      fechaGestion: makeNowDateTimeLocal(),
      fechaProximoContacto: "",
    });
  }, [inactivosProactivaRules]);

  const ejecutarAccionAlerta = useCallback((accion, row) => {
    if (!row) {
      return;
    }

    setAlertaAccionItem(null);

    switch (accion) {
      case "verCliente":
        abrirClienteDesdeAlerta(row);
        return;
      case "registrarGestion":
        goToGestion(row, buildSuggestedObservation(row, "Pendiente"));
        return;
      case "contactado":
        abrirAccionAlerta(row, "Contactado", buildSuggestedObservation(row, "Contactado"));
        return;
      case "posponer":
        abrirAccionAlerta(row, "Pendiente", buildSuggestedObservation(row, "Pendiente"));
        return;
      case "cerrar":
        abrirAccionAlerta(row, "Cerrado", buildSuggestedObservation(row, "Cerrado"));
        return;
      case "escalar":
        abrirAccionAlerta(row, "Escalado", buildSuggestedObservation(row, "Escalado"));
        return;
      case "copiarMensaje":
        copiarMensajeSugerido(row, "Alertas");
        return;
      case "whatsapp":
        abrirWhatsappSugerido(row, "Alertas");
        return;
      default:
        return;
    }
  }, [abrirAccionAlerta, abrirClienteDesdeAlerta, abrirWhatsappSugerido, copiarMensajeSugerido]);

  const guardarGestionDesdeAlerta = async (event) => {
    event.preventDefault();

    if (!alertaGestionDraft.clienteId || !alertaGestionDraft.reglaId || !alertaGestionDraft.estadoGestion) {
      Swal.fire("Alertas", "Complete regla y estado antes de guardar la gestión.", "info");
      return;
    }

    setCargas((prev) => ({ ...prev, guardandoAlertaGestion: true }));
    try {
      await crmPostventaApi.crearGestion(
        alertaGestionDraft.clienteId,
        Number(alertaGestionDraft.reglaId),
        alertaGestionDraft.estadoGestion,
        alertaGestionDraft.observacion,
        alertaGestionDraft.fechaGestion,
        alertaGestionDraft.fechaProximoContacto,
        alertaGestionMode === "proactiva"
          ? resolveMotivoCodigoFromRegla(inactivos?.reglas || alertas?.reglas || [], alertaGestionDraft.reglaId)
          : resolveMotivoCodigoFromAlerta(alertaSeleccionada)
      );

      Swal.fire("Ok", "La gestión de alerta se registró correctamente.", "success");
      setAlertaSeleccionada(null);
      if (alertaGestionMode === "proactiva") {
        await cargarInactivos(inactivosFiltros);
      } else {
        await cargarAlertas(alertasFiltros);
      }
      if (clienteSeleccionadoId === alertaGestionDraft.clienteId) {
        await cargarModulo(clienteSeleccionadoId, analyticsRange);
      }
    } catch (error) {
      console.error("Error guardando gestión desde alerta:", error);
      Swal.fire("Error", error.message || "No se pudo registrar la gestión.", "error");
    } finally {
      setCargas((prev) => ({ ...prev, guardandoAlertaGestion: false }));
    }
  };

  const renderFicha = () => {
    if (cargas.ficha) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando ficha...</div>;
    }

    if (errores.ficha) {
      return <Alert variant="danger">{errores.ficha}</Alert>;
    }

    if (!ficha) {
      return <Alert variant="secondary">No hay ficha disponible para este cliente.</Alert>;
    }

    const header = ficha.header || clienteHeader;

    return (
      <div className="crm-postventa-ficha-wrap">
        <div className="crm-postventa-panel-title mb-3">
          <h3></h3>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={abrirEdicionFicha}
            disabled={!ficha?.edicion?.permiteEdicion || !fichaCamposEdicion.length}
          >
            <FontAwesomeIcon icon={faUserPen} className="me-2" />
            Editar datos
          </Button>
        </div>
        <div className={`crm-postventa-ficha-resumen ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Estado</span>
            <PostventaBadge
              value={header.estado || "-"}
              tone={
                String(header.estado).toUpperCase() === "ALTA"
                  ? "success"
                  : String(header.estado).toUpperCase() === "BAJA"
                    ? "danger"
                    : "default"
              }
            />
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">CUIT</span>
            <strong>{header.cuit || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Terminal</span>
            <strong>{header.nDeTerminal || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Comercio</span>
            <strong>{header.nDeComercio || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Legajo</span>
            <strong>{header.legajo || "-"}</strong>
          </div>
          <div className="crm-postventa-ficha-resumen-item">
            <span className="crm-postventa-card-label">Producto</span>
            <strong>{header.producto || "-"}</strong>
          </div>
        </div>

        <div className="crm-postventa-ficha-grid">
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Identificación</h4>
            <DataList items={ficha.datosGenerales} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Contacto</h4>
            <DataList items={ficha.contacto} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Fiscal</h4>
            <DataList items={ficha.fiscal} />
          </section>
          <section className={`crm-postventa-ficha-block ${darkMode ? "is-dark" : ""}`}>
            <h4>Bancario / operativo</h4>
            <DataList items={ficha.operativo} />
          </section>
        </div>

        <section className={`crm-postventa-ficha-block mt-3 ${darkMode ? "is-dark" : ""}`}>
          <h4>Historial de cambios</h4>
          {ficha?.historialCambios?.length ? (
            <div className="crm-postventa-table-wrap crm-postventa-ficha-history-scroll">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Tipo</th>
                    <th>Valores modificados</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.historialCambios.map((item, index) => (
                    <tr key={`ficha-hist-${index}`}>
                      <td>{item.fecha || "-"}</td>
                      <td>{item.usuario || "-"}</td>
                      <td>{item.tipo || "-"}</td>
                      <td>{Array.isArray(item.valoresModificados) && item.valoresModificados.length ? item.valoresModificados.join(" | ") : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="secondary" className="mb-0">No hay modificaciones CRM registradas para esta ficha.</Alert>
          )}
        </section>

        <Modal
          show={fichaEditOpen}
          onHide={() => setFichaEditOpen(false)}
          centered
          dialogClassName="crm-postventa-ficha-workspace"
          contentClassName={`crm-postventa-ficha-workspace-content ${darkMode ? "is-dark" : ""}`}
        >
          <Form className="crm-postventa-ficha-workspace-form" onSubmit={guardarEdicionFicha}>
            <Modal.Header className="crm-postventa-ficha-workspace-header">
              <div className="crm-postventa-ficha-workspace-topbar">
                <div>
                  <span className="crm-postventa-ficha-workspace-kicker">CRM Postventa</span>
                  <Modal.Title>Editar datos del cliente</Modal.Title>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Cerrar"
                  onClick={() => setFichaEditOpen(false)}
                />
              </div>
              <div className={`crm-postventa-ficha-hero ${darkMode ? "is-dark" : ""}`}>
                <div className="crm-postventa-ficha-hero-main">
                  <span className="crm-postventa-ficha-hero-label">Cliente</span>
                  <strong>{clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Cliente"}</strong>
                </div>
                <div className="crm-postventa-ficha-hero-grid">
                  <div className="crm-postventa-ficha-hero-item">
                    <span>CUIT</span>
                    <strong>{clienteHeader.cuit || "-"}</strong>
                  </div>
                  <div className="crm-postventa-ficha-hero-item">
                    <span>Terminal</span>
                    <strong>{clienteHeader.nDeTerminal || "-"}</strong>
                  </div>
                  <div className="crm-postventa-ficha-hero-item">
                    <span>Comercio</span>
                    <strong>{clienteHeader.nDeComercio || "-"}</strong>
                  </div>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body className="crm-postventa-ficha-workspace-body">
              <div className="crm-postventa-ficha-workspace-intro">
                <div>
                  <h4>Edicion completa de ficha</h4>
                  <p>Organizamos los datos por secciones para editar en una grilla comoda y evitar una lista vertical eterna.</p>
                </div>
              </div>

              {fichaCatalogosLoading ? (
                <Alert variant="secondary" className="mb-0">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando opciones de edición...
                </Alert>
              ) : (
                <div className="crm-postventa-ficha-workspace-sections">
                  {Object.entries(fichaCamposAgrupados).map(([section, items]) => (
                    <section key={section} className={`crm-postventa-edit-section-card ${darkMode ? "is-dark" : ""}`}>
                      <div className="crm-postventa-edit-section-card-head">
                        <div>
                          <h5>{section}</h5>
                          <p>{items.length} campo(s) disponibles en esta seccion.</p>
                        </div>
                        <span>{items.length}</span>
                      </div>
                      <div className="crm-postventa-edit-grid">
                        {items.map((item) => (
                          <Form.Group key={item.key} className="crm-postventa-edit-field">
                            <Form.Label>
                              <span>{item.label}</span>
                              {!item.editable ? <small>Solo lectura</small> : null}
                            </Form.Label>
                            {renderFichaEditField(item)}
                            {item.readOnlyReason ? (
                              <Form.Text className="crm-postventa-field-help">{item.readOnlyReason}</Form.Text>
                            ) : null}
                          </Form.Group>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="crm-postventa-ficha-workspace-footer">
              <Button type="button" variant="outline-secondary" onClick={() => setFichaEditOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoFicha || fichaCatalogosLoading}>
                {cargas.guardandoFicha ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderFacturacion = () => {
    if (cargas.facturacion) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando facturación...</div>;
    }

    if (errores.facturacion) {
      return <Alert variant="danger">{errores.facturacion}</Alert>;
    }

    if (!facturacion?.tieneDatosReales) {
      return <Alert variant="secondary">{facturacion?.message || "No hay datos reales de facturación disponibles para este cliente."}</Alert>;
    }

    return (
      <div className="crm-postventa-alert-layout d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-range">
            <Form.Control
              type="date"
              value={analyticsRange.desde}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, desde: event.target.value }))}
            />
            <Form.Control
              type="date"
              value={analyticsRange.hasta}
              onChange={(event) => setAnalyticsRange((prev) => ({ ...prev, hasta: event.target.value }))}
            />
            <Button variant="outline-success" onClick={() => cargarModulo(clienteSeleccionadoId, analyticsRange)}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
          {facturacionFiltroComercio ? (
            <Button variant="outline-secondary" onClick={() => setFacturacionFiltroComercio("")}>
              Ver todo el grupo
            </Button>
          ) : null}
        </div>

        <div className="crm-postventa-card-grid crm-postventa-alert-kpis">
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Fecha referencia</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.fechaReferencia || facturacion.resumen.fechaHasta || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Monto hoy</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoHoy)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.montoAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 3 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio3Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Promedio 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyDetailed(facturacion.resumen.promedio7Dias)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Terminales</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.terminales}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Con facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.conFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Sin facturación</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.sinFacturacion}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Caída fuerte</span>
            <div className="crm-postventa-card-value">{facturacion.resumen.caidaFuerte}</div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Última actividad</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.ultimaActividad || "Sin datos"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">CUIT del grupo</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {facturacion.resumen.cuitDelGrupo || "-"}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs ayer</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVsAyer)}
            </div>
          </div>
          <div className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`}>
            <span className="crm-postventa-card-label">Variación vs 7 días</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {percent(facturacion.resumen.variacionVs7Dias)}
            </div>
          </div>
        </div>

        <div className="crm-postventa-insights crm-postventa-alert-insights">
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Motivo principal</span>
            <strong>{facturacion.insights.motivoPrincipal}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Insight</span>
            <strong>{facturacion.insights.insight}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Acción sugerida</span>
            <strong>{facturacion.insights.accionSugerida}</strong>
          </article>
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe">
          <Table className="crm-postventa-table" responsive hover>
            <thead className="table-light">
              <tr>
                <th>CUIT</th>
                <th>Terminal</th>
                <th>Comercio</th>
                <th>Telefono</th>
                <th>Estado</th>
                <th>Hoy</th>
                <th>Ayer</th>
                <th>Prom. 3d</th>
                <th>Prom. 7d</th>
                <th>Var. vs ayer</th>
                <th>Var. vs 7d</th>
                <th>Días sin fact.</th>
                <th>Última actividad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filasFacturacion.map((row) => (
                <tr key={`${row.cuit}-${row.terminal}-${row.comercio}`}>
                  <td className="text-nowrap">{row.cuit || "-"}</td>
                  <td>{row.terminal || "-"}</td>
                  <td>{row.comercio || "-"}</td>
                  <td>
                    {buildFacturacionPhoneLines(row).length ? (
                      buildFacturacionPhoneLines(row).map((line) => <div key={`${row.terminal}-${line}`}>{line}</div>)
                    ) : (
                      <div>-</div>
                    )}
                    {resolvePreferredWhatsappPhone(row) ? (
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="crm-postventa-action-button mt-2"
                        onClick={() => abrirWhatsappSugerido(row, "Facturacion")}
                      >
                        WhatsApp
                      </Button>
                    ) : null}
                  </td>
                  <td>
                    <PostventaBadge
                      value={row.estadoAlerta}
                      tone={
                        row.estadoAlerta === "OK"
                          ? "success"
                          : row.estadoAlerta === "Inactivo" || row.estadoAlerta === "Caída vs promedio"
                            ? "warning"
                            : "danger"
                      }
                    />
                  </td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoHoy)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.montoAyer)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio3Dias)}</td>
                  <td className="text-nowrap">{formatCurrencyDetailed(row.promedio7Dias)}</td>
                  <td className="text-nowrap">{percent(row.variacionVsAyer)}</td>
                  <td className="text-nowrap">{percent(row.variacion)}</td>
                  <td>{row.diasGrupoSinFacturar != null ? row.diasGrupoSinFacturar : "-"}</td>
                  <td className="text-nowrap">{row.ultimaActividad || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="crm-postventa-action-button"
                      onClick={() => setFacturacionAccionItem(row)}
                    >
                      Acciones
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <Modal show={Boolean(facturacionAccionItem)} onHide={() => setFacturacionAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de facturacion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle crm-postventa-action-summary p-0">
              <strong>{facturacionAccionItem?.nombreDeFantasia || "Cliente"}</strong>
              <span>Comercio {facturacionAccionItem?.comercio || "-"}</span>
              <span>Terminal {facturacionAccionItem?.terminal || "-"}</span>
              <span>CUIT {facturacionAccionItem?.cuit || "-"}</span>
              <span>Telefono {buildFacturacionPhoneLines(facturacionAccionItem).join(" | ") || "-"}</span>
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("detalle", facturacionAccionItem)}>
              Ver detalle
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("filtrar", facturacionAccionItem)}>
              Ver solo este comercio
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("copiarCuit", facturacionAccionItem)}>
              Copiar CUIT
            </Button>
            {resolvePreferredWhatsappPhone(facturacionAccionItem) ? (
              <Button variant="outline-success" onClick={() => abrirWhatsappSugerido(facturacionAccionItem, "Facturacion")}>
                Abrir WhatsApp
              </Button>
            ) : null}
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("registrarGestion", facturacionAccionItem)}>
              Registrar gestion
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionFacturacion("seguimiento", facturacionAccionItem)}>
              Marcar seguimiento
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(facturacionGestionItem)} onHide={() => setFacturacionGestionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Registrar gestion desde Facturacion</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeFacturacion}>
            <Modal.Body>
              <div className="crm-postventa-subtle crm-postventa-action-summary p-0 mb-3">
                <strong>{facturacionGestionItem?.nombreDeFantasia || "Cliente"}</strong>
                <span>Comercio {facturacionGestionItem?.comercio || "-"}</span>
                <span>Terminal {facturacionGestionItem?.terminal || "-"}</span>
                <span>CUIT {facturacionGestionItem?.cuit || "-"}</span>
                <span>Telefono {buildFacturacionPhoneLines(facturacionGestionItem).join(" | ") || "-"}</span>
              </div>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / accion</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.reglaId}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {manualAlertaRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{formatGestionRuleLabel(regla)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={facturacionGestionDraft.estadoGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {(GESTION_SCOPE_STATES.operativa || GESTION_STATES).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaGestion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Proximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={facturacionGestionDraft.fechaProximoContacto}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={facturacionGestionDraft.observacion}
                    onChange={(event) => setFacturacionGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setFacturacionGestionItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? "Guardando..." : "Guardar gestion"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {facturacionDetalle ? (
          <div className="crm-postventa-detail-grid">
            <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <div className="crm-postventa-panel-title">
                <h4>Detalle de facturación</h4>
                <Button variant="outline-secondary" size="sm" onClick={() => setFacturacionDetalle(null)}>
                  Cerrar
                </Button>
              </div>
              <p className="mb-3">
                {facturacionDetalle.nombreDeFantasia || "Cliente"} · Terminal {facturacionDetalle.terminal || "-"} · Comercio {facturacionDetalle.comercio || "-"}
              </p>
              <div className="crm-postventa-detail-table-wrap">
                <Table className="crm-postventa-table mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Total bruto</th>
                      <th>Operaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturacionDetalle.serieDiaria?.map((item) => (
                      <tr key={`${facturacionDetalle.terminal}-${item.fecha}`}>
                        <td>{item.fecha}</td>
                        <td>{formatCurrencyDetailed(item.totalBruto)}</td>
                        <td>{item.cantidadOperaciones || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>
            <aside className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
              <h4 className="mb-3">Resumen rápido</h4>
              <DataList
                items={[
                  { label: "Estado", value: facturacionDetalle.estadoAlerta, isBadge: true, tone: facturacionDetalle.estadoAlerta === "OK" ? "success" : "danger" },
                  { label: "Telefono", value: buildFacturacionPhoneLines(facturacionDetalle).join(" | ") || "-" },
                  { label: "Monto hoy", value: formatCurrencyDetailed(facturacionDetalle.montoHoy) },
                  { label: "Promedio 7 días", value: formatCurrencyDetailed(facturacionDetalle.promedio7Dias) },
                  { label: "Variación", value: percent(facturacionDetalle.variacion) },
                  { label: "Última actividad", value: facturacionDetalle.ultimaActividad || "Sin datos" },
                  { label: "Acción sugerida", value: facturacionDetalle.accionSugerida || "-" },
                ]}
              />
            </aside>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAlertaOperativaTableInline = (title, rows, total, searchValue, onSearchChange, emptyMessage) => (
    <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
      <div className="crm-postventa-panel-title">
        <h3>{title}</h3>
        <span className="crm-postventa-card-label">Mostrando {rows.length} de {total} alerta(s)</span>
      </div>
      <div className="crm-postventa-alert-queue-filters">
        <Form.Control
          type="search"
          className="crm-postventa-filter-control"
          placeholder={`Buscar en ${title.toLowerCase()} por comercio, CUIT, terminal, responsable o teléfono`}
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <div className="crm-postventa-alert-queue-scroll">
        <div className="crm-postventa-alert-queue-list">
          <Table className="crm-postventa-table crm-postventa-table-alertas mb-0" hover>
            <thead className="table-light">
              <tr>
                <th>Prioridad / estado</th>
                <th>Comercio</th>
                <th>Alerta</th>
                <th>Seguimiento</th>
                <th className="crm-postventa-action-cell">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((item, index) => (
                <tr key={`${title}-${item.cuit}-${item.terminal}-${item.tipoAlerta}-${index}`}>
                  <td data-label="Prioridad / estado">
                    <div className="crm-postventa-alert-table-stack">
                      <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                    </div>
                  </td>
                  <td className="crm-postventa-alert-table-commerce" data-label="Comercio">
                    <strong className="crm-postventa-alert-table-title">{item.comercio || "Sin comercio"}</strong>
                    <div className="crm-postventa-inline-badges">
                      <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                      <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                    </div>
                    <div className="crm-postventa-alert-table-subline">CUIT: {item.cuit || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Terminal: {item.terminal || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Provincia: {item.provincia || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Teléfono: {item.telefono || "-"}</div>
                  </td>
                  <td className="crm-postventa-alert-table-detail" data-label="Alerta">
                    <strong className="crm-postventa-alert-table-title">{item.tipoAlerta || "-"}</strong>
                    <div className="crm-postventa-alert-table-text">{item.resumen || "Sin descripción disponible."}</div>
                    <div className="crm-postventa-alert-table-subline">Motivo código: {item.motivoCodigo || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Acción sugerida: {item.accionSugerida || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Mensaje sugerido: {item.mensajeSugerido || "-"}</div>
                    {(item.motivoCodigo === "FALTA_FACTURACION" || item.motivoCodigo === "CAIDA_FACTURACION") ? (
                      <>
                        <div className="crm-postventa-alert-table-subline">Hoy: {currency(item.montoHoy)}</div>
                        <div className="crm-postventa-alert-table-subline">Prom. 7 días: {currency(item.promedio7Dias)}</div>
                        <div className="crm-postventa-alert-table-subline">Variación: {percent(item.variacion)}</div>
                      </>
                    ) : null}
                  </td>
                  <td data-label="Seguimiento">
                    <div className="crm-postventa-alert-table-subline">Responsable: {item.responsable || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Último contacto: {item.ultimaGestion || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Próximo contacto: {item.proximoContacto || "-"}</div>
                  </td>
                  <td className="crm-postventa-action-cell" data-label="Acciones">
                    <Button size="sm" variant="outline-secondary" className="crm-postventa-action-button" onClick={() => setAlertaAccionItem(item)}>
                      Acciones
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-muted">{emptyMessage}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </section>
  );

  const renderAlertas = () => {
    if (cargas.alertas) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando alertas...</div>;
    }

    if (errores.alertas) {
      return <Alert variant="danger">{errores.alertas}</Alert>;
    }

    if (!alertas) {
      return <Alert variant="secondary">No hay alertas disponibles para mostrar.</Alert>;
    }

    const gestionModalRules = alertaGestionMode === "proactiva" ? inactivosProactivaRules : manualAlertaRules;
    const gestionModalStates =
      alertaGestionMode === "proactiva"
        ? (GESTION_SCOPE_STATES.proactiva || GESTION_STATES)
        : (GESTION_SCOPE_STATES.operativa || GESTION_STATES);
    const gestionModalTitle =
      alertaGestionMode === "proactiva"
        ? "Gestionar terminal inactiva"
        : (alertaSeleccionada?.tipoAlerta || "Registrar gestión de alerta");

    return (
      <div className="crm-postventa-alert-layout d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-range crm-postventa-alert-filters">
            <Form.Control
              type="date"
              className="crm-postventa-filter-control crm-postventa-filter-date"
              value={alertasFiltros.fecha}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
            />
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={alertasFiltros.prioridad}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, prioridad: event.target.value }))}
            >
              <option value="">Todas las prioridades</option>
              <option value="Crítica">Crítica</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </Form.Select>
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={alertasFiltros.estado}
              onChange={(event) => setAlertasFiltros((prev) => ({ ...prev, estado: event.target.value }))}
            >
              <option value="">Todos los estados</option>
              {(GESTION_SCOPE_STATES.operativa || GESTION_STATES).map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Form.Select>
            <Form.Control
              type="search"
              className="crm-postventa-filter-control crm-postventa-filter-search"
              placeholder="Buscar por comercio, CUIT, provincia o responsable"
              value={alertasSearchDraft}
              onChange={(event) => setAlertasSearchDraft(event.target.value)}
              onKeyDown={handleAlertasSearchKeyDown}
            />
            <Button className="crm-postventa-filter-button" variant="outline-success" onClick={aplicarFiltrosAlertas}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="crm-postventa-card-grid crm-postventa-alert-kpis">
          {(alertas.kpis || []).map((item) => (
            <article className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`} key={item.label}>
              <span className="crm-postventa-card-label">{item.label}</span>
              <div className="crm-postventa-card-value">{item.value}</div>
              <p className="crm-postventa-card-note mb-0">{item.description}</p>
            </article>
          ))}
        </div>

        <div className="crm-postventa-insights crm-postventa-alert-insights">
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Insight general</span>
            <strong>{alertas.insightGeneral || "Sin datos"}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Alerta destacada</span>
            <strong>{alertas.alertaDestacada || "Sin datos"}</strong>
          </article>
          <article className="crm-postventa-insight">
            <span className="crm-postventa-card-label">Acción sugerida</span>
            <strong>{alertas.accionGeneral || "Sin datos"}</strong>
          </article>
        </div>

        <div className="crm-postventa-alert-blocks">
          <section className={`crm-postventa-panel crm-postventa-alert-critical-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Alertas críticas</h3>
            </div>
            <div className="crm-postventa-alert-list crm-postventa-alert-list-scroll">
              {alertas.alertasCriticas?.length ? alertas.alertasCriticas.map((item, index) => (
                <article className="crm-postventa-alert-list-item" key={`${item.cuit}-${item.tipoAlerta}-${index}`}>
                  <div className="d-flex justify-content-between gap-3 flex-wrap">
                    <strong>{item.comercio || "Sin comercio"} · {item.tipoAlerta}</strong>
                    <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                  </div>
                  <div className="crm-postventa-inline-badges">
                    <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                    <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                  </div>
                  <p className="mb-0">{item.resumen || "Sin resumen."}</p>
                  <div className="crm-postventa-alert-table-subline">{item.motivoPrioridad || "Sin motivo de prioridad."}</div>
                </article>
              )) : <Alert variant="secondary" className="mb-0">No hay alertas críticas para la fecha seleccionada.</Alert>}
            </div>
          </section>

          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Bloques</h3>
            </div>
            <div className="crm-postventa-alert-pill-row">
              <span className="crm-postventa-chip">Facturación {alertas.alertasFacturacion?.length || 0}</span>
              <span className="crm-postventa-chip">Zoco Check {alertas.resumenOperativo?.zocoCheck ?? alertas.alertasZocoCheck?.length ?? 0}</span>
              <span className="crm-postventa-chip">Terminales extraviadas {alertas.resumenOperativo?.terminalesExtraviadas ?? alertas.alertasTerminalExtraviada?.length ?? 0}</span>
              <span className="crm-postventa-chip">Fecha {alertas.fechaReferencia || "-"}</span>
            </div>
            {false ? (
              <p className="mb-0 mt-3">
                Generación automática: +{alertas.generacionAutomatica.creadasFacturacion || 0} facturación, +{alertas.generacionAutomatica.creadasRollos || 0} rollos, vencidos {alertas.generacionAutomatica.proximosContactosVencidos || 0}.
              </p>
            ) : null}
            <p className="mb-0 mt-3">{alertas.rollosMessage || "Las alertas operativas usan la misma fuente comun para todas las usuarias."}</p>
          </section>
        </div>

        {renderAlertaOperativaTableInline(
          "Alertas de facturación",
          alertasFacturacionFiltradas,
          alertas?.alertasFacturacion?.length || 0,
          alertasFacturacionSearch,
          setAlertasFacturacionSearch,
          "No hay alertas de facturación que coincidan con los filtros actuales."
        )}

        <ZocoCheckPanel
          darkMode={darkMode}
          rows={alertasZocoCheckFiltradas}
          totalRows={alertas?.alertasZocoCheck?.length || 0}
          reglas={alertas?.reglas || []}
          currentUserName={gestiones?.usuarioActual || ""}
          isAdmin={canManageZocoCheck}
          onRefresh={() => cargarAlertas(alertasFiltros)}
        />

        {renderAlertaOperativaTableInline(
          "Terminales extraviadas",
          alertasTerminalExtraviadaFiltradas,
          alertas?.alertasTerminalExtraviada?.length || 0,
          alertasTerminalExtraviadaSearch,
          setAlertasTerminalExtraviadaSearch,
          "No hay terminales extraviadas activas para mostrar."
        )}

        {false ? (
        <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Alertas operativas</h3>
            <span className="crm-postventa-card-label">
              Mostrando {alertasOperativasFiltradas.length} de {alertas.filas?.length || 0} alertas
            </span>
          </div>
          <div className="crm-postventa-alert-queue-filters">
            <Form.Control
              type="search"
              className="crm-postventa-filter-control"
              placeholder="Filtrar alertas operativas por comercio, CUIT, terminal, provincia, tipo, responsable o estado"
              value={alertasOperativasSearch}
              onChange={(event) => setAlertasOperativasSearch(event.target.value)}
            />
          </div>
          <div className="crm-postventa-alert-queue-scroll">
            <div className="crm-postventa-alert-queue-list">
            <Table className="crm-postventa-table crm-postventa-table-alertas mb-0" hover>
            <thead className="table-light">
              <tr>
                <th>Prioridad / estado</th>
                <th>Comercio</th>
                <th>Alerta</th>
                <th>Facturación</th>
                <th>Gestión</th>
                <th className="crm-postventa-action-cell">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {alertasOperativasFiltradas.length ? alertasOperativasFiltradas.map((item, index) => (
                <tr key={`${item.cuit}-${item.tipoAlerta}-${index}`}>
                  <td data-label="Prioridad / estado">
                    <div className="crm-postventa-alert-table-stack">
                      <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      <PostventaBadge value={`Score ${item.scorePrioridad || 0}`} tone={toneForPriority(item.prioridad)} />
                    </div>
                  </td>
                  <td className="crm-postventa-alert-table-commerce" data-label="Comercio">
                    <strong className="crm-postventa-alert-table-title">{item.comercio || "Sin comercio"}</strong>
                    <div className="crm-postventa-inline-badges">
                      <PostventaBadge value={`Segmento ${item.segmentoComercial || "Nuevo"}`} tone={toneForSegment(item.segmentoComercial)} />
                      <PostventaBadge value={item.patronFacturacion || "NUEVO"} tone={toneForPattern(item.patronFacturacion)} />
                    </div>
                    <div className="crm-postventa-alert-table-subline">CUIT: {item.cuit || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Terminal: {item.terminal || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Provincia: {item.provincia || "-"}</div>
                  </td>
                  <td className="crm-postventa-alert-table-detail" data-label="Alerta">
                    <strong className="crm-postventa-alert-table-title">{item.tipoAlerta || "-"}</strong>
                    <div className="crm-postventa-alert-table-text">{item.resumen || "Sin descripción disponible."}</div>
                    <div className="crm-postventa-alert-table-subline">Motivo prioridad: {item.motivoPrioridad || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Motivo código: {item.motivoCodigo || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Tipo de gestión: {item.esAutomatica ? "Automática" : "Manual"}</div>
                    <div className="crm-postventa-alert-table-subline">Acción sugerida: {item.accionSugerida || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Mensaje sugerido: {item.mensajeSugerido || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Última actividad: {item.ultimaActividad || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Última entrega de rollos: {item.ultimaEntregaRollos || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Próximo contacto: {item.proximoContacto || "-"}</div>
                  </td>
                  <td data-label="Facturación">
                    <div className="crm-postventa-alert-table-subline">Hoy: {currency(item.montoHoy)}</div>
                    <div className="crm-postventa-alert-table-subline">Prom. 7 días: {currency(item.promedio7Dias)}</div>
                    <div className="crm-postventa-alert-table-subline">Variación: {percent(item.variacion)}</div>
                    <div className="crm-postventa-alert-table-subline">30 días: {currency(item.facturacion30Dias)}</div>
                    <div className="crm-postventa-alert-table-subline">Días con venta 30d: {item.diasConVenta30Dias || 0}</div>
                    <div className="crm-postventa-alert-table-subline">Vs patrón: {item.diasSinFacturarVsPatron || 0} día(s)</div>
                  </td>
                  <td data-label="Gestión">
                    <div className="crm-postventa-alert-table-subline">Última gestión: {item.ultimaGestion || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Responsable: {item.responsable || "-"}</div>
                    <div className="crm-postventa-alert-table-subline">Teléfono: {item.telefono || "-"}</div>
                  </td>
                  <td className="crm-postventa-action-cell" data-label="Acciones">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="crm-postventa-action-button"
                      onClick={() => setAlertaAccionItem(item)}
                    >
                      Acciones
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    No hay alertas que coincidan con los filtros actuales.
                  </td>
                </tr>
              )}
            </tbody>
            </Table>
          </div>
        </div>
        </section>
        ) : null}

        <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <div>
              <h3>Terminales inactivas (+90 días)</h3>
              <span className="crm-postventa-card-label">
                Terminales activas que llevan más de 90 días sin facturar. No son alertas operativas.
              </span>
            </div>
            <span className="crm-postventa-card-label">
              Mostrando {inactivosFiltrados.length} de {inactivos?.filas?.length || 0} terminales
            </span>
          </div>
          <div className="crm-postventa-subtle">
            Esta sección sirve para controlar recuperación, retiro o revisión comercial por terminal.
          </div>
          <div className="crm-postventa-inactivos-filters mt-3">
            <Form.Control
              type="search"
              className="crm-postventa-filter-control crm-postventa-inactivos-filter-search"
              placeholder="Buscar por comercio, CUIT, terminal, provincia o responsable"
              value={inactivosSearchDraft}
              onChange={(event) => setInactivosSearchDraft(event.target.value)}
              onKeyDown={handleInactivosSearchKeyDown}
            />
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={inactivosFiltros.provincia}
              onChange={(event) => setInactivosFiltros((prev) => ({ ...prev, provincia: event.target.value }))}
            >
              <option value="">Todas las provincias</option>
              {inactivosProvinciaOptions.map((item) => (
                <option key={`inactivos-provincia-${item}`} value={item}>{item}</option>
              ))}
            </Form.Select>
            <Form.Control
              type="search"
              className="crm-postventa-filter-control"
              placeholder="Responsable"
              value={inactivosFiltros.responsable}
              onChange={(event) => setInactivosFiltros((prev) => ({ ...prev, responsable: event.target.value }))}
            />
            <Form.Select
              className="crm-postventa-filter-control crm-postventa-filter-select"
              value={inactivosFiltros.diasSinOperar}
              onChange={(event) => setInactivosFiltros((prev) => ({ ...prev, diasSinOperar: event.target.value }))}
            >
              <option value="">Todos los rangos</option>
              <option value="90-120">90-120</option>
              <option value="121-180">121-180</option>
              <option value="181-365">181-365</option>
              <option value="Mas de 365">Más de 365</option>
            </Form.Select>
            <Button className="crm-postventa-filter-button" variant="outline-success" onClick={aplicarFiltrosInactivos}>
              <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
              Actualizar
            </Button>
          </div>
          <div className="crm-postventa-alert-queue-scroll crm-postventa-inactivos-scroll mt-3">
            <div className="crm-postventa-alert-queue-list">
              {cargas.inactivos ? (
                <div className="crm-postventa-subtle py-3"><Spinner animation="border" size="sm" className="me-2" />Cargando terminales inactivas...</div>
              ) : errores.inactivos ? (
                <Alert variant="danger" className="mb-0">{errores.inactivos}</Alert>
              ) : (
                <Table className="crm-postventa-table crm-postventa-table-alertas crm-postventa-inactivos-table mb-0" hover>
                  <thead className="table-light">
                    <tr>
                      <th>Estado</th>
                      <th>CUIT</th>
                      <th>Comercio</th>
                      <th>Razón social</th>
                      <th>Terminal</th>
                      <th>Provincia</th>
                      <th>Teléfono</th>
                      <th>Última facturación</th>
                      <th>Días sin operar</th>
                      <th>Facturación 30 días</th>
                      <th>Promedio histórico</th>
                      <th>Responsable</th>
                      <th>Última gestión</th>
                      <th>Acción sugerida</th>
                      <th className="crm-postventa-action-cell">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactivosFiltrados.length ? inactivosFiltrados.map((item, index) => (
                      <tr key={`${item.cuit}-${item.terminal}-${index}`}>
                        <td data-label="Estado">
                          <PostventaBadge value={item.estado || "Candidato a retiro"} tone="warning" />
                        </td>
                        <td data-label="CUIT">{item.cuit || "-"}</td>
                        <td data-label="Comercio">
                          <strong className="crm-postventa-alert-table-title">{item.comercio || "-"}</strong>
                        </td>
                        <td data-label="Razón social">{item.razonSocial || "-"}</td>
                        <td data-label="Terminal">{item.terminal || "-"}</td>
                        <td data-label="Provincia">{item.provincia || "-"}</td>
                        <td data-label="Teléfono">{item.telefono || "-"}</td>
                        <td data-label="Última facturación">{item.ultimaFechaFacturacion || "-"}</td>
                        <td data-label="Días sin operar">{item.diasSinOperar || 0}</td>
                        <td data-label="Facturación 30 días">{currency(item.facturacionUltimos30Dias)}</td>
                        <td data-label="Promedio histórico">{currency(item.facturacionPromedioHistorica)}</td>
                        <td data-label="Responsable">{item.usuarioResponsable || "-"}</td>
                        <td data-label="Última gestión">
                          <div className="crm-postventa-alert-table-subline">{item.ultimaGestion || "-"}</div>
                          <div className="crm-postventa-alert-table-subline">Próximo: {item.proximoContacto || "-"}</div>
                        </td>
                        <td data-label="Acción sugerida">
                          <div className="crm-postventa-alert-table-text">{item.accionSugerida || "-"}</div>
                        </td>
                        <td className="crm-postventa-action-cell" data-label="Acciones">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="crm-postventa-action-button"
                            onClick={() => abrirGestionInactiva(item)}
                          >
                            Gestionar
                          </Button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={15} className="text-center py-4 text-muted">
                          {inactivos?.message || "No hay terminales inactivas que coincidan con los filtros actuales."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </section>

        <Modal show={Boolean(alertaAccionItem)} onHide={() => setAlertaAccionItem(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Acciones de alerta</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-2">
            <div className="crm-postventa-subtle p-0">
              {alertaAccionItem?.comercio || "Cliente"} · {alertaAccionItem?.tipoAlerta || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Acción sugerida: {alertaAccionItem?.accionSugerida || "-"}
            </div>
            <div className="crm-postventa-subtle p-0">
              Mensaje sugerido: {alertaAccionItem?.mensajeSugerido || "-"}
            </div>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("verCliente", alertaAccionItem)}>
              Ver cliente
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("copiarMensaje", alertaAccionItem)}>
              Copiar mensaje
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("whatsapp", alertaAccionItem)}>
              Abrir WhatsApp
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("registrarGestion", alertaAccionItem)}>
              Registrar gestión
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("contactado", alertaAccionItem)}>
              Marcar como contactado
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("posponer", alertaAccionItem)}>
              Posponer seguimiento
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("cerrar", alertaAccionItem)}>
              Cerrar alerta
            </Button>
            <Button variant="outline-secondary" onClick={() => ejecutarAccionAlerta("escalar", alertaAccionItem)}>
              Escalar
            </Button>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(alertaSeleccionada)} onHide={() => setAlertaSeleccionada(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{gestionModalTitle}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={guardarGestionDesdeAlerta}>
            <Modal.Body>
              <p className="mb-3">{alertaSeleccionada?.comercio || "Cliente"} · {alertaSeleccionada?.cuit || "-"}</p>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Regla / acción</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.reglaId}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))}
                  >
                    <option value="">Seleccionar</option>
                    {gestionModalRules.map((regla) => (
                      <option key={regla.id} value={regla.id}>{formatGestionRuleLabel(regla)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    value={alertaGestionDraft.estadoGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))}
                  >
                    {gestionModalStates.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha / hora</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaGestion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Próximo contacto</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={alertaGestionDraft.fechaProximoContacto}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observación</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={alertaGestionDraft.observacion}
                    onChange={(event) => setAlertaGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setAlertaSeleccionada(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoAlertaGestion}>
                {cargas.guardandoAlertaGestion ? "Guardando..." : "Guardar gestión"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    );
  };

  const renderOrdenesPago = () => {
    if (cargas.ordenesPago) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando órdenes de pago...</div>;
    }

    if (errores.ordenesPago) {
      return <Alert variant="danger">{errores.ordenesPago}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Fecha consultada</Form.Label>
              <Form.Control
                type="date"
                value={ordenesPagoFiltros.fecha}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, fecha: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscar por CUIT, comercio o documento</Form.Label>
              <Form.Control
                value={ordenesPagoFiltros.search}
                onChange={(event) => setOrdenesPagoFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="CUIT, comercio o documento"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Fecha consultada: {ordenesPago?.fechaPago || ordenesPagoFiltros.fecha || "-"} · Resultado: {ordenesPago?.filas?.length || 0} documento(s)
        </div>

        <div className="crm-postventa-table-wrap">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre documento</th>
                <th>CUIT</th>
                <th>Comercio / terminal</th>
                <th>Estado conciliación</th>
                <th>Origen PDF</th>
                <th>Fecha</th>
                <th>Total bruto</th>
                <th>PDF</th>
                <th>Detalle</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ordenesPago?.filas?.length ? (
                ordenesPago.filas.map((item) => (
                  <tr key={item.documentoKey || `${item.documentoId}-${item.cuit}`}>
                    <td>{item.documentoId}</td>
                    <td>{item.nombreDocumento || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                    <td>{[item.numeroComercio, item.numeroTerminal].filter(Boolean).join(" / ") || "-"}</td>
                    <td>{item.estadoConciliacion || "-"}</td>
                    <td>{item.origenDatosPdf || "-"}</td>
                    <td>{item.fechaSubida || "-"}</td>
                    <td>{formatCurrencyCell(item.totalBruto)}</td>
                    <td>{item.pdfDisponible ? "Disponible" : "Pendiente"}</td>
                    <td>{item.mensajeConciliacion || "-"}</td>
                    <td>
                      <Button variant="outline-secondary" size="sm" onClick={() => descargarOrdenPago(item)} disabled={!item.pdfDisponible}>
                        Descargar PDF
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">{ordenesPago?.message || "No hay órdenes de pago para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderCupones = () => {
    if (cargas.cupones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando cupones...</div>;
    }

    if (errores.cupones) {
      return <Alert variant="danger">{errores.cupones}</Alert>;
    }

    return (
      <div className="d-grid gap-3">
        <div className="crm-postventa-actions-row">
          <div className="crm-postventa-form-grid">
            <Form.Group>
              <Form.Label>Mes</Form.Label>
              <Form.Control
                type="month"
                value={cuponesFiltros.mes}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, mes: event.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Buscador local</Form.Label>
              <Form.Control
                value={cuponesFiltros.search}
                onChange={(event) => setCuponesFiltros((prev) => ({ ...prev, search: event.target.value }))}
                placeholder="Cupón, tarjeta, comercio, CUIT o provincia"
              />
            </Form.Group>
          </div>
          <Button variant="outline-secondary" onClick={() => cargarCupones(clienteSeleccionadoId, cuponesFiltros)}>
            Actualizar
          </Button>
        </div>

        <div className="crm-postventa-subtle">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} | Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-card-grid">
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Cantidad de cupones</span>
            <div className="crm-postventa-card-value">{cupones?.resumen?.cantidad ?? 0}</div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Total bruto</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {formatCurrencyCell(cupones?.resumen?.totalBruto ?? 0)}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha operación</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaOperacionMin || "-"} a {cupones?.resumen?.fechaOperacionMax || "-"}
            </div>
          </article>
          <article className="crm-postventa-card">
            <span className="crm-postventa-card-label">Fecha pago</span>
            <div className="crm-postventa-card-value" style={{ fontSize: 18 }}>
              {cupones?.resumen?.fechaPagoMin || "-"} a {cupones?.resumen?.fechaPagoMax || "-"}
            </div>
          </article>
        </div>

        <div className="crm-postventa-subtle d-none">
          Mes consultado: {cupones?.mesConsultado || formatLegacyMonthLabel(cuponesFiltros.mes) || "-"} · Resultado: {cupones?.filas?.length || 0} cupon(es)
        </div>

        <div className="crm-postventa-subtle d-none">
          Rango: {cupones?.fechaDesde || cuponesFiltros.desde || "-"} a {cupones?.fechaHasta || cuponesFiltros.hasta || "-"} · Resultado: {cupones?.filas?.length || 0} cupón(es)
        </div>

        <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll">
          <Table className="crm-postventa-table mb-0" responsive hover>
            <thead>
              <tr>
                <th>Fecha operación</th>
                <th>Fecha de pago</th>
                <th>N° cupón</th>
                <th>N° comercio</th>
                <th>N° tarjeta</th>
                <th>Bruto</th>
                <th>Cuotas</th>
                <th>Tarjeta</th>
                <th>Provincia</th>
                <th>Comercio</th>
                <th>CUIT</th>
              </tr>
            </thead>
            <tbody>
              {cupones?.filas?.length ? (
                cupones.filas.map((item) => (
                  <tr key={buildCuponDedupeKey(item)}>
                    <td>{item.fechaOperacion || "-"}</td>
                    <td>{item.fechaDePago || "-"}</td>
                    <td>{item.nroDeCupon || "-"}</td>
                    <td>{item.nroDeComercio || "-"}</td>
                    <td>{item.nroDeTarjeta || "-"}</td>
                    <td>{currency(item.totalBruto)}</td>
                    <td>{item.cuotas || "-"}</td>
                    <td>{item.tarjeta || "-"}</td>
                    <td>{item.provincia || "-"}</td>
                    <td>{item.nombreComercio || "-"}</td>
                    <td>{item.cuit || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">{cupones?.message || "No hay cupones para mostrar."}</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderHerramientas = () => {
    const tools = [
      {
        title: "Simulador",
        description: "Acceso directo al simulador comercial actual del CRM.",
        path: "/administracion/AdminComparador",
      },
      {
        title: "Comparador",
        description: "Abrí el comparador actual sin duplicar lógica ni rutas.",
        path: "/administracion/AdminSimulador",
      },
      {
        title: "Simulador de órdenes",
        description: "Ingreso directo al simulador de liquidaciones / órdenes vigente.",
        path: "/admin/Simuladorliquidacion",
      },
    ];

    return (
      <div className="crm-postventa-tool-grid">
        {tools.map((tool) => (
          <article className="crm-postventa-tool-card" key={tool.path}>
            <span className="crm-postventa-card-label">Herramienta</span>
            <h4>{tool.title}</h4>
            <p>{tool.description}</p>
            <Button variant="success" onClick={() => navigate(tool.path)}>
              <FontAwesomeIcon className="me-2" icon={faArrowUpRightFromSquare} />
              Abrir
            </Button>
          </article>
        ))}
      </div>
    );
  };

  const renderAltasBajas = () => {
    return (
      <div className="crm-postventa-workflow">
        <div className="crm-postventa-subtabs">
          <button
            type="button"
            className={`crm-postventa-subtab ${altasBajasMode === "alta" ? "is-active" : ""}`}
            onClick={() => activateAltaMode("alta")}
          >
            Alta guiada
          </button>
          <button
            type="button"
            className={`crm-postventa-subtab ${altasBajasMode === "agrega" ? "is-active" : ""}`}
            onClick={() => activateAltaMode("agrega")}
          >
            Agregar terminal a comercio existente
          </button>
          <button
            type="button"
            className={`crm-postventa-subtab ${altasBajasMode === "baja" ? "is-active" : ""}`}
            onClick={() => setAltasBajasMode("baja")}
          >
            Baja por terminal
          </button>
          <Button type="button" variant="outline-secondary" size="sm" onClick={resetAltasBajas}>
            Reiniciar
          </Button>
        </div>

        {altasBajasMode !== "baja" ? (
          <div className="d-grid gap-4">
            {!isAltaAgregaMode ? (
            <div className="crm-postventa-stepper">
              {[
                { id: 1, title: "Blanqueo", detail: altaWizardStatus.blanqueo || "Crear o reutilizar usuario por CUIT." },
                { id: 2, title: "Legajo", detail: altaWizardStatus.legajo || "Guardar legajo y cargar documentos." },
                { id: 3, title: "Alta CRM", detail: altaWizardStatus.alta || "Completar datos operativos y ejecutar el alta." },
              ].map((step) => (
                <article
                  key={step.id}
                  className={`crm-postventa-step-card ${altaWizardStep === step.id ? "is-active" : ""} ${altaWizardStep > step.id ? "is-done" : ""}`}
                >
                  <span>{step.id}</span>
                  <strong>{step.title}</strong>
                  <p>{step.detail}</p>
                </article>
              ))}
            </div>
            ) : null}

            <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
              {!isAltaAgregaMode && altaWizardStep === 1 ? (
                <Form className="crm-postventa-form-grid" onSubmit={handleAltaBlanqueoSubmit}>
                  <div className="crm-postventa-panel-title">
                    <h3>Paso 1. Blanqueo</h3>
                  </div>
                  <Form.Group>
                    <Form.Label>CUIT</Form.Label>
                    <Form.Control
                      value={altaWizardData.usuario.cuit}
                      onChange={(event) => handleAltaWizardUserChange("cuit", event.target.value)}
                      maxLength={11}
                      placeholder="Solo números"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nombre / razón social</Form.Label>
                    <Form.Control
                      value={altaWizardData.usuario.nombre}
                      onChange={(event) => handleAltaWizardUserChange("nombre", event.target.value)}
                      placeholder="Nombre visible del usuario"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={altaWizardData.usuario.email}
                      onChange={(event) => handleAltaWizardUserChange("email", event.target.value)}
                      placeholder="mail@comercio.com"
                    />
                  </Form.Group>
                  <div className="crm-postventa-inline-note">
                    El paso busca primero por CUIT en el flujo viejo. Si el usuario ya existe, lo reutiliza y actualiza; si no existe, lo crea.
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button type="submit" variant="success" disabled={cargas.guardandoBlanqueo}>
                      {cargas.guardandoBlanqueo ? "Guardando..." : "Guardar y continuar"}
                    </Button>
                  </div>
                </Form>
              ) : null}

              {!isAltaAgregaMode && altaWizardStep === 2 ? (
                <div className="d-grid gap-4">
                  <Form className="crm-postventa-form-grid" onSubmit={handleAltaLegajoSubmit}>
                    <div className="crm-postventa-panel-title">
                      <h3>Paso 2. Legajo</h3>
                    </div>
                    {altaWizardLegajos.length ? (
                      <Form.Group>
                        <Form.Label>Legajo existente para este usuario</Form.Label>
                        <Form.Select
                          value={altaWizardData.legajoId || ""}
                          onChange={handleAltaLegajoSelect}
                        >
                          <option value="">Crear uno nuevo</option>
                          {altaWizardLegajos.map((item) => (
                            <option key={item.id ?? item.Id} value={item.id ?? item.Id}>
                              {item.nroLegajo ?? item.NroLegajo} - {item.nombreDeFantasia ?? item.NombreDeFantasia}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    ) : null}

                    <Form.Group>
                      <Form.Label>N° de legajo sugerido</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.nroLegajo}
                        onChange={(event) => handleAltaWizardLegajoChange("nroLegajo", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Terminal</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.terminal}
                        onChange={(event) => handleAltaWizardLegajoChange("terminal", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Nombre de fantasía</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.nombreDeFantasia}
                        onChange={(event) => handleAltaWizardLegajoChange("nombreDeFantasia", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Razón social</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.razonSocial}
                        onChange={(event) => handleAltaWizardLegajoChange("razonSocial", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Tipo persona</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.tipoPersona}
                        onChange={(event) => handleAltaWizardLegajoChange("tipoPersona", event.target.value)}
                      >
                        <option value="fisica">Física</option>
                        <option value="juridica">Jurídica</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Provincia</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.provincia}
                        onChange={(event) => handleAltaWizardLegajoChange("provincia", event.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {fichaCatalogos.provincias.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Calle</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.calle}
                        onChange={(event) => handleAltaWizardLegajoChange("calle", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Número</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.numero}
                        onChange={(event) => handleAltaWizardLegajoChange("numero", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Piso / Depto</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.pisoDepto}
                        onChange={(event) => handleAltaWizardLegajoChange("pisoDepto", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Localidad</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.localidad}
                        onChange={(event) => handleAltaWizardLegajoChange("localidad", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Código postal</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.codigoPostal}
                        onChange={(event) => handleAltaWizardLegajoChange("codigoPostal", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Email contacto</Form.Label>
                      <Form.Control
                        type="email"
                        value={altaWizardData.legajo.emailContacto}
                        onChange={(event) => handleAltaWizardLegajoChange("emailContacto", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Celular / WhatsApp</Form.Label>
                      <Form.Control
                        value={altaWizardData.legajo.celularWhatsapp}
                        onChange={(event) => handleAltaWizardLegajoChange("celularWhatsapp", event.target.value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Asesor / gestor</Form.Label>
                      <Form.Select
                        value={altaWizardData.legajo.asesor}
                        onChange={(event) => handleAltaWizardLegajoChange("asesor", event.target.value)}
                      >
                        <option value="">Seleccionar</option>
                        {fichaCatalogos.asesores.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(1)}>
                        Volver
                      </Button>
                      <Button type="submit" variant="success" disabled={cargas.guardandoLegajo || cargas.altaBajaCatalogos}>
                        {cargas.guardandoLegajo ? "Guardando..." : "Guardar legajo y continuar"}
                      </Button>
                    </div>
                  </Form>

                  <section className={`crm-postventa-detail-card ${darkMode ? "is-dark" : ""}`}>
                    <div className="crm-postventa-panel-title">
                      <h4>Documentos del legajo</h4>
                      <span className="crm-postventa-card-label">
                        {altaWizardData.legajoId ? `Legajo #${altaWizardData.legajoId}` : "Guardá el legajo para habilitar archivos"}
                      </span>
                    </div>
                    <Form className="crm-postventa-docs-grid" onSubmit={handleAltaLegajoDocumento}>
                      <Form.Group>
                        <Form.Label>Tipo documento</Form.Label>
                        <Form.Select
                          value={altaLegajoDocDraft.nombreDocumento}
                          onChange={(event) => setAltaLegajoDocDraft((prev) => ({ ...prev, nombreDocumento: event.target.value }))}
                          disabled={!altaWizardData.legajoId}
                        >
                          {ALTA_LEGAJO_DOC_TYPES.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Archivo</Form.Label>
                        <Form.Control
                          type="file"
                          disabled={!altaWizardData.legajoId}
                          onChange={(event) =>
                            setAltaLegajoDocDraft((prev) => ({
                              ...prev,
                              file: event.target.files?.[0] || null,
                            }))
                          }
                        />
                      </Form.Group>
                      <div className="d-flex gap-2 flex-wrap align-items-end">
                        <Button type="submit" variant="outline-success" disabled={!altaWizardData.legajoId || cargas.guardandoDocumentoLegajo}>
                          {cargas.guardandoDocumentoLegajo ? "Subiendo..." : "Subir documento"}
                        </Button>
                        <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(3)} disabled={!altaWizardData.legajoId}>
                          Continuar al alta
                        </Button>
                      </div>
                    </Form>

                    <div className="crm-postventa-table-wrap mt-3">
                      <Table className="crm-postventa-table mb-0" responsive hover>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Documento</th>
                            <th>Tipo</th>
                            <th>Tamaño</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {altaLegajoDocs.length ? (
                            altaLegajoDocs.map((item) => (
                              <tr key={item.idDocumento ?? item.IdDocumento}>
                                <td>{item.idDocumento ?? item.IdDocumento}</td>
                                <td>{item.nombreDocumento ?? item.NombreDocumento}</td>
                                <td>{item.documentoTipo ?? item.DocumentoTipo ?? "-"}</td>
                                <td>{item.bytes ?? item.Bytes ?? 0}</td>
                                <td>
                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleEliminarDocumentoLegajo(item.idDocumento ?? item.IdDocumento)}
                                  >
                                    Eliminar
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-4 text-muted">
                                No hay documentos cargados todavía.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </section>
                </div>
              ) : null}

              {isAltaAgregaMode || altaWizardStep === 3 ? (
                <Form className="crm-postventa-form-grid" onSubmit={handleAltaWorkflowSubmit}>
                  <div className="crm-postventa-panel-title">
                    <h3>{isAltaAgregaMode ? "Alta terminal en comercio existente" : "Paso 3. Alta CRM"}</h3>
                  </div>
                  {isAltaAgregaMode ? (
                    <div className="crm-postventa-step-block crm-postventa-form-span-2">
                      <div className="crm-postventa-step-block-title">
                        <h4>Base para AGREGA</h4>
                        <span>Seleccioná una terminal o comercio existente para copiar sus datos fijos.</span>
                      </div>
                      <div className="crm-postventa-step-block-grid">
                        <Form.Group className="crm-postventa-form-span-2">
                          <Form.Label>Buscar terminal / comercio</Form.Label>
                          <div className="d-flex gap-2 flex-wrap">
                            <Form.Control
                              value={altaAgregaSearch}
                              onChange={(event) => setAltaAgregaSearch(event.target.value)}
                              placeholder="Comercio, terminal, CUIT o nombre"
                            />
                            <Button type="button" variant="outline-secondary" onClick={buscarBaseAgrega} disabled={altaAgregaLoading}>
                              {altaAgregaLoading ? "Buscando..." : "Buscar"}
                            </Button>
                          </div>
                        </Form.Group>
                        {altaAgregaResults.length ? (
                          <div className="crm-postventa-step-block-results crm-postventa-form-span-2">
                            {altaAgregaResults.slice(0, 8).map((item) => (
                              <Button
                                key={`${item.clienteId}-${item.nDeTerminal}-${item.nDeComercio}`}
                                type="button"
                                variant="outline-success"
                                className="text-start"
                                onClick={() => seleccionarBaseAgrega(item)}
                              >
                                {(item.nombreDeFantasia || item.razonSocial || "Sin nombre")} | Comercio {item.nDeComercio || "-"} | Terminal {item.nDeTerminal || "-"}
                              </Button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {isAltaAgregaMode ? (
                    <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                      Modo AGREGA activo. Este flujo reutiliza el alta legacy, copia los datos fijos del comercio base y deja editable solo la nueva terminal y los campos necesarios.
                    </div>
                  ) : null}
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Datos personales / comercio</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>N° terminal</Form.Label>
                    <Form.Control
                      value={altaWizardData.legajo.terminal}
                      readOnly={!isAltaAgregaMode}
                      onChange={isAltaAgregaMode ? (event) => handleAltaWizardLegajoChange("terminal", event.target.value) : undefined}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Legajo</Form.Label>
                    <Form.Control value={altaWizardData.legajo.nroLegajo} readOnly />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Control value={altaWizardData.alta.estado || "ALTA"} readOnly />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Mail</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.correo}
                      onChange={(event) => handleAltaWizardAltaChange("correo", event.target.value)}
                    />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Contacto</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Teléfono 1</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.celular}
                      onChange={(event) => handleAltaWizardAltaChange("celular", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Puesto / cargo / referencia 1</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.referenciaTelefono}
                      onChange={(event) => handleAltaWizardAltaChange("referenciaTelefono", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Teléfono 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.telefonoAlternativo || altaWizardData.alta.telFijo}
                      onChange={(event) => handleAltaWizardAltaChange("telefonoAlternativo", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Puesto / cargo / referencia 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.referenciaTelefonoAlternativo}
                      onChange={(event) => handleAltaWizardAltaChange("referenciaTelefonoAlternativo", event.target.value)}
                    />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Terminal / comercio</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Nombre del asesor</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.nombreDelAsesor}
                      onChange={(event) => handleAltaWizardAltaChange("nombreDelAsesor", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.asesores.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Provincia</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.provincia}
                      onChange={(event) => handleAltaWizardAltaChange("provincia", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.provincias.map((item) => (
                        <option key={`provincia-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Provincia fiscal</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.provincia3}
                      onChange={(event) => handleAltaWizardAltaChange("provincia3", event.target.value)}
                      disabled={fichaCatalogosLoading}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.provincias.map((item) => (
                        <option key={`provincia3-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Alicuota</Form.Label>
                    <Form.Control value={altaWizardData.alta.alicuota} readOnly />
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>AFIP</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Condición AFIP</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.condicionAFIP}
                      onChange={(event) => handleAltaWizardAltaChange("condicionAFIP", event.target.value)}
                    >
                      <option value="Monotributista">Monotributista</option>
                      <option value="Responsable Inscripto">Responsable Inscripto</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad laboral / profesional</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividadLaboralProfesional}
                      onChange={(event) => handleAltaWizardAltaChange("actividadLaboralProfesional", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Estado AFIP</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.estadoAfip}
                      onChange={(event) => handleAltaWizardAltaChange("estadoAfip", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.estadoAfip.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Exento</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.exento}
                      onChange={(event) => handleAltaWizardAltaChange("exento", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.exento.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Rentas</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Estado rentas</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.estadoRentas}
                      onChange={(event) => handleAltaWizardAltaChange("estadoRentas", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {FICHA_FIXED_OPTIONS.estadoRentas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Domicilio comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.domicilioComercio}
                      onChange={(event) => handleAltaWizardAltaChange("domicilioComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Localidad comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.localidadComercio}
                      onChange={(event) => handleAltaWizardAltaChange("localidadComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código postal comercio</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codigoPostalComercio}
                      onChange={(event) => handleAltaWizardAltaChange("codigoPostalComercio", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Link de pago</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.linkDePago}
                      onChange={(event) => handleAltaWizardAltaChange("linkDePago", event.target.value)}
                    >
                      {FICHA_FIXED_OPTIONS.siNo.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Bancario</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Banco</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.banco}
                      onChange={(event) => handleAltaWizardAltaChange("banco", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.bancos.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Tipo cuenta</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.tipoCuenta}
                      onChange={(event) => handleAltaWizardAltaChange("tipoCuenta", event.target.value)}
                    >
                      {LEGACY_ALTA_TIPO_CUENTA_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>CBU / CVU</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.cbucvu}
                      onChange={(event) => handleAltaWizardAltaChange("cbucvu", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Alias</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.alias}
                      onChange={(event) => handleAltaWizardAltaChange("alias", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>CUIT bancario</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.cuitbancario}
                      onChange={(event) => handleAltaWizardAltaChange("cuitbancario", event.target.value)}
                      placeholder="Acepta 20-XXXXXXXX-X o 11 dÃ­gitos"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Titular / razón social bancaria</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.titularRazonSocial}
                      onChange={(event) => handleAltaWizardAltaChange("titularRazonSocial", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° comercio solicitado</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.numeroComercioSolicitado}
                      onChange={(event) => handleAltaWizardAltaChange("numeroComercioSolicitado", event.target.value)}
                      disabled={isAltaAgregaMode}
                    >
                      <option value="">Seleccionar</option>
                      {altaWizardComercioOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° comercio actual</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.numeroComericoActual}
                      readOnly={isAltaAgregaMode}
                      onChange={isAltaAgregaMode ? undefined : (event) => handleAltaWizardAltaChange("numeroComericoActual", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Tipo alta</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.tipoAlta}
                      onChange={(event) => handleAltaWizardAltaChange("tipoAlta", event.target.value)}
                      disabled={isAltaAgregaMode}
                    >
                      <option value="NuevoAliado">Nuevo aliado</option>
                      <option value="AgregaTerminal">Agrega terminal</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Rubro</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.rubro}
                      onChange={(event) => handleAltaWizardAltaChange("rubro", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.rubros.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Altas</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.altas}
                      onChange={(event) => handleAltaWizardAltaChange("altas", event.target.value)}
                      disabled={isAltaAgregaMode}
                    >
                      {FICHA_FIXED_OPTIONS.altas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <div className="crm-postventa-step-block-title crm-postventa-form-span-2">
                    <h4>Alta / baja / operación</h4>
                  </div>
                  <Form.Group>
                    <Form.Label>Baja</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.baja}
                      onChange={(event) => handleAltaWizardAltaChange("baja", event.target.value)}
                    >
                      <option value="">Sin valor</option>
                      {FICHA_FIXED_OPTIONS.bajas.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Rubro secundario</Form.Label>
                    <Form.Select
                      value={altaWizardData.alta.rubroSecundario}
                      onChange={(event) => handleAltaWizardAltaChange("rubroSecundario", event.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      {fichaCatalogos.rubros.map((item) => (
                        <option key={`rubro-sec-${item.value}`} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>N° local</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.nroLocal}
                      onChange={(event) => handleAltaWizardAltaChange("nroLocal", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código actividad</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codActividad}
                      onChange={(event) => handleAltaWizardAltaChange("codActividad", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividad}
                      onChange={(event) => handleAltaWizardAltaChange("actividad", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad 2</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividad2}
                      onChange={(event) => handleAltaWizardAltaChange("actividad2", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Código actividad rentas</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.codActividadRentas}
                      onChange={(event) => handleAltaWizardAltaChange("codActividadRentas", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Actividad rentas</Form.Label>
                    <Form.Control
                      value={altaWizardData.alta.actividadRentas}
                      onChange={(event) => handleAltaWizardAltaChange("actividadRentas", event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="crm-postventa-form-span-2">
                    <Form.Label>Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={altaWizardData.alta.observaciones}
                      onChange={(event) => handleAltaWizardAltaChange("observaciones", event.target.value)}
                      placeholder="Observaciones que quedarán en el alta final."
                    />
                  </Form.Group>
                  <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                    El paso final crea el formulario de alta con los datos ya cargados en Blanqueo + Legajo y luego ejecuta el alta real del CRM viejo para no romper la lógica existente.
                  </div>
                  <div className="d-flex gap-2 flex-wrap crm-postventa-form-span-2">
                    {!isAltaAgregaMode ? (
                    <Button type="button" variant="outline-secondary" onClick={() => setAltaWizardStep(2)}>
                      Volver al legajo
                    </Button>
                    ) : null}
                    <Button type="submit" variant="success" disabled={cargas.guardandoAltaWorkflow}>
                      {cargas.guardandoAltaWorkflow ? "Generando alta..." : "Generar alta CRM"}
                    </Button>
                  </div>
                </Form>
              ) : null}
            </section>
          </div>
        ) : (
          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Baja por terminal</h3>
            </div>
            <Form className="crm-postventa-form-grid" onSubmit={handleBajaWorkflowSubmit}>
              <Form.Group>
                <Form.Label>Buscar terminal o comercio</Form.Label>
                <Form.Control
                  value={bajaLookupSearch}
                  onChange={(event) => setBajaLookupSearch(event.target.value)}
                  placeholder="Ingresar terminal, comercio o razón social"
                />
              </Form.Group>
              <div className="d-flex gap-2 flex-wrap align-items-end">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => buscarOpcionesBaja(bajaLookupSearch)}
                  disabled={bajaLookupLoading}
                >
                  {bajaLookupLoading ? "Buscando..." : "Buscar"}
                </Button>
              </div>
              {bajaLookupResults.length ? (
                <Form.Group className="crm-postventa-form-span-2">
                  <Form.Label>Resultados</Form.Label>
                  <Form.Select defaultValue="" onChange={handleSeleccionBajaResultado}>
                    <option value="">Seleccionar una terminal</option>
                    {bajaLookupResults.map((item) => (
                      <option key={item.key} value={item.key}>
                        {item.terminal || "-"} {item.comercio ? `| comercio ${item.comercio}` : ""} {item.nombre ? `| ${item.nombre}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : null}
              <Form.Group>
                <Form.Label>Terminal</Form.Label>
                <Form.Control
                  value={bajaDraft.terminal}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, terminal: event.target.value }))}
                  placeholder="Ingresar número de terminal"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Motivo</Form.Label>
                <Form.Select
                  value={bajaDraft.motivo}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, motivo: event.target.value }))}
                >
                  <option value="BAJA">Baja</option>
                  <option value="BAJA POR FACTURACION">Baja por facturación</option>
                  <option value="BAJA POR POSNET">Baja por Posnet</option>
                  <option value="BAJA POR CAMBIO DE TERMINAL">Baja por cambio de terminal</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha de baja</Form.Label>
                <Form.Control
                  type="date"
                  value={String(bajaDraft.fechaBaja || "").replace(/\//g, "-")}
                  onChange={(event) =>
                    setBajaDraft((prev) => ({
                      ...prev,
                      fechaBaja: event.target.value.replace(/-/g, "/"),
                    }))
                  }
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observación</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={bajaDraft.observacion}
                  onChange={(event) => setBajaDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                  placeholder="Motivo operativo y contexto de la baja."
                />
              </Form.Group>
              <div className="crm-postventa-inline-note crm-postventa-form-span-2">
                Esta acción reutiliza el flujo viejo de Gestionar terminales (`/api/abm/baja`). La búsqueda es opcional: la baja se ejecuta siempre con la terminal cargada en el formulario.
              </div>
              <div className="d-flex gap-2 flex-wrap crm-postventa-form-span-2">
                <Button type="submit" variant="danger" disabled={cargas.guardandoBajaWorkflow}>
                  {cargas.guardandoBajaWorkflow ? "Procesando baja..." : "Registrar baja por terminal"}
                </Button>
              </div>
            </Form>
          </section>
        )}
      </div>
    );
  };

  const renderGestion = () => {
    if (cargas.gestiones) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando gestiones...</div>;
    }

    if (errores.gestiones) {
      return <Alert variant="danger">{errores.gestiones}</Alert>;
    }

    const historial = Array.isArray(gestiones?.historial) ? gestiones.historial : [];
    const ultimaGestion = gestiones?.ultimaGestion || historial[0] || null;
    const rollosHistorial = Array.isArray(gestiones?.rollos?.historial) ? gestiones.rollos.historial : [];

    const focusGestionComposer = (scope = "ipc") => {
      const rules = gestionScopeRules[scope] || [];
      const states = GESTION_SCOPE_STATES[scope] || GESTION_STATES;

      setGestionComposerScope(scope);
      setGestionDraft((prev) => ({
        ...prev,
        reglaId: rules?.[0]?.id ? String(rules[0].id) : prev.reglaId,
        estadoGestion: states.includes(prev.estadoGestion) ? prev.estadoGestion : (states[0] || "Pendiente"),
        tipoIpc: scope === "ipc" ? "" : prev.tipoIpc,
        ipcMotivo: scope === "ipc" ? "" : prev.ipcMotivo,
      }));

      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          document.getElementById("crm-postventa-gestion-form")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    };

    const resetGestionForm = () =>
      setGestionDraft({
        reglaId: currentGestionRules?.[0]?.id ? String(currentGestionRules[0].id) : "",
        estadoGestion: currentGestionStates[0] || "Pendiente",
        tipoIpc: "",
        ipcMotivo: "",
        canalContacto: "",
        resultadoLlamada: "",
        evidenciaContacto: "",
        observacion: "",
        fechaGestion: makeNowDateTimeLocal(),
        fechaProximoContacto: "",
      });

    const renderGestionScopeSummary = (scope) => {
      const items = gestionesPorScope[scope] || [];
      const latest = items[0];
      const rules = gestionScopeRules[scope] || [];

      return (
        <section key={scope} className={`crm-postventa-panel crm-postventa-gestion-summary-card ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <div>
              <h4>{GESTION_SCOPE_LABELS[scope]}</h4>
              <span className="crm-postventa-card-label">{items.length} registro(s)</span>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!rules.length}
              onClick={() => focusGestionComposer(scope)}
            >
              Nueva gestion
            </Button>
          </div>
          {latest ? (
            <>
              <dl className="crm-postventa-gestion-mini-list">
                <div>
                  <dt>Ultima gestion</dt>
                  <dd>{latest.fechaHora || "-"}</dd>
                </div>
                <div>
                  <dt>Proximo contacto</dt>
                  <dd>{latest.proximoContacto || "-"}</dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Motivo</dt>
                  <dd title={latest.motivo || "-"}>
                    {truncateText(latest.motivo, 92)}
                  </dd>
                </div>
              </dl>
              <div className="d-flex gap-2 flex-wrap mt-3">
                <PostventaBadge value={latest.estado} tone={toneForGestionState(latest.estado)} />
                <PostventaBadge value={latest.prioridad} tone={toneForPriority(latest.prioridad)} />
              </div>
            </>
          ) : (
            <Alert variant="secondary" className="mb-0">{GESTION_SCOPE_EMPTY_MESSAGES[scope]}</Alert>
          )}
        </section>
      );
    };

    return (
      <div className="crm-postventa-gestion-grid crm-postventa-gestion-layout">
        <section className={`crm-postventa-panel crm-postventa-gestion-sidebar-panel ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Última gestión</h3>
          </div>
          {ultimaGestion ? (
            <>
              <dl className="crm-postventa-gestion-mini-list">
                <div>
                  <dt>Fecha y hora</dt>
                  <dd>{ultimaGestion.fechaHora || "-"}</dd>
                </div>
                <div>
                  <dt>Usuario</dt>
                  <dd>{ultimaGestion.usuario || "-"}</dd>
                </div>
                <div>
                  <dt>Tipo</dt>
                  <dd>{getGestionTypeLabel(ultimaGestion)}</dd>
                </div>
                <div>
                  <dt>Telefono</dt>
                  <dd>{ultimaGestion.telefono || "-"}</dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Motivo</dt>
                  <dd title={ultimaGestion.motivo || "-"}>{truncateText(ultimaGestion.motivo, 110)}</dd>
                </div>
                <div>
                  <dt>Estado</dt>
                  <dd>
                    <PostventaBadge value={ultimaGestion.estado} tone={toneForGestionState(ultimaGestion.estado)} />
                  </dd>
                </div>
                <div>
                  <dt>Prioridad</dt>
                  <dd>
                    <PostventaBadge value={ultimaGestion.prioridad} tone={toneForPriority(ultimaGestion.prioridad)} />
                  </dd>
                </div>
                <div className="crm-postventa-gestion-mini-list-span-2">
                  <dt>Proximo contacto</dt>
                  <dd>{ultimaGestion.proximoContacto || "-"}</dd>
                </div>
              </dl>
              <div className="crm-postventa-gestion-preview-grid">
                <div className="crm-postventa-gestion-preview-block">
                  <span className="crm-postventa-card-label">Accion sugerida</span>
                  <p className="crm-postventa-gestion-preview-text" title={ultimaGestion.accionSugerida || "-"}>
                    {truncateText(ultimaGestion.accionSugerida, 120)}
                  </p>
                </div>
                <div className="crm-postventa-gestion-preview-block">
                  <span className="crm-postventa-card-label">Mensaje sugerido</span>
                  <p className="crm-postventa-gestion-preview-text" title={ultimaGestion.mensajeSugerido || "-"}>
                    {truncateText(ultimaGestion.mensajeSugerido, 140)}
                  </p>
                </div>
              </div>
              <div className="crm-postventa-gestion-latest-actions">
                <Button variant="outline-secondary" size="sm" onClick={() => copiarMensajeSugerido(ultimaGestion, "Gestiones")}>
                  Copiar mensaje
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirWhatsappSugerido(ultimaGestion, "Gestiones")}>
                  Abrir WhatsApp
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Contactado", buildSuggestedObservation(ultimaGestion, "Contactado"))}>
                  Contactado
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Pendiente", buildSuggestedObservation(ultimaGestion, "Pendiente"))}>
                  Posponer
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Cerrado", buildSuggestedObservation(ultimaGestion, "Cerrado"))}>
                  Cerrar
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => abrirAccionAlerta(ultimaGestion, "Escalado", buildSuggestedObservation(ultimaGestion, "Escalado"))}>
                  Escalar
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setGestionDetalleItem(ultimaGestion)}>
                  Ver detalle
                </Button>
              </div>
            </>
          ) : (
            <Alert variant="secondary">Todavia no hay gestiones de postventa para este cliente.</Alert>
          )}
          {false ? (
            <DataList
              items={[
                { label: "Fecha y hora", value: gestiones.ultimaGestion.fechaHora },
                { label: "Usuario", value: gestiones.ultimaGestion.usuario },
                { label: "Motivo", value: gestiones.ultimaGestion.motivo },
                { label: "Motivo código", value: gestiones.ultimaGestion.motivoCodigo || "-" },
                { label: "Tipo", value: gestiones.ultimaGestion.esAutomatica ? "Automática" : (gestiones.ultimaGestion.tipo || "Manual") },
                {
                  label: "Prioridad",
                  value: gestiones.ultimaGestion.prioridad,
                  isBadge: true,
                  tone: toneForPriority(gestiones.ultimaGestion.prioridad),
                },
                {
                  label: "Estado",
                  value: gestiones.ultimaGestion.estado,
                  isBadge: true,
                  tone: toneForGestionState(gestiones.ultimaGestion.estado),
                },
                { label: "Descripción", value: gestiones.ultimaGestion.descripcion },
                { label: "Acción sugerida", value: gestiones.ultimaGestion.accionSugerida || "-" },
                { label: "Mensaje sugerido", value: gestiones.ultimaGestion.mensajeSugerido || "-" },
                { label: "Teléfono", value: gestiones.ultimaGestion.telefono || "-" },
                { label: "Próximo contacto", value: gestiones.ultimaGestion.proximoContacto },
              ]}
            />
          ) : (
            <Alert variant="secondary">Todavía no hay gestiones de postventa para este cliente.</Alert>
          )}

          {false && gestiones?.ultimaGestion ? (
            <div className="d-flex gap-2 flex-wrap mt-3">
              <Button variant="outline-secondary" onClick={() => copiarMensajeSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Copiar mensaje
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirWhatsappSugerido(gestiones.ultimaGestion, "Gestiones")}>
                Abrir WhatsApp
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Contactado", buildSuggestedObservation(gestiones.ultimaGestion, "Contactado"))}>
                Marcar como contactado
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Pendiente", buildSuggestedObservation(gestiones.ultimaGestion, "Pendiente"))}>
                Posponer
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Cerrado", buildSuggestedObservation(gestiones.ultimaGestion, "Cerrado"))}>
                Cerrar
              </Button>
              <Button variant="outline-secondary" onClick={() => abrirAccionAlerta(gestiones.ultimaGestion, "Escalado", buildSuggestedObservation(gestiones.ultimaGestion, "Escalado"))}>
                Escalar
              </Button>
            </div>
          ) : null}

          <div className="crm-postventa-rollos crm-postventa-gestion-rollos mt-4">
            <div className="crm-postventa-panel-title mb-3">
              <div>
                <h4>Seguimiento de rollos</h4>
                <span className="crm-postventa-card-label">Bloque secundario y compacto.</span>
              </div>
              <div className="crm-postventa-gestion-rollos-actions">
                <Button variant="outline-secondary" size="sm" onClick={() => setGestionesRollosExpanded((prev) => !prev)}>
                  {gestionesRollosExpanded ? "Ocultar historial" : "Ver historial"}
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleRegistrarRollo}>
                  Registrar entrega
                </Button>
              </div>
            </div>
            <DataList
              items={[
                { label: "Ultima entrega", value: gestiones?.rollos?.ultimaEntrega || "-" },
                {
                  label: "Cantidad ultima entrega",
                  value: gestiones?.rollos?.cantidadUltimaEntrega > 0 ? String(gestiones.rollos.cantidadUltimaEntrega) : "-",
                },
                { label: "Observacion ultima entrega", value: gestiones?.rollos?.observacionUltimaEntrega || "-" },
              ]}
            />
            {gestionesRollosExpanded ? (
            <div className="crm-postventa-table-wrap crm-postventa-gestion-rollos-table-wrap mt-3">
              <Table className="crm-postventa-table mb-0" responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Cantidad</th>
                    <th>Usuario</th>
                    <th>Observacion</th>
                  </tr>
                </thead>
                <tbody>
                  {gestiones?.rollos?.historial?.length ? (
                    gestiones.rollos.historial.map((item) => (
                      <tr key={`rollo-${item.id}`}>
                        <td>{item.fechaEntrega}</td>
                        <td>{item.cantidad}</td>
                        <td>{item.usuario}</td>
                        <td>
                          <span className="crm-postventa-gestion-text-cell" title={item.observacion || "-"}>
                            {truncateText(item.observacion, 96)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4 text-muted">
                        No hay entregas registradas para este cliente.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            ) : null}
          </div>
        </section>

        <section id="crm-postventa-gestion-form" className={`crm-postventa-panel crm-postventa-gestion-main-panel ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-panel-title">
            <h3>Nueva gestión</h3>
          </div>
          <div className="d-flex gap-2 flex-wrap mb-3 crm-postventa-gestion-scope-tabs">
            {["ipc", "operativa", "proactiva"].map((scope) => (
              <Button
                key={scope}
                type="button"
                variant={gestionComposerScope === scope ? "success" : "outline-secondary"}
                size="sm"
                onClick={() => setGestionComposerScope(scope)}
              >
                {GESTION_SCOPE_LABELS[scope]}
              </Button>
            ))}
          </div>
          <Form className="crm-postventa-form-grid crm-postventa-gestion-form" onSubmit={handleCrearGestion}>
            <Form.Group>
              <Form.Label>Fecha/hora</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaGestion: event.target.value }))
                }
              />
            </Form.Group>

            {gestionComposerScope === "ipc" ? (
              <Form.Group>
                <Form.Label>Tipo IPC</Form.Label>
                <Form.Select
                  value={gestionDraft.tipoIpc}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({
                      ...prev,
                      tipoIpc: event.target.value,
                      ipcMotivo: "",
                    }))
                  }
                >
                  <option value="">Seleccionar</option>
                  {GESTION_IPC_TYPES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}

            <Form.Group>
              <Form.Label>Motivo / acción</Form.Label>
              {gestionComposerScope === "ipc" ? (
                <Form.Select
                  value={gestionDraft.ipcMotivo}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({ ...prev, ipcMotivo: event.target.value }))
                  }
                  disabled={!gestionDraft.tipoIpc}
                >
                  <option value="">{gestionDraft.tipoIpc ? "Seleccionar" : "Primero seleccione Tipo IPC"}</option>
                  {currentIpcMotivoOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Select
                  value={gestionDraft.reglaId}
                  onChange={(event) =>
                    setGestionDraft((prev) => ({ ...prev, reglaId: event.target.value }))
                  }
                >
                  <option value="">Seleccionar</option>
                  {currentGestionRules.map((regla) => (
                    <option key={regla.id} value={regla.id}>
                      {formatGestionRuleLabel(regla)}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Estado de gestion</Form.Label>
              <Form.Select
                value={gestionDraft.estadoGestion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, estadoGestion: event.target.value }))
                }
              >
                {currentGestionStates.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {usaProtocoloOperativo ? (
              <>
                <Form.Group>
                  <Form.Label>Canal de contacto</Form.Label>
                  <Form.Select
                    value={gestionDraft.canalContacto}
                    onChange={(event) =>
                      setGestionDraft((prev) => ({
                        ...prev,
                        canalContacto: event.target.value,
                        resultadoLlamada: event.target.value === "Llamada" ? prev.resultadoLlamada : "",
                        evidenciaContacto: event.target.value === "Llamada" ? prev.evidenciaContacto : "",
                      }))
                    }
                  >
                    <option value="">Seleccionar</option>
                    {CONTACT_CHANNEL_OPTIONS.map((item) => {
                      const enabled = canalesProtocoloHabilitados.includes(item);
                      return (
                        <option key={item} value={item} disabled={!enabled}>
                          {enabled ? item : `${item} (bloqueado)`}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>

                <div className="crm-postventa-inline-note crm-postventa-gestion-note">
                  Proxima accion obligatoria: {protocoloContacto?.proximaAccionObligatoria || "-"} | Motivo: {protocoloContacto?.motivo || "-"} | Evidencia requerida: {gestionDraft.canalContacto === "Llamada" ? "Si" : (protocoloContacto?.evidenciaRequerida ? "Si" : "No")}
                </div>

                {gestionDraft.canalContacto === "Llamada" ? (
                  <>
                    <Form.Group>
                      <Form.Label>Resultado de llamada</Form.Label>
                      <Form.Select
                        value={gestionDraft.resultadoLlamada}
                        onChange={(event) =>
                          setGestionDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))
                        }
                      >
                        <option value="">Seleccionar</option>
                        {CALL_RESULT_OPTIONS.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>Evidencia</Form.Label>
                      <Form.Control
                        type="text"
                        value={gestionDraft.evidenciaContacto}
                        onChange={(event) =>
                          setGestionDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))
                        }
                        placeholder="Link, ruta, adjunto o referencia de evidencia"
                      />
                    </Form.Group>
                  </>
                ) : null}
              </>
            ) : null}

            <Form.Group>
              <Form.Label>Proximo contacto</Form.Label>
              <Form.Control
                type="datetime-local"
                value={gestionDraft.fechaProximoContacto}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observación</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={gestionDraft.observacion}
                onChange={(event) =>
                  setGestionDraft((prev) => ({ ...prev, observacion: event.target.value }))
                }
                placeholder="Describí brevemente la interacción con el cliente."
              />
            </Form.Group>

            <div className="crm-postventa-inline-note crm-postventa-gestion-note">
              Usuario que atiende: {gestiones?.usuarioActual || "-"}
            </div>
            <div className="crm-postventa-inline-note crm-postventa-gestion-note">
              {gestionComposerScope === "operativa"
                ? "Las alertas gestionadas salen de Alertas y solo vuelven cuando vence el proximo contacto o al dia siguiente."
                : gestionComposerScope === "proactiva"
                  ? "Las proactivas se siguen desde este bloque y no se mezclan con Alertas operativas."
                  : "Las gestiones IPC mantienen su propio historial y no suman alertas por si solas."}
            </div>

            <div className="d-flex gap-2 flex-wrap crm-postventa-gestion-form-actions">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={resetGestionForm}
              >
                Limpiar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                {cargas.guardandoGestion ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  "Guardar gestión"
                )}
              </Button>
            </div>
          </Form>

          <div className="crm-postventa-table-wrap crm-postventa-table-wrap-scroll crm-postventa-table-wrap-safe mt-4 crm-postventa-gestion-history-wrap">
            <Table className="crm-postventa-table crm-postventa-gestion-history-table mb-0" responsive hover>
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Prioridad</th>
                  <th>Usuario</th>
                  <th>Motivo</th>
                  <th>Canal</th>
                  <th>Resultado llamada</th>
                  <th>Evidencia</th>
                  <th>Proxima accion</th>
                  <th>Acción sugerida</th>
                  <th>Mensaje sugerido</th>
                  <th>Estado</th>
                  <th>Próximo contacto</th>
                  <th>Observación</th>
                </tr>
              </thead>
              <tbody>
                {gestiones?.historial?.length ? (
                  gestiones.historial.map((item) => (
                    <tr key={item.id}>
                      <td>{item.fechaHora || "-"}</td>
                      <td>
                        <div className="crm-postventa-gestion-table-stack">
                          <strong>{GESTION_SCOPE_LABELS[resolveGestionScope(item)] || "Gestion"}</strong>
                          <span>{getGestionTypeLabel(item)}</span>
                        </div>
                      </td>
                      <td>
                        <PostventaBadge value={item.prioridad} tone={toneForPriority(item.prioridad)} />
                      </td>
                      <td>{item.usuario || "-"}</td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.motivo || "-"}>
                          {truncateText(item.motivo, 72)}
                        </span>
                      </td>
                      <td>{item.canalContacto || "-"}</td>
                      <td>{item.resultadoLlamada || "-"}</td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.evidenciaContacto || "-"}>
                          {truncateText(item.evidenciaContacto, 48)}
                        </span>
                      </td>
                      <td>
                        <span className="crm-postventa-gestion-text-cell" title={item.proximaAccionObligatoria || "-"}>
                          {truncateText(item.proximaAccionObligatoria, 48)}
                        </span>
                      </td>
                      <td>{item.accionSugerida || "-"}</td>
                      <td>{item.mensajeSugerido || "-"}</td>
                      <td>
                        <PostventaBadge value={item.estado} tone={toneForGestionState(item.estado)} />
                      </td>
                      <td>{item.proximoContacto || "-"}</td>
                      <td>
                        <div className="crm-postventa-gestion-row-actions">
                          <Button size="sm" variant="outline-secondary" onClick={() => setGestionDetalleItem(item)}>
                            Ver detalle
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => abrirEditarGestion(item)} disabled={!item.puedeEditar}>
                            Editar
                          </Button>
                          <Button size="sm" variant="outline-success" onClick={() => abrirFinalizarGestion(item)} disabled={!item.puedeFinalizar}>
                            Finalizar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={14} className="text-center py-4 text-muted">
                      No hay historial de gestiones para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="crm-postventa-gestion-summary-grid mt-4">
            {["ipc", "operativa", "proactiva"].map((scope) => renderGestionScopeSummary(scope))}
          </div>

        <Modal show={Boolean(gestionDetalleItem)} onHide={() => setGestionDetalleItem(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Detalle de gestion</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-grid gap-3">
            <div className="crm-postventa-detail-meta">
              <div>
                <span className="crm-postventa-card-label">Fecha</span>
                <strong>{gestionDetalleItem?.fechaHora || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Usuario</span>
                <strong>{gestionDetalleItem?.usuario || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Tipo</span>
                <strong>{getGestionTypeLabel(gestionDetalleItem)}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Scope</span>
                <strong>{GESTION_SCOPE_LABELS[resolveGestionScope(gestionDetalleItem)] || "Gestion"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Estado</span>
                <strong>{gestionDetalleItem?.estado || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Prioridad</span>
                <strong>{gestionDetalleItem?.prioridad || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Telefono</span>
                <strong>{gestionDetalleItem?.telefono || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Proximo contacto</span>
                <strong>{gestionDetalleItem?.proximoContacto || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Canal</span>
                <strong>{gestionDetalleItem?.canalContacto || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Resultado llamada</span>
                <strong>{gestionDetalleItem?.resultadoLlamada || "-"}</strong>
              </div>
            </div>

            <div className="crm-postventa-gestion-detail-grid">
              <div>
                <span className="crm-postventa-card-label">Motivo</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.motivo || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Motivo codigo</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.motivoCodigo || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Accion sugerida</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.accionSugerida || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Mensaje sugerido</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.mensajeSugerido || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Descripcion</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.descripcion || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Observacion</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.observacion || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Evidencia</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.evidenciaContacto || "-"}</div>
              </div>
              <div>
                <span className="crm-postventa-card-label">Proxima accion obligatoria</span>
                <div className="crm-postventa-detail-pre">{gestionDetalleItem?.proximaAccionObligatoria || "-"}</div>
              </div>
            </div>

            <div>
              <span className="crm-postventa-card-label">Historial de modificaciones</span>
              {gestionDetalleItem?.historialModificaciones?.length ? (
                <div className="crm-postventa-table-wrap crm-postventa-table-wrap-safe mt-2">
                  <Table size="sm" className="crm-postventa-table crm-postventa-gestion-change-table mb-0" responsive>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Usuario</th>
                        <th>Accion</th>
                        <th>Estado</th>
                        <th>Observacion</th>
                        <th>Proximo contacto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestionDetalleItem.historialModificaciones.map((cambio, index) => (
                        <tr key={`${cambio.fecha}-${index}`}>
                          <td>{cambio.fecha || "-"}</td>
                          <td>{cambio.usuario || "-"}</td>
                          <td>{cambio.accion || "-"}</td>
                          <td>{`${cambio.estadoAnterior || "-"} -> ${cambio.estadoNuevo || "-"}`}</td>
                          <td>{`${cambio.observacionAnterior || "-"} -> ${cambio.observacionNueva || "-"}`}</td>
                          <td>{`${cambio.proximoContactoAnterior || "-"} -> ${cambio.proximoContactoNuevo || "-"}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="crm-postventa-detail-pre mt-2">Sin modificaciones registradas.</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setGestionDetalleItem(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={Boolean(gestionEditItem)} onHide={() => setGestionEditItem(null)} centered size="lg">
          <Form onSubmit={handleGuardarEdicionGestion}>
            <Modal.Header closeButton>
              <Modal.Title>Editar gestion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="crm-postventa-form-grid">
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={gestionEditDraft.estadoResultante}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, estadoResultante: event.target.value }))}
                >
                  {GESTION_EDIT_STATES.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Proximo contacto</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={gestionEditDraft.fechaProximoContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, fechaProximoContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Prioridad</Form.Label>
                <Form.Select
                  value={gestionEditDraft.prioridad}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, prioridad: event.target.value }))}
                >
                  {["Alta", "Media", "Baja"].map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Canal</Form.Label>
                <Form.Select
                  value={gestionEditDraft.canalContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, canalContacto: event.target.value }))}
                >
                  <option value="">Seleccionar</option>
                  {CONTACT_CHANNEL_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Resultado llamada</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionEditDraft.resultadoLlamada}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Evidencia</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionEditDraft.evidenciaContacto}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observacion</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={gestionEditDraft.observacion}
                  onChange={(event) => setGestionEditDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setGestionEditItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                Guardar cambios
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={Boolean(gestionFinalizarItem)} onHide={() => setGestionFinalizarItem(null)} centered size="lg">
          <Form onSubmit={handleFinalizarGestionExistente}>
            <Modal.Header closeButton>
              <Modal.Title>Finalizar gestion</Modal.Title>
            </Modal.Header>
            <Modal.Body className="crm-postventa-form-grid">
              <Form.Group>
                <Form.Label>Resultado final</Form.Label>
                <Form.Select
                  value={gestionFinalizarDraft.estadoResultante}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, estadoResultante: event.target.value }))}
                >
                  <option value="Cerrado">Cerrado</option>
                  <option value="Derivado">Derivado</option>
                  <option value="Descartado">Descartado</option>
                  <option value="Sin respuesta">Sin respuesta</option>
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Motivo de cierre</Form.Label>
                <Form.Select
                  value={gestionFinalizarDraft.motivoCierre}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, motivoCierre: event.target.value }))}
                >
                  {GESTION_CLOSE_REASONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Resultado llamada</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionFinalizarDraft.resultadoLlamada}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, resultadoLlamada: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Evidencia</Form.Label>
                <Form.Control
                  type="text"
                  value={gestionFinalizarDraft.evidenciaContacto}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, evidenciaContacto: event.target.value }))}
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-span-2">
                <Form.Label>Observacion final</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={gestionFinalizarDraft.observacionFinal}
                  onChange={(event) => setGestionFinalizarDraft((prev) => ({ ...prev, observacionFinal: event.target.value }))}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setGestionFinalizarItem(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoGestion}>
                Finalizar
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={rolloModalOpen} onHide={() => setRolloModalOpen(false)} centered>
          <Form onSubmit={handleGuardarRollo}>
            <Modal.Header closeButton>
              <Modal.Title>Registrar nueva entrega</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="crm-postventa-form-grid">
                <Form.Group>
                  <Form.Label>Fecha entrega</Form.Label>
                  <Form.Control
                    type="date"
                    value={rolloDraft.fechaEntrega}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, fechaEntrega: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Cantidad</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={rolloDraft.cantidad}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, cantidad: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={rolloDraft.observacion}
                    onChange={(event) => setRolloDraft((prev) => ({ ...prev, observacion: event.target.value }))}
                    placeholder="Detalle opcional de la entrega."
                  />
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="outline-secondary" onClick={() => setRolloModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" disabled={cargas.guardandoRollo}>
                {cargas.guardandoRollo ? "Guardando..." : "Guardar"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
        </section>
      </div>
    );
  };

  const renderSeguimiento = () => {
    if (cargas.seguimiento) {
      return <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando seguimiento...</div>;
    }

    if (errores.seguimiento) {
      return <Alert variant="danger">{errores.seguimiento}</Alert>;
    }

    if (!seguimiento) {
      return <Alert variant="secondary">No hay seguimiento disponible para este cliente.</Alert>;
    }

    return (
      <div className="d-grid gap-4">
        {!seguimiento.tieneDatosReales && seguimiento.message ? (
          <Alert variant="secondary">{seguimiento.message}</Alert>
        ) : null}

        <div className="crm-postventa-detail-grid">
          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Hoja de vida del cliente</h3>
            </div>
            <div className="crm-postventa-timeline">
              {seguimiento.timeline?.length ? (
                seguimiento.timeline.map((item, index) => (
                  <article className="crm-postventa-timeline-item" key={`${item.fecha}-${index}`}>
                    <span className="crm-postventa-card-label">{item.fecha}</span>
                    <h4 className="mb-1">{item.titulo}</h4>
                    {item.usuario && item.usuario !== "-" ? (
                      <div className="crm-postventa-timeline-user">Usuario: {item.usuario}</div>
                    ) : null}
                    <p className="crm-postventa-timeline-summary">
                      {item.resumen || item.descripcion || "Sin resumen disponible."}
                    </p>
                    <div className="crm-postventa-timeline-actions">
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="crm-postventa-timeline-button"
                        onClick={() => setTimelineDetalle(item)}
                      >
                        Ver detalle
                      </Button>
                    </div>
                  </article>
                ))
              ) : (
                <Alert variant="secondary">No hay eventos de seguimiento para mostrar.</Alert>
              )}
            </div>
          </section>

          <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-postventa-panel-title">
              <h3>Métricas</h3>
            </div>
            <div className="crm-postventa-metric-grid">
              {seguimiento.metricas?.map((item) => (
                <article className={`crm-postventa-card ${darkMode ? "is-dark" : ""}`} key={item.label}>
                  <span className="crm-postventa-card-label">{item.label}</span>
                  <div className="crm-postventa-card-value" style={{ fontSize: 19 }}>
                    {item.value}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <Modal
          show={Boolean(timelineDetalle)}
          onHide={() => setTimelineDetalle(null)}
          centered
          size="lg"
          className="crm-postventa-history-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{timelineDetalle?.titulo || "Detalle del evento"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="crm-postventa-detail-meta">
              <div>
                <span className="crm-postventa-card-label">Fecha</span>
                <strong>{timelineDetalle?.fecha || "-"}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Tipo</span>
                <strong>{getTimelineTipoDetalle(timelineDetalle)}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Usuario</span>
                <strong>{timelineDetalle?.usuario || "-"}</strong>
              </div>
            </div>

            <div className="mt-3">
              <span className="crm-postventa-card-label">Resumen</span>
              <p className="mb-0">{timelineDetalle?.resumen || timelineDetalle?.descripcion || "-"}</p>
            </div>

            <div className="mt-4">
              <span className="crm-postventa-card-label">Observaciones completas</span>
              {timelineDetalle?.detalleLineas?.length ? (
                <ul className="crm-postventa-detail-lines">
                  {timelineDetalle.detalleLineas.map((linea, index) => (
                    <li key={`${timelineDetalle.fecha}-${index}`}>{linea}</li>
                  ))}
                </ul>
              ) : (
                <div className="crm-postventa-detail-pre">
                  {timelineDetalle?.detalle || timelineDetalle?.resumen || "Sin detalle disponible."}
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setTimelineDetalle(null)}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  const renderCampaniasZocoCheck = () => (
    <ZocoCheckCampaignsPanel darkMode={darkMode} isAdmin={canManageZocoCheck} />
  );

  const renderSection = () => {
    switch (section) {
      case "ficha":
        return renderFicha();
      case "facturacion":
        return renderFacturacion();
      case "alertas":
        return renderAlertas();
      case "ordenes-pago":
        return renderOrdenesPago();
      case "cupones":
        return renderCupones();
      case "herramientas":
        return renderHerramientas();
      case "altas-bajas":
        return renderAltasBajas();
      case "campanias-zoco-check":
        return renderCampaniasZocoCheck();
      case "gestion":
        return renderGestion();
      case "seguimiento":
        return renderSeguimiento();
      default:
        return renderAlertas();
    }
  };

  if (cargandoSesion) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const alertaHeader = {
    kicker: "CRM Postventa",
    title: "Centro de alertas",
    chips: [
      `Fecha ${alertas?.fechaReferencia || alertasFiltros.fecha || "-"}`,
      `Facturacion ${alertas?.alertasFacturacion?.length || 0}`,
      `Operativas manuales ${alertas?.alertasGestion?.length || 0}`,
      `Criticas ${alertas?.alertasCriticas?.length || 0}`,
    ],
  };
  const requiereClienteSeleccionado = !["alertas", "altas-bajas", "campanias-zoco-check"].includes(section);
  const isAltasBajasSection = section === "altas-bajas";
  const isZocoCampaignsSection = section === "campanias-zoco-check";
  const topbarKicker =
    section === "alertas"
      ? alertaHeader.kicker
      : isZocoCampaignsSection
        ? "CRM Postventa"
      : isAltasBajasSection
        ? "CRM Postventa"
        : clienteHeader.clienteSeleccionado || "Cliente seleccionado";
  const topbarTitle =
    section === "alertas"
      ? alertaHeader.title
      : isZocoCampaignsSection
        ? "Campanas Zoco Check"
      : isAltasBajasSection
        ? "Altas / Bajas"
        : clienteHeader.nombreDeFantasia || clienteHeader.razonSocial || "Seleccioná un cliente";
  const topbarChips =
    section === "alertas"
      ? alertaHeader.chips
      : isZocoCampaignsSection
        ? ["Administracion de campanas y bootstrap del flujo mensual."]
      : isAltasBajasSection
        ? []
        : [
            `CUIT ${clienteHeader.cuit || "-"}`,
            `Estado ${clienteHeader.estado || "-"}`,
            `Terminal ${clienteHeader.nDeTerminal || "-"}`,
            `Comercio ${clienteHeader.nDeComercio || "-"}`,
            `Legajo ${clienteHeader.legajo || "-"}`,
          ];
  const showTopbarClientButton = !isAltasBajasSection && !isZocoCampaignsSection;
  const emptyStateTitle =
    section === "ordenes-pago"
      ? "Seleccioná un cliente para ver sus órdenes de pago."
      : section === "cupones"
        ? "Seleccioná un cliente para ver sus cupones."
        : "Seleccioná un cliente para ver la información";
  const emptyStateDescription =
    section === "ordenes-pago"
      ? "La consulta reutiliza las órdenes reales asociadas al cliente seleccionado en CRM Postventa."
      : section === "cupones"
        ? "El listado usa cupones reales del cliente seleccionado y respeta el mismo selector global."
        : "El módulo queda preparado sobre datos reales. Elegí un cliente/comercio para cargar ficha, facturación, herramientas, gestión y seguimiento.";
  const panelTitle =
    section === "ficha"
      ? "Ficha del cliente"
      : section === "facturacion"
        ? "Facturacion"
        : section === "alertas"
          ? "Alertas"
          : section === "ordenes-pago"
            ? "Órdenes de pago"
            : section === "cupones"
              ? "Cupones"
              : section === "herramientas"
                ? "Herramientas"
                : section === "altas-bajas"
                  ? "Altas / Bajas"
                  : section === "campanias-zoco-check"
                    ? "Campanas Zoco Check"
                  : section === "gestion"
                    ? "Gestion"
                    : "Seguimiento";

  return (
    <div className="crm-postventa-page" translate="no">
      <TituloPagina title="CRM Postventa" />

      <div className="container crm-postventa-shell py-4">
        <section className={`crm-postventa-topbar ${darkMode ? "is-dark" : ""}`}>
          <div className="crm-postventa-topbar-copy">
            <span className="crm-postventa-kicker">{topbarKicker}</span>
            <h2>{topbarTitle}</h2>
            {topbarChips.length ? (
              <div className="crm-postventa-meta">
                {topbarChips.map((item) => (
                  <span className="crm-postventa-chip" key={item}>
                    {item}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {showTopbarClientButton ? (
            <Button className="crm-postventa-cta" variant="success" onClick={() => setClienteModalOpen(true)}>
              <FontAwesomeIcon className="me-2" icon={faUserPen} />
              {section === "alertas" ? "Seleccionar cliente" : "Cambiar cliente"}
            </Button>
          ) : null}
        </section>

        {!clienteSeleccionadoId && requiereClienteSeleccionado ? (
          <section className="crm-postventa-empty-state mt-4">
            <FontAwesomeIcon icon={faBuilding} size="2x" />
            <h3>{emptyStateTitle}</h3>
            <p className="mb-0">{emptyStateDescription}</p>
            <Button variant="success" onClick={() => setClienteModalOpen(true)}>
              Elegir cliente
            </Button>
          </section>
        ) : (
          <section className={`crm-postventa-panel mt-4 ${darkMode ? "is-dark" : ""}`}>
            {terminalesGrupo.length > 0 && section !== "alertas" && section !== "altas-bajas" ? (
              <div className="crm-postventa-terminal-switcher">
                <div className="crm-postventa-terminal-tabs">
                  {terminalesGrupo.map((terminal) => (
                    <button
                      key={terminal.clienteId}
                      type="button"
                      className={`crm-postventa-terminal-tab ${terminal.clienteId === clienteSeleccionadoId ? "is-active" : ""}`}
                      onClick={() => handleCambiarTerminal(terminal)}
                    >
                      <span>{terminal.label}</span>
                      <small>{terminal.resumen || "Sin datos"}</small>
                      <small>Estado: {resolveTerminalStatusLabel(terminal)}</small>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="crm-postventa-panel-title">
              <h3>{panelTitle}</h3>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (section === "alertas") {
                    cargarAlertas(alertasFiltros);
                    return;
                  }

                  if (section === "ordenes-pago") {
                    cargarOrdenesPago(clienteSeleccionadoId, ordenesPagoFiltros);
                    return;
                  }

                  if (section === "cupones") {
                    cargarCupones(clienteSeleccionadoId, cuponesFiltros);
                    return;
                  }

                  if (section === "altas-bajas") {
                    if (clienteSeleccionadoId) {
                      cargarModulo(clienteSeleccionadoId, analyticsRange);
                    }
                    resetAltasBajas();
                    return;
                  }

                  cargarModulo(clienteSeleccionadoId, analyticsRange);
                }}
              >
                <FontAwesomeIcon className="me-2" icon={faArrowsRotate} />
                Recargar
              </Button>
            </div>
            {renderSection()}
          </section>
        )}
      </div>

      <div className="py-4 mt-auto">
        <Footer />
      </div>

      <ClienteSelectorModal
        show={clienteModalOpen}
        onHide={() => setClienteModalOpen(false)}
        search={clienteSearch}
        onSearchChange={setClienteSearch}
        loading={clienteLoading}
        results={clienteGrupos}
        selectedKey={currentGroupKey}
        onSelect={setClientePendiente}
        onConfirm={() => seleccionarCliente(clientePendiente || ficha?.header)}
      />
    </div>
  );
};

export default CrmPostventa;
